import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { plantsAPI, socialMediaAPI } from './services/api';
import { Loader, CheckCircle, AlertCircle, Sparkles, Image as ImageIcon, Share2, Facebook, Instagram, Search, ChevronDown, ChevronUp, Info, Layout, Trash2, Mail, Upload, Pencil } from 'lucide-react';
import Footer from './components/Footer';
import PublicNav from './components/PublicNav';
import { PlantPhotoManager } from './components/PlantPhotoManager';
import { coerceImageUrl } from './utils/imageUrl';
import { convertHeicToJpeg, convertHeicFiles, isHeicFile } from './utils/convertHeic';
import { uploadPlantIdentificationImage, identifyPlantWithBlobUrl } from './services/plantIdentifyApi';

// Plant Attribution Component
const PlantAttribution = ({ plant }) => {
  const navigate = useNavigate();
  
  if (!plant) return null;
  
  // Get creator info from plant data
  const creatorEmail = plant.creator_email;
  const creatorName = plant.creator_display_name || plant.creator_name;
  const creatorBusinessName = plant.creator_business_name;
  const creatorDesignerId = plant.creator_designer_id;
  const creatorUserId = plant.creator_user_id;
  const creatorUserName = plant.creator_user_name;
  const creatorUserEmail = plant.creator_user_email;
  const creatorUserProfileData = plant.creator_user_profile_data;
  const photoAttribution = plant.photo_attribution;
  
  // Parse social media handles from user profile_data
  let socialHandles = null;
  if (creatorUserProfileData) {
    try {
      const profileData = typeof creatorUserProfileData === 'string' 
        ? JSON.parse(creatorUserProfileData) 
        : creatorUserProfileData;
      socialHandles = {
        instagram: profileData.instagram || profileData.ig,
        facebook: profileData.facebook || profileData.fb,
        tiktok: profileData.tiktok,
        twitter: profileData.twitter || profileData.x
      };
      // Remove empty values
      Object.keys(socialHandles).forEach(key => {
        if (!socialHandles[key]) delete socialHandles[key];
      });
      if (Object.keys(socialHandles).length === 0) socialHandles = null;
    } catch (e) {
      // Invalid JSON, ignore
    }
  }
  
  // Special case: johnconnors1690@gmail.com -> Aspen Ridge Garden Design
  const isAspenRidge = creatorEmail && creatorEmail.toLowerCase() === 'johnconnors1690@gmail.com';
  
  // Separate photo attribution from plant info attribution
  // Photo attribution comes from photo_attribution field (who took the photo)
  // Plant info attribution comes from created_by_designer_id (who created the plant entry)
  
  let photoAttributionText = photoAttribution || null;
  let photoAttributionLink = null;
  
  let infoAttributionText = '';
  let infoAttributionLink = null;
  let isContributor = false;
  
  // Determine plant info attribution (who created the entry)
  if (isAspenRidge && creatorDesignerId) {
    // Link to Aspen Ridge Garden Design profile
    infoAttributionText = creatorBusinessName || 'Aspen Ridge Garden Design';
    infoAttributionLink = () => navigate(`/find-designer?designer=${creatorDesignerId}`);
  } else if (creatorDesignerId) {
    // Designer with profile - link to their designer card
    infoAttributionText = creatorBusinessName || creatorName || creatorEmail || 'Designer';
    infoAttributionLink = () => navigate(`/find-designer?designer=${creatorDesignerId}`);
  } else if (creatorUserId && creatorUserName) {
    // Contributor (user) - show name and/or social handles
    isContributor = true;
    infoAttributionText = creatorUserName;
  } else if (plant.created_by_designer_id) {
    // Fallback: if we have created_by_designer_id but no other info
    infoAttributionText = 'Aspen Ridge Garden Design';
    infoAttributionLink = () => navigate(`/find-designer?designer=${plant.created_by_designer_id}`);
  }
  
  // If photo_attribution exists, use it (this is separate from plant info creator)
  // If no photo_attribution but creator exists, don't assume they took the photo
  
  // Only show attribution if we have photo attribution OR plant info attribution
  // Don't show anything if neither exists
  if (!photoAttributionText && !infoAttributionText) {
    return null;
  }
  
  // Build social media links for contributors
  const getSocialLink = (platform, handle) => {
    if (!handle) return null;
    const cleanHandle = handle.replace(/^@/, ''); // Remove @ if present
    const links = {
      instagram: `https://instagram.com/${cleanHandle}`,
      facebook: handle.includes('facebook.com') ? handle : `https://facebook.com/${cleanHandle}`,
      tiktok: `https://tiktok.com/@${cleanHandle}`,
      twitter: `https://twitter.com/${cleanHandle}`,
      x: `https://x.com/${cleanHandle}`
    };
    return links[platform] || links[platform.toLowerCase()] || null;
  };
  
  return (
    <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
      <div className="flex items-center gap-2 flex-wrap">
        {/* Photo Attribution */}
        {photoAttributionText && (
          <>
            <span className="text-gray-400 italic">Photo by:</span>
            <span>📸 {photoAttributionText}</span>
          </>
        )}
        
        {/* Plant Info Attribution */}
        {infoAttributionText && (
          <>
            {photoAttributionText && <span className="text-gray-400">•</span>}
            {!photoAttributionText && <span className="text-gray-400 italic">Info by:</span>}
            {photoAttributionText && <span className="text-gray-400 italic">Info by:</span>}
            <span>{infoAttributionText}</span>
            {infoAttributionLink && (
              <button
                onClick={infoAttributionLink}
                className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
              >
                View Profile
              </button>
            )}
          </>
        )}
        
        {/* Social Media Links for Contributors */}
        {isContributor && socialHandles && (
          <div className="flex items-center gap-2 ml-2">
            {socialHandles.instagram && (
              <a
                href={getSocialLink('instagram', socialHandles.instagram)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-pink-600 hover:text-pink-800 hover:underline"
                title={`@${socialHandles.instagram.replace(/^@/, '')}`}
              >
                📷 IG
              </a>
            )}
            {socialHandles.facebook && (
              <a
                href={getSocialLink('facebook', socialHandles.facebook)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline"
                title={socialHandles.facebook}
              >
                👤 FB
              </a>
            )}
            {socialHandles.tiktok && (
              <a
                href={getSocialLink('tiktok', socialHandles.tiktok)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-800 hover:text-gray-900 hover:underline"
                title={`@${socialHandles.tiktok.replace(/^@/, '')}`}
              >
                🎵 TT
              </a>
            )}
            {(socialHandles.twitter || socialHandles.x) && (
              <a
                href={getSocialLink('twitter', socialHandles.twitter || socialHandles.x)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-600 hover:underline"
                title={`@${(socialHandles.twitter || socialHandles.x).replace(/^@/, '')}`}
              >
                🐦 X
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const PlantDetailModal = ({
  plant,
  onClose,
  onEdit,
  onDelete,
  isOwner,
  isDesigner,
  onAILookup,
  onShareToCommunity,
  onLike,
  isLiked,
  isFromPublicLibrary,
  onRemoveFromCollection,
  isFromPlacement,
  onSetPlacementImage,
  onMarkImageVerified,
  onPlantUpdated,
  // Placement-specific edit actions
  onEditProjectItem,
  onEditLibraryPlant,
  // When true (client view from shared plan), hide Add External Photo and Share button
  isClientView = false
}) => {
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const generatePostUploadInputRef = useRef(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [showBusinessPostModal, setShowBusinessPostModal] = useState(false);
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [postingTo, setPostingTo] = useState(null);
  const [postMessage, setPostMessage] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedImageForPlacement, setSelectedImageForPlacement] = useState(null);
  const [markingVerified, setMarkingVerified] = useState(false);
  const [showGeneratePostModal, setShowGeneratePostModal] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [includeDiscoveryLine, setIncludeDiscoveryLine] = useState(true);
  const [discoveryLineText, setDiscoveryLineText] = useState('Discover more plants at plantaday.com.');
  const [includePhotoCaption, setIncludePhotoCaption] = useState(false);
  const [photoCaptionText, setPhotoCaptionText] = useState('');
  const [generatedTitle, setGeneratedTitle] = useState('');
  const [generatedBody, setGeneratedBody] = useState('');
  const [showExternalPhotoModal, setShowExternalPhotoModal] = useState(false);
  const [externalPhotoLoading, setExternalPhotoLoading] = useState(false);
  const [externalPhotoError, setExternalPhotoError] = useState(null);
  const [externalImageUrl, setExternalImageUrl] = useState('');
  const [externalSourcePageUrl, setExternalSourcePageUrl] = useState('');
  const [externalCreatorName, setExternalCreatorName] = useState('');
  const [externalLicense, setExternalLicense] = useState('');
  const [removingImageIndex, setRemovingImageIndex] = useState(null);
  const [externalSetAsPrimary, setExternalSetAsPrimary] = useState(true);
  const [externalArchiveCurrent, setExternalArchiveCurrent] = useState(true);
  const [lightboxImageUrl, setLightboxImageUrl] = useState(null);
  const [imageQualityWarning, setImageQualityWarning] = useState(null);
  const [imageQualityDetails, setImageQualityDetails] = useState([]); // [{ idx, label, w, h }] for each image with issues
  const [emailPostLoading, setEmailPostLoading] = useState(false);
  const [emailPostError, setEmailPostError] = useState(null);
  const [emailPostTo, setEmailPostTo] = useState(''); // Optional: send to someone else for social posting
  const [generatePostFindPhotosLoading, setGeneratePostFindPhotosLoading] = useState(false);
  const [generatePostPhotoPickerPhotos, setGeneratePostPhotoPickerPhotos] = useState([]);
  const [showGeneratePostPhotoPicker, setShowGeneratePostPhotoPicker] = useState(false);
  const [generatePostPickingPhoto, setGeneratePostPickingPhoto] = useState(null);
  const [generatePostPendingUploadFile, setGeneratePostPendingUploadFile] = useState(null);
  const [generatePostUploadLoading, setGeneratePostUploadLoading] = useState(false);
  const [generatePostUploadAttribution, setGeneratePostUploadAttribution] = useState('');
  const [editAttributionIndex, setEditAttributionIndex] = useState(null); // 0 = primary, 1+ = additional
  const [editAttributionValue, setEditAttributionValue] = useState('');
  const [showManagePhotosModal, setShowManagePhotosModal] = useState(false);
  
  // Build images array - must be done before early return for useEffect
  const plantData = plant?.plant_data || {};
  const allImages = [];
  const imageAttributions = [];
  const seenUrls = new Set(); // Track URLs to prevent duplicates
  
  // Helper to normalize URL for comparison (remove trailing slashes, normalize protocol)
  const normalizeUrl = (url) => {
    if (!url) return '';
    return url.trim().replace(/\/$/, '').toLowerCase();
  };
  
  const primaryImageUrl = plant?.image_url ? normalizeUrl(plant.image_url) : null;
  
  if (plant?.image_url) {
    allImages.push(plant.image_url);
    imageAttributions.push(plant.photo_attribution || null);
    seenUrls.add(primaryImageUrl);
  }
  
  if (plantData.additional_images && Array.isArray(plantData.additional_images)) {
    plantData.additional_images.forEach(item => {
      let urlToCheck = null;
      if (typeof item === 'string') {
        urlToCheck = item;
      } else if (item && item.url) {
        urlToCheck = item.url;
      }
      
      if (urlToCheck) {
        const normalizedUrl = normalizeUrl(urlToCheck);
        
        // Only add if URL exists, is not the primary image, and hasn't been seen before
        if (normalizedUrl && normalizedUrl !== primaryImageUrl && !seenUrls.has(normalizedUrl)) {
          allImages.push(urlToCheck); // Use original URL for display
          if (typeof item === 'string') {
            imageAttributions.push(null);
          } else if (item && item.url) {
            imageAttributions.push(item.photographer ? `Photo by ${item.photographer}` : null);
          }
          seenUrls.add(normalizedUrl);
        }
      }
    });
  }
  
  // Set initial selected image for placement when modal opens
  useEffect(() => {
    if (isFromPlacement && plant && allImages.length > 0 && !selectedImageForPlacement) {
      const currentImageIndex = allImages.findIndex(img => img === plant.image_url);
      if (currentImageIndex >= 0) {
        setSelectedImageForPlacement(plant.image_url);
      } else {
        setSelectedImageForPlacement(allImages[0]);
      }
    }
  }, [isFromPlacement, plant, allImages.length, selectedImageForPlacement]);
  
  // Image quality check for social when Generate Post modal is open - check all images
  useEffect(() => {
    if (!showGeneratePostModal) {
      setImageQualityWarning(null);
      setImageQualityDetails([]);
      return;
    }
    const imagesToCheck = [];
    if (plant?.image_url) imagesToCheck.push({ url: plant.image_url, idx: 0, label: 'Primary image' });
    const pd = plant?.plant_data || {};
    const addl = Array.isArray(pd.additional_images) ? pd.additional_images : [];
    addl.forEach((item, i) => {
      const u = typeof item === 'string' ? item : item?.url;
      if (u) imagesToCheck.push({ url: u, idx: i + 1, label: `Image ${i + 2}` });
    });
    if (imagesToCheck.length === 0) {
      setImageQualityWarning('No images for this plant.');
      setImageQualityDetails([]);
      return;
    }
    const minSocial = 1080;
    const minAcceptable = 600;
    const issues = [];
    let loaded = 0;
    imagesToCheck.forEach(({ url, idx, label }) => {
      const img = new Image();
      const src = coerceImageUrl(url);
      if (!src) return;
      img.onload = () => {
        const w = img.naturalWidth || 0;
        const h = img.naturalHeight || 0;
        if (w > 0 && h > 0 && (w < minAcceptable || h < minAcceptable)) {
          issues.push({ idx, label, w, h, severity: 'too_small' });
        } else if (w > 0 && h > 0 && (w < minSocial || h < minSocial)) {
          issues.push({ idx, label, w, h, severity: 'suboptimal' });
        }
        loaded++;
        if (loaded >= imagesToCheck.length) {
          setImageQualityDetails(issues);
          setImageQualityWarning(issues.length > 0 ? `${issues.length} image(s) may not look good on social.` : null);
        }
      };
      img.onerror = () => {
        loaded++;
        if (loaded >= imagesToCheck.length) {
          setImageQualityDetails(issues);
          setImageQualityWarning(issues.length > 0 ? `${issues.length} image(s) may not look good on social.` : null);
        }
      };
      img.src = src;
    });
  }, [showGeneratePostModal, plant?.image_url, plant?.plant_data]);
  
  // Close lightbox on ESC (must be declared before any early return — hooks rule)
  useEffect(() => {
    if (!lightboxImageUrl) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setLightboxImageUrl(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightboxImageUrl]);

  if (!plant) return null;

  const currentImage = allImages[selectedImageIndex] || plant.image_url;
  // Strip HTML tags from attribution for clean display
  const stripHtml = (text) => {
    if (!text) return null;
    return text.replace(/<[^>]*>/g, '').trim();
  };
  const rawAttribution = imageAttributions[selectedImageIndex] || plant.photo_attribution || null;
  const currentAttribution = stripHtml(rawAttribution);
  
  // Download image function
  const handleDownloadImage = async () => {
    if (!currentImage) return;
    
    try {
      const response = await fetch(currentImage);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${plant.common_name}_${selectedImageIndex + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
      // Fallback: open in new tab
      window.open(currentImage, '_blank');
    }
  };

  // Remove image from plant (admin/owner only - when onEdit is available)
  const handleRemoveImage = async (idx, e) => {
    e.stopPropagation();
    if (!onEdit || !plant?.id || idx < 0 || idx >= allImages.length) return;
    const urlToRemove = allImages[idx];
    setRemovingImageIndex(idx);
    try {
      const plantData = plant.plant_data || {};
      const pdAdditional = Array.isArray(plantData.additional_images) ? plantData.additional_images : [];
      const toItem = (url) => {
        const item = pdAdditional.find(i => (typeof i === 'string' ? i : i?.url) === url);
        return (item && typeof item === 'object' && item.photographer) ? item : url;
      };
      let newImageUrl;
      let newAdditionalImages;
      let newPhotoAttribution;

      if (idx === 0) {
        if (allImages.length > 1) {
          newImageUrl = allImages[1];
          newPhotoAttribution = imageAttributions[1] || '';
          const restUrls = allImages.slice(2).filter(u => u !== allImages[0]);
          newAdditionalImages = restUrls.map(u => toItem(u));
        } else {
          newImageUrl = null;
          newPhotoAttribution = '';
          newAdditionalImages = [];
        }
      } else {
        newImageUrl = plant.image_url;
        newPhotoAttribution = undefined;
        newAdditionalImages = allImages
          .filter((_, i) => i !== idx && i !== 0)
          .map(u => toItem(u));
      }

      const payload = {
        image_url: newImageUrl,
        additional_images: newAdditionalImages,
        replace_additional_images: true
      };
      if (idx === 0) payload.photo_attribution = newPhotoAttribution;
      await plantsAPI.update(plant.id, payload);

      if (onPlantUpdated) {
        const updated = { ...plant, image_url: newImageUrl, plant_data: { ...plantData, additional_images: newAdditionalImages } };
        if (idx === 0 && newPhotoAttribution !== undefined) updated.photo_attribution = newPhotoAttribution;
        onPlantUpdated(updated);
      }
      setSelectedImageIndex(Math.max(0, Math.min(selectedImageIndex, allImages.length - 2)));
    } catch (error) {
      console.error('Error removing image:', error);
      alert(`Failed to remove image: ${error.message || 'Unknown error'}`);
    } finally {
      setRemovingImageIndex(null);
    }
  };

  // Handle click outside modal to close
  const handleBackdropClick = (e) => {
    // Only close if clicking directly on the backdrop, not on modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get the plant-specific URL for sharing
  // Note: This creates a shareable link to view the plant card.
  // For private plants, the link will only work if the viewer is the owner or if the plant is made public.
  // To make a plant public, use the "Share with Community" button instead.
  const getShareUrl = () => {
    const baseUrl = window.location.origin;
    // Create a direct link to this specific plant using plantId query parameter
    // This will open the plant detail modal when the link is visited
    return `${baseUrl}/plants?plantId=${plant.id}`;
  };

  // Get share text
  const getShareText = () => {
    const privacyNote = !plant.is_public ? ' (from my private collection)' : '';
    return `Check out this ${plant.common_name} (${plant.botanical_name})${privacyNote} on PlantADay!`;
  };

  // Native share function using Web Share API
  const handleShare = async () => {
    const shareData = {
      title: `${plant.common_name} - PlantADay`,
      text: getShareText(),
      url: getShareUrl(),
    };

    // Check if Web Share API is available (mobile devices, some browsers)
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // User cancelled or error occurred
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
          // Fall back to copying link
          await copyLinkToClipboard();
        }
      }
    } else {
      // Fallback: copy link to clipboard
      await copyLinkToClipboard();
    }
  };

  // Fallback function to copy link to clipboard
  const copyLinkToClipboard = async () => {
    const text = `${getShareText()} ${getShareUrl()}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  // Load social media accounts
  const loadSocialAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const response = await socialMediaAPI.getAccounts();
      setSocialAccounts(response.accounts || []);
    } catch (error) {
      console.error('Error loading social accounts:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Open business post modal
  const handleOpenBusinessPost = async () => {
    await loadSocialAccounts();
    setPostMessage(`${plant.common_name} (${plant.botanical_name})\n\n${plant.description || 'Beautiful plant from PlantADay!'}`);
    setShowBusinessPostModal(true);
  };

  // Connect Facebook account
  const handleConnectFacebook = async () => {
    try {
      const response = await socialMediaAPI.getFacebookAuthUrl();
      window.location.href = response.authUrl;
    } catch (error) {
      alert('Failed to connect Facebook. Please check your Facebook App configuration.');
      console.error('Error connecting Facebook:', error);
    }
  };

  // Post to social media
  const handlePostToSocial = async (platform) => {
    if (!postMessage.trim()) {
      alert('Please enter a message');
      return;
    }

    setPostingTo(platform);
    try {
      const caption = postMessage;
      const imageUrl = plant.image_url;

      if (platform === 'facebook') {
        await socialMediaAPI.postToFacebook(caption, imageUrl, plant.id);
        alert('Posted to Facebook successfully!');
      } else if (platform === 'instagram') {
        await socialMediaAPI.postToInstagram(caption, imageUrl, plant.id);
        alert('Posted to Instagram successfully!');
      }
      
      setShowBusinessPostModal(false);
      setPostMessage('');
    } catch (error) {
      alert(`Failed to post to ${platform}. ${error.message || 'Please try again.'}`);
      console.error(`Error posting to ${platform}:`, error);
    } finally {
      setPostingTo(null);
    }
  };

  // Disconnect account
  const handleDisconnect = async (platform) => {
    if (!window.confirm(`Disconnect ${platform} account?`)) return;
    
    try {
      await socialMediaAPI.disconnectAccount(platform);
      await loadSocialAccounts();
      alert(`${platform} account disconnected`);
    } catch (error) {
      alert(`Failed to disconnect ${platform}`);
      console.error('Error disconnecting:', error);
    }
  };

  const downloadUrl = async (url, filename) => {
    if (!url) return;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename || 'plant-image.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading image:', error);
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const copyPlainText = async (text) => {
    const value = (text || '').toString();
    if (!value.trim()) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
        return true;
      }
    } catch (_) {
      // fallthrough
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch (e) {
      console.error('Copy failed:', e);
      return false;
    }
  };

  const readClipboardText = async () => {
    try {
      if (!navigator.clipboard?.readText) return null;
      const text = await navigator.clipboard.readText();
      return (text || '').toString();
    } catch (e) {
      console.warn('Clipboard read failed:', e);
      return null;
    }
  };

  const sanitizeAuthToken = () => {
    let token = localStorage.getItem('authToken') || '';
    try {
      if (token && token.trim().startsWith('"')) {
        token = JSON.parse(token);
      }
    } catch (_) {
      // ignore
    }
    token = (token || '').toString().trim();
    if (token.toLowerCase().startsWith('bearer ')) {
      token = token.slice(7).trim();
    }
    return token;
  };

  const handleAttachExternalPhoto = async () => {
    if (!plant?.id) return;
    if (!externalImageUrl.trim()) {
      setExternalPhotoError('Please paste a direct image URL.');
      return;
    }

    setExternalPhotoLoading(true);
    setExternalPhotoError(null);
    try {
      const token = sanitizeAuthToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL ||
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

      const response = await fetch(`${API_BASE_URL}/plants/${plant.id}/external-photo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          image_url: externalImageUrl.trim(),
          source_page_url: externalSourcePageUrl.trim(),
          creator_name: externalCreatorName.trim(),
          license: externalLicense.trim(),
          set_as_primary: !!externalSetAsPrimary,
          archive_current_primary: !!externalArchiveCurrent
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP ${response.status}`);
      }

      const updated = data.plant || null;
      if (updated && typeof onPlantUpdated === 'function') {
        onPlantUpdated(updated);
      }
      setShowExternalPhotoModal(false);
    } catch (e) {
      console.error('Attach external photo error:', e);
      setExternalPhotoError(e.message || 'Failed to attach external photo.');
    } finally {
      setExternalPhotoLoading(false);
    }
  };

  const handleGeneratePost = async () => {
    if (!plant?.id) return;
    setGenerateLoading(true);
    setGenerateError(null);
    try {
      // Token can be stored in different formats across browsers/devices (e.g. quoted JSON string, or already prefixed).
      let token = localStorage.getItem('authToken') || '';
      try {
        // If token is a JSON string (e.g. "\"eyJ...\""), parse it
        if (token && token.trim().startsWith('"')) {
          token = JSON.parse(token);
        }
      } catch (_) {
        // ignore
      }
      token = (token || '').toString().trim();
      if (token.toLowerCase().startsWith('bearer ')) {
        token = token.slice(7).trim();
      }
      const API_BASE_URL = process.env.REACT_APP_API_URL ||
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

      const response = await fetch(`${API_BASE_URL}/plants/${plant.id}/generate-post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          include_discovery_line: includeDiscoveryLine,
          discovery_line_text: discoveryLineText,
          include_photo_caption: includePhotoCaption,
          photo_caption_text: photoCaptionText
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const msg = (data.message || data.error || 'Failed to generate post').toString();
        if (msg.toLowerCase().includes('invalid token')) {
          throw new Error('Session expired or invalid token. Please log out and sign back in, then try again.');
        }
        // If server returned rawResponse (e.g. parse error), try to extract title/body client-side
        if (data.rawResponse) {
          const raw = (data.rawResponse || '').trim();
          const match = raw.match(/\{[\s\S]*\}/);
          if (match) {
            try {
              const candidate = match[0].replace(/,(\s*[}\]])/g, '$1');
              const parsed = JSON.parse(candidate);
              if (parsed.title || parsed.body) {
                setGeneratedTitle((parsed.title || '').toString());
                setGeneratedBody((parsed.body || '').toString());
                setGenerateError('Could not parse AI response cleanly, but extracted content below. You can edit and copy.');
                setGenerateLoading(false);
                return;
              }
            } catch (_) {}
          }
        }
        throw new Error(msg);
      }

      setGeneratedTitle((data.title || '').toString());
      setGeneratedBody((data.body || '').toString());
    } catch (error) {
      console.error('Generate post error:', error);
      setGenerateError(error.message || 'Failed to generate post. Please try again.');
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleGeneratePostFindPhotos = async () => {
    if (!plant?.id) return;
    const bn = (plant?.botanical_name || '').trim();
    const cn = (plant?.common_name || '').trim();
    if (!bn && !cn) {
      alert('Plant needs a botanical or common name to search for photos.');
      return;
    }
    setGeneratePostFindPhotosLoading(true);
    setGeneratePostPhotoPickerPhotos([]);
    try {
      const token = sanitizeAuthToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const res = await fetch(`${API_BASE_URL}/plants/find-photos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          botanicalName: bn || undefined,
          query: bn || cn,
          commonName: cn || null,
          verifiedOnly: true
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to fetch photos');
      const photos = (data.photos || []).filter(p => p?.url && !p.url.includes('placeholder'));
      if (photos.length === 0) {
        alert('No verified photos found. Try "Add by URL" with an image from Wikimedia Commons or similar.');
        return;
      }
      setGeneratePostPhotoPickerPhotos(photos);
      setShowGeneratePostPhotoPicker(true);
    } catch (e) {
      alert(e.message || 'Failed to find photos.');
    } finally {
      setGeneratePostFindPhotosLoading(false);
    }
  };

  const handleGeneratePostPickPhoto = async (photo, setAsPrimary) => {
    if (!plant?.id || !photo?.url) return;
    setGeneratePostPickingPhoto(photo);
    try {
      const token = sanitizeAuthToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const res = await fetch(`${API_BASE_URL}/plants/${plant.id}/external-photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          image_url: photo.url,
          source_page_url: photo.photographer_url || '',
          creator_name: photo.photographer || photo.attribution || '',
          license: '',
          set_as_primary: !!setAsPrimary,
          archive_current_primary: false
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || 'Failed to add photo');
      if (data.plant && typeof onPlantUpdated === 'function') onPlantUpdated(data.plant);
      setShowGeneratePostPhotoPicker(false);
      setGeneratePostPhotoPickerPhotos([]);
    } catch (e) {
      alert(e.message || 'Failed to add photo.');
    } finally {
      setGeneratePostPickingPhoto(null);
    }
  };

  const handleGeneratePostUploadConfirm = async (setAsPrimary) => {
    const file = generatePostPendingUploadFile;
    if (!file || !plant?.id) return;
    setGeneratePostUploadLoading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('set_as_primary', setAsPrimary ? 'true' : 'false');
      fd.append('archive_current_primary', 'true');
      if (generatePostUploadAttribution.trim()) fd.append('creator_name', generatePostUploadAttribution.trim());
      const token = sanitizeAuthToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const res = await fetch(`${API_BASE_URL}/plants/${plant.id}/upload-photo`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || 'Failed to upload photo');
      if (data.plant && typeof onPlantUpdated === 'function') onPlantUpdated(data.plant);
      setGeneratePostPendingUploadFile(null);
      setGeneratePostUploadAttribution('');
      if (generatePostUploadInputRef.current) generatePostUploadInputRef.current.value = '';
    } catch (e) {
      alert(e.message || 'Failed to upload photo.');
    } finally {
      setGeneratePostUploadLoading(false);
    }
  };

  const handleSaveAttribution = async () => {
    if (!plant?.id || editAttributionIndex == null) return;
    try {
      const newAttr = (editAttributionValue || '').trim() ? `Photo by ${(editAttributionValue || '').trim()}` : null;
      if (editAttributionIndex === 0) {
        await plantsAPI.update(plant.id, { photo_attribution: newAttr });
      } else {
        const addl = [...(plantData.additional_images || [])];
        const url = allImages[editAttributionIndex];
        const norm = (i) => (typeof i === 'string' ? i : i?.url);
        const next = addl.map((item) => {
          const u = norm(item);
          if (u === url) return newAttr ? { url: u, photographer: newAttr } : u;
          return item;
        });
        await plantsAPI.update(plant.id, { additional_images: next, replace_additional_images: true });
      }
      if (typeof onPlantUpdated === 'function') {
        const updated = await plantsAPI.get(plant.id);
        onPlantUpdated(updated);
      }
    } catch (e) {
      alert(e.message || 'Failed to save attribution.');
      return;
    }
    setEditAttributionIndex(null);
    setEditAttributionValue('');
  };

  const handleEmailPost = async () => {
    if (!plant?.id) return;
    setEmailPostError(null);
    setEmailPostLoading(true);
    try {
      let token = localStorage.getItem('authToken') || '';
      try {
        if (token && token.trim().startsWith('"')) token = JSON.parse(token);
      } catch (_) {}
      token = (token || '').toString().trim();
      if (token.toLowerCase().startsWith('bearer ')) token = token.slice(7).trim();
      const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const res = await fetch(`${API_BASE_URL}/plants/${plant.id}/email-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          title: generatedTitle,
          body: generatedBody,
          toEmail: emailPostTo.trim() || undefined // When set, sends to this address instead of John
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'Failed to send email');
      alert('Email sent! Check your inbox.');
    } catch (e) {
      setEmailPostError(e.message || 'Failed to send email');
    } finally {
      setEmailPostLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-2 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div 
        ref={modalRef}
        className="bg-white rounded-lg sm:rounded-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-4 gap-2">
            <div className="flex-1 min-w-0">
              {/* Botanical name - PRIMARY (prominent) */}
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 break-words mb-1">{plant.botanical_name || 'Unknown'}</h2>
              {/* Common name - SECONDARY (smaller, below) */}
              {plant.common_name && plant.common_name !== plant.botanical_name && (
                <p className="text-sm sm:text-base text-gray-600 italic break-words mb-2">Common name: {plant.common_name}</p>
              )}
              {/* Image attribution - shown separately from prose */}
              {(() => {
                const pd = plant?.plant_data || {};
                const hasExternal = !!(pd.photo_source_url || pd.photo_creator || pd.photo_license) || plant.photo_source === 'external';
                const shouldShowSimple =
                  plant.photo_attribution && (plant.photo_source === 'wikimedia' || plant.photo_source === 'inaturalist' || plant.image_verified);

                if (!hasExternal && !shouldShowSimple) return null;

                return (
                  <div className="mb-2">
                    {plant.image_verified && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs mr-2">
                        ✓ Verified Image
                      </span>
                    )}
                    {plant.photo_attribution && (
                      <p className="text-xs text-gray-500 break-words">{plant.photo_attribution}</p>
                    )}
                    {hasExternal && (
                      <div className="mt-1 text-xs text-gray-600 space-y-0.5">
                        {pd.photo_creator && (
                          <div><span className="font-medium">Creator:</span> {pd.photo_creator}</div>
                        )}
                        {pd.photo_license && (
                          <div><span className="font-medium">License:</span> {pd.photo_license}</div>
                        )}
                        {pd.photo_source_url && (
                          <div className="break-words">
                            <span className="font-medium">Source page:</span>{' '}
                            <a className="text-blue-600 hover:underline" href={pd.photo_source_url} target="_blank" rel="noopener noreferrer">
                              {pd.photo_source_url}
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
              <span className="inline-block mt-2 px-2 sm:px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs sm:text-sm">
                {plant.category}
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 active:text-gray-800 text-3xl sm:text-2xl leading-none flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Admin: Generate Post. Owner/Admin: Manage Photos */}
          {!isFromPlacement && (() => {
            const currentUser = (() => {
              try {
                const userData = localStorage.getItem('userData') || localStorage.getItem('user');
                return userData ? JSON.parse(userData) : {};
              } catch {
                return {};
              }
            })();
            const isAdmin = (currentUser?.role === 'admin') || (currentUser?.email?.toLowerCase() === 'johnconnors1690@gmail.com');
            const canManagePhotos = isOwner || isAdmin;
            if (!isAdmin && !canManagePhotos) return null;
            return (
              <div className="mb-4 sm:mb-6 space-y-2">
                {canManagePhotos && (
                  <button
                    type="button"
                    onClick={() => setShowManagePhotosModal(true)}
                    className="w-full bg-emerald-600 text-white py-3 px-4 rounded-lg hover:bg-emerald-700 transition-colors font-semibold flex items-center justify-center gap-2 min-h-[44px] touch-manipulation"
                    title="Search, upload, add by URL, replace, delete, edit attribution"
                  >
                    <ImageIcon className="w-5 h-5" />
                    Manage Photos
                  </button>
                )}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => { setShowGeneratePostModal(true); setGenerateError(null); }}
                    className="w-full bg-gray-900 text-white py-3 px-4 rounded-lg hover:bg-black transition-colors font-semibold flex items-center justify-center gap-2 min-h-[44px] touch-manipulation"
                    title="Generate a Plant of the Day post (admin only)"
                  >
                    <Sparkles className="w-5 h-5" />
                    Generate Post (Admin)
                  </button>
                )}
              </div>
            );
          })()}

          {/* Main Image Display */}
          {currentImage && (
            <div className="mb-4 sm:mb-6">
              <div className="relative flex justify-center items-center bg-gray-50 rounded-lg p-2">
                <img
                  src={currentImage}
                  alt={`${plant.common_name} - Image ${selectedImageIndex + 1}`}
                  className="max-w-full max-h-64 sm:max-h-80 md:max-h-96 object-contain rounded-lg cursor-zoom-in"
                  title="Click to enlarge"
                  onClick={() => setLightboxImageUrl(currentImage)}
                onError={(e) => {
                  e.target.src = 'https://placehold.co/400x300/e5e7eb/6b7280?text=Plant+Image';
                }}
              />
                {/* Photo attribution overlay */}
                {currentAttribution && (
                  <div className="absolute bottom-4 left-4 bg-black bg-opacity-70 text-white text-xs px-3 py-1.5 rounded-lg">
                    {currentAttribution}
                  </div>
                )}
              </div>
              {allImages.length > 1 && (
                <div className="text-center mt-2 text-sm text-gray-600">
                  Image {selectedImageIndex + 1} of {allImages.length}
                </div>
              )}
            </div>
          )}

          {/* Image Lightbox */}
          {lightboxImageUrl && (
            <div
              className="fixed inset-0 bg-black bg-opacity-90 z-[120] flex items-center justify-center p-4"
              onClick={() => setLightboxImageUrl(null)}
            >
              <div className="relative max-w-6xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => setLightboxImageUrl(null)}
                  className="absolute -top-10 right-0 text-white hover:text-gray-300 text-3xl leading-none"
                  aria-label="Close image preview"
                >
                  ×
                </button>
                <img
                  src={lightboxImageUrl}
                  alt={`${plant.common_name} - enlarged`}
                  className="w-full max-h-[90vh] object-contain rounded-lg"
                />
                {currentAttribution && (
                  <div className="mt-3 text-center text-xs text-gray-200">
                    {currentAttribution}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Owner Action: Confirm image matches plant (remove "Not Verified") */}
          {isOwner &&
            !plant.is_public &&
            plant.image_url &&
            !plant.image_verified &&
            plant.image_confidence !== 'verified_botanical' &&
            !(plant.image_confidence === 'ornamental' || plant.not_a_botanical_species) &&
            typeof onMarkImageVerified === 'function' && (
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-orange-900">Not Verified (Private Plant)</p>
                    <p className="text-xs sm:text-sm text-orange-800 mt-1">
                      If you’ve confirmed this photo matches your plant, you can remove the “Not Verified” badge.
                    </p>
                    <button
                      onClick={async () => {
                        if (markingVerified) return;
                        const ok = window.confirm(
                          'Mark this plant image as verified?\n\nThis removes the “Not Verified” badge for your private plant.'
                        );
                        if (!ok) return;
                        try {
                          setMarkingVerified(true);
                          await onMarkImageVerified(plant, true);
                        } finally {
                          setMarkingVerified(false);
                        }
                      }}
                      disabled={markingVerified}
                      className="mt-3 inline-flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium min-h-[44px] touch-manipulation"
                    >
                      {markingVerified ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Updating…
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Mark Verified
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

          {/* Image Thumbnails */}
          {allImages.length > 1 && (
            <div className="mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-900">
                {plant.image_url ? 'All Images' : 'Additional Images'}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                {allImages.map((imgUrl, idx) => {
                  const rawAttribution = imageAttributions[idx];
                  const attribution = stripHtml(rawAttribution);
                  return (
                    <div 
                      key={idx} 
                      className={`relative group cursor-pointer touch-manipulation ${
                        selectedImageIndex === idx ? 'ring-4 ring-green-500 ring-offset-2' : ''
                      }`}
                      onClick={() => setSelectedImageIndex(idx)}
                    >
                      <img
                        src={imgUrl}
                        alt={`${plant.common_name} - Thumbnail ${idx + 1}`}
                        className={`w-full h-24 sm:h-32 object-cover rounded-lg border-2 transition-all ${
                          selectedImageIndex === idx 
                            ? 'border-green-500' 
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                        onError={(e) => {
                          e.target.src = 'https://placehold.co/200x150/e5e7eb/6b7280?text=Image+Error';
                        }}
                      />
                      {selectedImageIndex === idx && (
                        <div className="absolute inset-0 bg-green-500 bg-opacity-20 rounded-lg flex items-center justify-center">
                          <div className="bg-green-500 text-white rounded-full p-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        </div>
                      )}
                      <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1.5 py-0.5 rounded">
                        {idx + 1}
                      </div>
                      {attribution && (
                        <div className="absolute top-1 right-1 bg-black bg-opacity-50 text-white text-xs px-1.5 py-0.5 rounded truncate max-w-[80%]">
                          {attribution}
                        </div>
                      )}
                      {onEdit && (
                        <button
                          type="button"
                          onClick={(e) => handleRemoveImage(idx, e)}
                          disabled={removingImageIndex === idx}
                          className="absolute top-1 left-1 z-10 min-w-[44px] min-h-[44px] p-2 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-full shadow-lg disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
                          title="Remove this image"
                          aria-label="Remove image"
                        >
                          {removingImageIndex === idx ? (
                            <Loader className="w-3 h-3 animate-spin" />
                          ) : (
                            <Trash2 className="w-3 h-3" />
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Description */}
          {plant.description && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Description</h3>
            <p className="text-gray-700">{plant.description}</p>
          </div>
          )}

          {/* Specifications Grid */}
          {(plant.size || plant.growth_rate || plant.sun_requirements || plant.water_requirements || plant.hardiness_zones || (!plant.is_public && plantData.price)) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
              {plant.size && (
            <div>
              <h4 className="font-semibold text-gray-700 mb-1">Size</h4>
              <p className="text-gray-600">{plant.size}</p>
            </div>
              )}
              {plant.growth_rate && (
            <div>
              <h4 className="font-semibold text-gray-700 mb-1">Growth Rate</h4>
              <p className="text-gray-600">{plant.growth_rate}</p>
            </div>
              )}
              {plant.sun_requirements && (
            <div>
              <h4 className="font-semibold text-gray-700 mb-1">Sun Requirements</h4>
              <p className="text-gray-600">{plant.sun_requirements}</p>
            </div>
              )}
              {plant.water_requirements && (
            <div>
              <h4 className="font-semibold text-gray-700 mb-1">Water Needs</h4>
              <p className="text-gray-600">{plant.water_requirements}</p>
            </div>
              )}
              {plant.hardiness_zones && (
            <div>
              <h4 className="font-semibold text-gray-700 mb-1">Hardiness Zones</h4>
              <p className="text-gray-600">{plant.hardiness_zones}</p>
            </div>
              )}
            {!plant.is_public && plantData.price && (
              <div>
                <h4 className="font-semibold text-gray-700 mb-1">Price</h4>
                <p className="text-gray-600">${plantData.price}</p>
              </div>
            )}
          </div>
          )}

          {/* Additional Details */}
          {(plantData.spacing || plantData.notes) && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              {plantData.spacing && (
                <p className="text-sm text-gray-700 mb-2">
                  <span className="font-semibold">Spacing:</span> {plantData.spacing}
                </p>
              )}
              {plantData.notes && (
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Notes:</span> {plantData.notes}
                </p>
              )}
            </div>
          )}

          {/* Attribution */}
          <div className="mb-6">
            <PlantAttribution plant={plant} />
          </div>

          {/* AI Lookup, View Images on Google, Add to Collection, and Share Buttons */}
          <div className="mb-6 pt-4 border-t space-y-3">
            <div className="flex gap-3">
              {onAILookup && (
                <button
                  onClick={() => onAILookup(plant)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-all shadow-md hover:shadow-lg font-medium text-sm sm:text-base min-h-[44px]"
                  title="Get AI-enhanced information about this plant"
                >
                  <Sparkles size={20} />
                  <span>AI Lookup</span>
                </button>
              )}
              {plant.botanical_name && (
                <a
                  href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(plant.botanical_name)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all shadow-md hover:shadow-lg font-medium text-sm sm:text-base min-h-[44px] touch-manipulation"
                  title={`View images of ${plant.botanical_name} on Google`}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span>View Images on Google</span>
                </a>
              )}
              {onLike && !isOwner && (
                <button
                  onClick={() => {
                    if (isFromPublicLibrary && isLiked && onRemoveFromCollection) {
                      onRemoveFromCollection(plant);
                    } else {
                      onLike(plant);
                    }
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg active:shadow-md font-medium text-sm sm:text-base min-h-[44px] touch-manipulation ${
                    isLiked
                      ? isFromPublicLibrary
                        ? 'bg-orange-600 text-white hover:bg-orange-700 active:bg-orange-800'
                        : 'bg-pink-600 text-white hover:bg-pink-700 active:bg-pink-800'
                      : 'bg-pink-100 text-pink-700 hover:bg-pink-200 active:bg-pink-300'
                  }`}
                  title={
                    isFromPublicLibrary && isLiked
                      ? "Remove from Private Collection (plant remains in Public Library)"
                      : isLiked
                      ? "Remove from Private Collection"
                      : "Add to Private Collection"
                  }
                >
                  {isLiked ? (isFromPublicLibrary ? '📚' : '❤️') : '🤍'}
                  <span>
                    {isFromPublicLibrary && isLiked
                      ? 'Remove from Collection'
                      : isLiked
                      ? 'In Collection'
                      : 'Add to Collection'}
                  </span>
                </button>
              )}
            {!isClientView && (
            <button
              onClick={handleShare}
                className="flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 active:from-green-700 active:to-emerald-700 transition-all shadow-md hover:shadow-lg font-medium text-sm sm:text-base min-h-[44px] touch-manipulation"
              title="Share this plant"
            >
                <Share2 size={20} className="flex-shrink-0" />
              <span>Share Plant</span>
            </button>
            )}
            </div>
            {copiedLink && (
              <p className="text-sm text-green-600 text-center">Link copied to clipboard!</p>
            )}
            
          </div>

          {/* Set Image Button (for placements) */}
          {isFromPlacement && allImages.length > 0 && (
            <div className="mb-6 pt-4 border-t">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-3 font-medium">
                  Select which image should display on the plan:
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {allImages.map((img, idx) => (
              <button
                      key={idx}
                      onClick={() => setSelectedImageForPlacement(img)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all min-h-[44px] ${
                        selectedImageForPlacement === img
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-500'
                      }`}
                    >
                      Image {idx + 1}
              </button>
                  ))}
          </div>
                {selectedImageForPlacement && (
                <button
                  onClick={() => {
                      if (onSetPlacementImage) {
                        onSetPlacementImage(selectedImageForPlacement);
                      }
                    }}
                    className="w-full bg-green-600 text-white py-2.5 px-4 rounded-lg hover:bg-green-700 active:bg-green-800 min-h-[44px] touch-manipulation font-medium"
                  >
                    Set as Plan Image
                </button>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {(isOwner || (isFromPlacement && isDesigner)) && (
            <div className="space-y-2 sm:space-y-3 pt-4 border-t">
              {isFromPlacement ? (
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    {onEditProjectItem && (
                      <button
                        onClick={() => onEditProjectItem(plant)}
                        className="flex-1 bg-blue-600 text-white py-2.5 px-4 rounded-lg hover:bg-blue-700 active:bg-blue-800 min-h-[44px] touch-manipulation font-medium"
                      >
                        Edit Project Plant
                      </button>
                    )}
                    {onEditLibraryPlant && (
                      <button
                        onClick={() => onEditLibraryPlant(plant)}
                        className="flex-1 bg-white text-gray-900 py-2.5 px-4 rounded-lg border border-gray-300 hover:bg-gray-50 active:bg-gray-100 min-h-[44px] touch-manipulation font-medium"
                      >
                        Edit in Plant Library
                      </button>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    Project edits affect only this plan’s plant list. Library edits affect your saved plant library.
                  </div>
                </div>
              ) : (
                <div
                  className="flex flex-col sm:flex-row gap-2 sm:gap-3"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDownCapture={(e) => e.stopPropagation()}
                >
                  {onEdit && (
                    <button
                      type="button"
                      role="button"
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onEdit(plant);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(plant);
                      }}
                      className="flex-1 bg-blue-600 text-white py-2.5 px-4 rounded-lg hover:bg-blue-700 active:bg-blue-800 min-h-[48px] min-w-[48px] touch-manipulation font-medium select-none"
                    >
                      Edit Plant
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete ${plant.common_name}? This cannot be undone.`)) {
                          onDelete(plant.id);
                        }
                      }}
                      className="flex-1 bg-red-600 text-white py-2.5 px-4 rounded-lg hover:bg-red-700 active:bg-red-800 min-h-[44px] touch-manipulation font-medium"
                    >
                      Delete Plant
                    </button>
                  )}
                </div>
              )}
              
              {/* Share to Community Button */}
              {onShareToCommunity && (
                <div className="flex gap-2 sm:gap-3">
                  {!plant.is_public ? (
                    <button
                      onClick={() => onShareToCommunity(plant, true)}
                      className="flex-1 bg-green-600 text-white py-2.5 px-4 rounded-lg hover:bg-green-700 active:bg-green-800 flex items-center justify-center gap-2 min-h-[44px] touch-manipulation font-medium"
                    >
                      🌍 Share with Community
                    </button>
                  ) : (
                    <button
                      onClick={() => onShareToCommunity(plant, false)}
                      className="flex-1 bg-gray-600 text-white py-2.5 px-4 rounded-lg hover:bg-gray-700 active:bg-gray-800 flex items-center justify-center gap-2 min-h-[44px] touch-manipulation font-medium"
                    >
                      🔒 Remove from Community
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Business Post Modal */}
      {showBusinessPostModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">Post to Business Account</h3>
                <button
                  onClick={() => {
                    setShowBusinessPostModal(false);
                    setPostMessage('');
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Plant Preview */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex gap-4">
                  {coerceImageUrl(plant.image_url) && (
                    <img
                      src={coerceImageUrl(plant.image_url)}
                      alt={plant.common_name}
                      className="w-20 h-20 object-cover rounded"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="font-semibold">{plant.common_name}</h4>
                    <p className="text-sm text-gray-600 italic">{plant.botanical_name}</p>
                  </div>
                </div>
              </div>

              {/* Message Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Post Message
                </label>
                <textarea
                  value={postMessage}
                  onChange={(e) => setPostMessage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                  placeholder="Write your post message..."
                />
              </div>

              {/* Connected Accounts */}
              {loadingAccounts ? (
                <div className="text-center py-4">
                  <Loader className="animate-spin mx-auto" size={24} />
                  <p className="text-sm text-gray-600 mt-2">Loading accounts...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Facebook */}
                  {socialAccounts.find(a => a.platform === 'facebook' && a.is_connected) ? (
                    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Facebook className="text-blue-600" size={24} />
                        <div>
                          <p className="font-medium">{socialAccounts.find(a => a.platform === 'facebook')?.account_name || 'Facebook'}</p>
                          <p className="text-xs text-gray-500">Connected</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePostToSocial('facebook')}
                          disabled={postingTo === 'facebook'}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          {postingTo === 'facebook' ? 'Posting...' : 'Post'}
                        </button>
                        <button
                          onClick={() => handleDisconnect('facebook')}
                          className="px-3 py-2 text-gray-600 hover:text-red-600 text-sm"
                          title="Disconnect"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={handleConnectFacebook}
                      className="w-full flex items-center justify-center gap-3 p-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                    >
                      <Facebook className="text-blue-600" size={24} />
                      <span className="font-medium">Connect Facebook Page</span>
                    </button>
                  )}

                  {/* Instagram */}
                  {socialAccounts.find(a => a.platform === 'instagram' && a.is_connected) ? (
                    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Instagram className="text-pink-600" size={24} />
                        <div>
                          <p className="font-medium">{socialAccounts.find(a => a.platform === 'instagram')?.account_name || 'Instagram'}</p>
                          <p className="text-xs text-gray-500">Connected</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePostToSocial('instagram')}
                          disabled={postingTo === 'instagram'}
                          className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          {postingTo === 'instagram' ? 'Posting...' : 'Post'}
                        </button>
                        <button
                          onClick={() => handleDisconnect('instagram')}
                          className="px-3 py-2 text-gray-600 hover:text-red-600 text-sm"
                          title="Disconnect"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                      <div className="flex items-center gap-3 text-gray-500">
                        <Instagram className="text-pink-600" size={24} />
                        <span className="text-sm">Connect Facebook to enable Instagram posting</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 pt-4 border-t text-xs text-gray-500">
                <p>💡 Tip: Connect your Facebook Business Page to post to both Facebook and Instagram. Your Instagram Business account must be connected to your Facebook Page.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate Post Modal (admin-only) */}
      {showGeneratePostModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-3 sm:p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowGeneratePostModal(false);
          }}
        >
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 sm:p-6 border-b flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Generate Plant of the Day Post</h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Admin-only. Generates a draft you can edit and copy. No auto-publishing.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowGeneratePostModal(false)}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4">
              {/* Image for post - lookup, upload, add by URL, edit attribution */}
              <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-gray-900">Image for this post</p>
                <div className="flex flex-wrap items-center gap-3">
                  {plant?.image_url && (
                    <div className="relative">
                      <img
                        src={coerceImageUrl(plant.image_url)}
                        alt={plant.common_name}
                        className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                        onError={(e) => { e.target.src = 'https://placehold.co/64x64/e5e7eb/6b7280?text=Image'; }}
                      />
                      {(plant.photo_attribution || editAttributionIndex === 0) && (
                        <p className="text-xs text-gray-600 mt-1 max-w-[80px] truncate" title={plant.photo_attribution || ''}>
                          {plant.photo_attribution || 'No attribution'}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-2 flex-1 min-w-0 flex-wrap">
                    <button
                      type="button"
                      onClick={handleGeneratePostFindPhotos}
                      disabled={generatePostFindPhotosLoading}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-60 text-sm font-medium min-h-[44px] touch-manipulation"
                    >
                      {generatePostFindPhotosLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      Lookup verified photos
                    </button>
                    <input
                      ref={generatePostUploadInputRef}
                      type="file"
                      accept="image/*,image/heic,image/heif,.heic,.heif"
                      className="hidden"
                      onChange={async (e) => {
                        let f = e.target.files?.[0];
                        if (f && isHeicFile(f)) {
                          try {
                            f = await convertHeicToJpeg(f);
                          } catch (err) {
                            alert(err.message || 'Could not convert HEIC image.');
                            e.target.value = '';
                            return;
                          }
                        }
                        if (f) setGeneratePostPendingUploadFile(f);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => generatePostUploadInputRef.current?.click()}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium min-h-[44px] touch-manipulation"
                    >
                      <Upload className="w-4 h-4" />
                      Upload photo
                    </button>
                    {plant?.image_url && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditAttributionIndex(0);
                          const a = plant.photo_attribution || '';
                          setEditAttributionValue(a.startsWith('Photo by ') ? a.slice(9) : a);
                        }}
                        className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium min-h-[44px] touch-manipulation"
                      >
                        <Pencil className="w-4 h-4" />
                        Edit attribution
                      </button>
                    )}
                  </div>
                </div>
                {/* Edit attribution inline form */}
                {editAttributionIndex === 0 && (
                  <div className="flex gap-2 items-center bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <input
                      type="text"
                      value={editAttributionValue}
                      onChange={(e) => setEditAttributionValue(e.target.value)}
                      placeholder="Photographer name (e.g., Jane Doe)"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveAttribution()}
                    />
                    <button type="button" onClick={handleSaveAttribution} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">
                      Save
                    </button>
                    <button type="button" onClick={() => { setEditAttributionIndex(null); setEditAttributionValue(''); }} className="px-3 py-2 text-gray-600 hover:text-gray-800 text-sm">
                      Cancel
                    </button>
                  </div>
                )}
                {/* Gallery images with Edit attribution */}
                {allImages.length > 1 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-600">Gallery images</p>
                    {allImages.slice(1).map((url, i) => {
                      const idx = i + 1;
                      const attr = imageAttributions[idx];
                      const attrText = attr ? (attr.startsWith('Photo by ') ? attr.slice(9) : attr) : '';
                      return (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <img src={coerceImageUrl(url)} alt="" className="w-10 h-10 object-cover rounded border" />
                          {editAttributionIndex === idx ? (
                            <>
                              <input
                                type="text"
                                value={editAttributionValue}
                                onChange={(e) => setEditAttributionValue(e.target.value)}
                                placeholder="Photographer"
                                className="flex-1 px-2 py-1 border rounded text-xs max-w-[140px]"
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveAttribution()}
                              />
                              <button type="button" onClick={handleSaveAttribution} className="px-2 py-1 bg-emerald-600 text-white rounded text-xs">Save</button>
                              <button type="button" onClick={() => { setEditAttributionIndex(null); setEditAttributionValue(''); }} className="text-gray-500 text-xs">Cancel</button>
                            </>
                          ) : (
                            <>
                              <span className="text-gray-600 truncate max-w-[120px]">{attrText || 'No attribution'}</span>
                              <button type="button" onClick={() => { setEditAttributionIndex(idx); setEditAttributionValue(attrText); }} className="text-gray-500 hover:text-gray-700" title="Edit attribution">
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Pending upload: choose Primary or Gallery */}
              {generatePostPendingUploadFile && (
                <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-800 mb-2">Photo selected. Add as:</p>
                  <input
                    type="text"
                    value={generatePostUploadAttribution}
                    onChange={(e) => setGeneratePostUploadAttribution(e.target.value)}
                    placeholder="Photographer name (optional)"
                    className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleGeneratePostUploadConfirm(true)}
                      disabled={generatePostUploadLoading}
                      className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-60 touch-manipulation"
                    >
                      {generatePostUploadLoading ? <Loader className="w-4 h-4 animate-spin mx-auto" /> : 'Set as primary'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleGeneratePostUploadConfirm(false)}
                      disabled={generatePostUploadLoading}
                      className="flex-1 px-4 py-2.5 bg-white border-2 border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-60 touch-manipulation"
                    >
                      {generatePostUploadLoading ? null : 'Add to gallery'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setGeneratePostPendingUploadFile(null); setGeneratePostUploadAttribution(''); generatePostUploadInputRef.current && (generatePostUploadInputRef.current.value = ''); }}
                      className="px-3 py-2 text-gray-600 hover:text-gray-800 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Options */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={includeDiscoveryLine}
                    onChange={(e) => setIncludeDiscoveryLine(e.target.checked)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Append a short discovery line (plantaday.com)</p>
                    {includeDiscoveryLine && (
                      <input
                        type="text"
                        value={discoveryLineText}
                        onChange={(e) => setDiscoveryLineText(e.target.value)}
                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={includePhotoCaption}
                    onChange={(e) => setIncludePhotoCaption(e.target.checked)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Include a final photo caption line (optional)</p>
                    <p className="text-xs text-gray-600">Shown separately below either way; this only affects the copied post.</p>
                    {includePhotoCaption && (
                      <input
                        type="text"
                        value={photoCaptionText}
                        onChange={(e) => setPhotoCaptionText(e.target.value)}
                        placeholder={(() => {
                          const pd = plant?.plant_data || {};
                          return pd.photo_source_url
                            ? `Photo source: ${pd.photo_source_url}`
                            : pd.photo_creator
                            ? `Photo by ${pd.photo_creator}`
                            : plant.photo_attribution
                            ? `Photo: ${plant.photo_attribution}`
                            : plant.photo_source
                            ? `Photo source: ${plant.photo_source}`
                            : 'Photo: (optional)';
                        })()}
                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGeneratePost}
                  disabled={generateLoading}
                  className="w-full bg-emerald-600 text-white py-3 px-4 rounded-lg hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed font-semibold flex items-center justify-center gap-2 min-h-[44px] touch-manipulation"
                >
                  {generateLoading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Generating…
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate Draft
                    </>
                  )}
                </button>

                {generateError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">{generateError}</p>
                    <p className="text-xs text-red-700 mt-1">You can still write/edit a post manually below.</p>
                  </div>
                )}
                {imageQualityWarning && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                    <p className="text-sm font-medium text-amber-800">⚠️ {imageQualityWarning}</p>
                    {imageQualityDetails.length > 0 && (
                      <ul className="text-xs text-amber-700 space-y-1">
                        {imageQualityDetails.map((d, i) => (
                          <li key={i}>
                            <strong>{d.label}</strong>: {d.w}×{d.h} px
                            {d.severity === 'too_small' ? ' — too small for social (min 600px)' : ' — use 1080×1080+ for best results'}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* Editable preview */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={generatedTitle}
                    onChange={(e) => setGeneratedTitle(e.target.value)}
                    placeholder="Generated title will appear here"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
                  <textarea
                    value={generatedBody}
                    onChange={(e) => setGeneratedBody(e.target.value)}
                    placeholder="Generated body will appear here"
                    rows={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm whitespace-pre-wrap"
                  />
                </div>

                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Send to email (optional)
                    </label>
                    <input
                      type="email"
                      value={emailPostTo}
                      onChange={(e) => setEmailPostTo(e.target.value)}
                      placeholder="Leave blank to email yourself, or enter who will post on social"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={async () => {
                      const textToCopy = `${(generatedTitle || '').trim()}\n\n${(generatedBody || '').trim()}`.trim();
                      const ok = await copyPlainText(textToCopy);
                      if (ok) {
                        alert('Copied!');
                      } else {
                        alert('Copy failed. Please select and copy manually.');
                      }
                    }}
                    disabled={!((generatedTitle || '').trim() || (generatedBody || '').trim())}
                    className="flex-1 bg-gray-900 text-white py-3 px-4 rounded-lg hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed font-semibold min-h-[44px] touch-manipulation"
                  >
                    Copy Post
                  </button>
                  <button
                    type="button"
                    onClick={handleEmailPost}
                    disabled={!((generatedTitle || '').trim() || (generatedBody || '').trim()) || emailPostLoading}
                    className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed font-semibold min-h-[44px] touch-manipulation flex items-center justify-center gap-2"
                  >
                    {emailPostLoading ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4" />
                    )}
                    {emailPostTo.trim() ? `Email to ${emailPostTo.trim()}` : 'Email to Me'}
                  </button>
                  </div>
                </div>
                {emailPostError && (
                  <p className="text-sm text-red-600">{emailPostError}</p>
                )}
              </div>

              {/* Photo picker modal (when Lookup returns results) */}
              {showGeneratePostPhotoPicker && generatePostPhotoPickerPhotos.length > 0 && (
                <div className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4" onClick={() => setShowGeneratePostPhotoPicker(false)}>
                  <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold">Choose a photo to use</h4>
                      <button type="button" onClick={() => setShowGeneratePostPhotoPicker(false)} className="text-gray-500 hover:text-gray-700">×</button>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">Click a photo to add it as the primary image (with attribution).</p>
                    <p className="text-xs text-gray-600 mb-2">Choose to set as primary photo or add to gallery (keeps current primary).</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {generatePostPhotoPickerPhotos.map((p, i) => (
                        <div key={i} className="flex flex-col gap-2">
                          <img src={p.url} alt={p.alt || ''} className="aspect-square object-cover rounded-lg border-2 border-gray-200" />
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => handleGeneratePostPickPhoto(p, true)}
                              disabled={!!generatePostPickingPhoto}
                              className="flex-1 py-2 bg-emerald-600 text-white text-xs font-medium rounded hover:bg-emerald-700 disabled:opacity-50 touch-manipulation"
                            >
                              Primary
                            </button>
                            <button
                              type="button"
                              onClick={() => handleGeneratePostPickPhoto(p, false)}
                              disabled={!!generatePostPickingPhoto}
                              className="flex-1 py-2 bg-white text-gray-800 text-xs font-medium rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 touch-manipulation"
                            >
                              Gallery
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Images + attribution */}
              <div className="pt-2">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Plant Images</h4>
                <div className="space-y-3">
                  {allImages.map((url, idx) => {
                    const raw = imageAttributions[idx] || (idx === 0 ? plant.photo_attribution : null);
                    const attribution = stripHtml(raw);
                    const source = idx === 0 ? plant.photo_source : null;
                    const filenameBase = (plant.botanical_name || plant.common_name || 'plant')
                      .toString()
                      .trim()
                      .replace(/\s+/g, '_')
                      .replace(/[^a-zA-Z0-9_\-]/g, '');
                    return (
                      <div key={`${url}-${idx}`} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="flex items-stretch">
                          <div className="w-28 h-20 bg-gray-100 flex-shrink-0">
                            <img src={coerceImageUrl(url)} alt={`Plant image ${idx + 1}`} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 p-3 min-w-0">
                            <p className="text-xs text-gray-500">Image {idx + 1}{idx === 0 ? ' (primary)' : ''}</p>
                            {(attribution || source) ? (
                              <p className="text-xs text-gray-700 mt-1 break-words">
                                {source ? <span className="font-medium">Source:</span> : null} {source || ''}
                                {source && attribution ? ' · ' : ''}
                                {attribution ? <><span className="font-medium">Attribution:</span> {attribution}</> : null}
                              </p>
                            ) : (
                              <p className="text-xs text-gray-500 mt-1">No attribution recorded</p>
                            )}
                          </div>
                          <div className="p-3 flex items-center">
                            <button
                              type="button"
                              onClick={() => downloadUrl(url, `${filenameBase}_${idx + 1}.jpg`)}
                              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-xs font-medium hover:bg-gray-50 min-h-[44px] touch-manipulation"
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Photos Modal */}
      {showManagePhotosModal && plant && (
        <PlantPhotoManager
          plant={plant}
          isAdmin={(() => {
            try {
              const u = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('user') || '{}');
              return (u?.role === 'admin') || (u?.email?.toLowerCase() === 'johnconnors1690@gmail.com');
            } catch { return false; }
          })()}
          onSave={(updated) => {
            if (typeof onPlantUpdated === 'function') onPlantUpdated(updated);
          }}
          onClose={() => setShowManagePhotosModal(false)}
        />
      )}

      {/* Add External Photo Modal (admin-only UI, backend enforces admin) */}
      {showExternalPhotoModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[75] p-3 sm:p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowExternalPhotoModal(false);
          }}
        >
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 sm:p-6 border-b flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Add External Photo</h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Paste a direct image URL and source attribution. We’ll download and store it in PlantADay.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowExternalPhotoModal(false)}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4">
              {externalPhotoError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{externalPhotoError}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image URL (direct file link)</label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={externalImageUrl}
                    onChange={(e) => setExternalImageUrl(e.target.value)}
                    placeholder="https://.../photo.jpg"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      const clip = await readClipboardText();
                      if (clip) setExternalImageUrl(clip.trim());
                      else alert('Could not read clipboard. Please paste manually.');
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium min-h-[44px] touch-manipulation"
                  >
                    Paste
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Tip: Google results often aren’t direct image URLs. Open the source page and copy the image address.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source page URL</label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={externalSourcePageUrl}
                    onChange={(e) => setExternalSourcePageUrl(e.target.value)}
                    placeholder="https://source-site.com/page"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      const clip = await readClipboardText();
                      if (clip) setExternalSourcePageUrl(clip.trim());
                      else alert('Could not read clipboard. Please paste manually.');
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium min-h-[44px] touch-manipulation"
                  >
                    Paste
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                    Creator/Photographer (optional)
                    <span
                      className="inline-flex text-gray-400 hover:text-gray-600 cursor-help"
                      title="Not required – the source page URL already provides attribution"
                    >
                      <Info className="w-4 h-4" />
                    </span>
                  </label>
                  <input
                    type="text"
                    value={externalCreatorName}
                    onChange={(e) => setExternalCreatorName(e.target.value)}
                    placeholder="e.g., Jane Doe"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">License (optional)</label>
                  <input
                    type="text"
                    value={externalLicense}
                    onChange={(e) => setExternalLicense(e.target.value)}
                    placeholder="e.g., CC BY-SA 4.0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={externalSetAsPrimary} onChange={(e) => setExternalSetAsPrimary(e.target.checked)} />
                  Set as primary photo
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={externalArchiveCurrent} onChange={(e) => setExternalArchiveCurrent(e.target.checked)} />
                  Archive current primary into gallery
                </label>
              </div>

              <button
                type="button"
                onClick={handleAttachExternalPhoto}
                disabled={externalPhotoLoading}
                className="w-full bg-emerald-600 text-white py-3 px-4 rounded-lg hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed font-semibold flex items-center justify-center gap-2 min-h-[44px] touch-manipulation"
              >
                {externalPhotoLoading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Attaching…
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-5 h-5" />
                    Fetch & Attach Photo
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const PlantCard = ({ plant, onClick, onAILookup, onEdit, onDelete, onShareToCommunity, onLike, onMarkImageVerified, isOwner, isLiked, isFromPublicLibrary, isAIGenerated, isAdmin, alsoDiscoveredByAI, isAIDiscoveredOnly }) => {
  const plantData = plant.plant_data || {};
  
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow flex flex-col h-full">
      <div
        onClick={() => onClick(plant)}
        className="cursor-pointer flex-grow flex flex-col"
      >
        <div className="h-48 bg-gray-200 relative">
          {coerceImageUrl(plant.image_url) ? (
            <>
              <img
                src={coerceImageUrl(plant.image_url)}
                alt={plant.common_name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://placehold.co/300x200/e5e7eb/6b7280?text=Plant+Image';
                }}
              />
              {/* Image Verification Status Badge - Top Left */}
              {/* Don't show "Not Verified" or "Representative" badges for public library plants (they're all verified) */}
              {/* But DO show badges for AI-generated plants even if marked as public */}
              <div className="absolute top-2 left-2 z-10">
                {plant.image_verified || plant.image_confidence === 'verified_botanical' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded text-xs font-medium shadow-lg">
                    <CheckCircle className="w-3 h-3" />
                    Verified Botanical
                  </span>
                ) : (isAIGenerated || !plant.is_public) && (plant.image_confidence === 'ornamental' || plant.image_confidence === 'representative' || plant.not_a_botanical_species) ? (
                  <span 
                    className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500 text-white rounded text-xs font-medium shadow-lg cursor-help"
                    title="Click card to find verified botanical images from trusted sources"
                  >
                    <AlertCircle className="w-3 h-3" />
                    Representative Image
                  </span>
                ) : (isAIGenerated || !plant.is_public) && plant.image_url && !plant.image_verified ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-500 text-white rounded text-xs font-medium shadow-lg">
                    <AlertCircle className="w-3 h-3" />
                    Not Verified
                  </span>
                ) : null}
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 relative">
              <div className="text-center px-4">
                <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-sm font-medium mb-1">No Image Available</p>
                <p className="text-xs text-gray-500">Click to view more</p>
              </div>
            </div>
          )}
        </div>
        <div className="p-4 flex-grow flex flex-col">
          <div className="flex justify-between items-start mb-2">
            <div className="flex-1">
              {/* Botanical name - PRIMARY (prominent) */}
              <h3 className="text-lg font-bold text-gray-900 mb-1">{plant.botanical_name || 'Unknown'}</h3>
              {/* Common name - SECONDARY (smaller, below) */}
              {plant.common_name && plant.common_name !== plant.botanical_name && (
                <p className="text-sm text-gray-600 italic">Common name: {plant.common_name}</p>
              )}
            </div>
            {!plant.is_public && plantData.price && (
              <span className="text-green-600 font-semibold ml-2">${plantData.price}</span>
            )}
          </div>
          {/* Image Verification Warning - Show for non-verified or representative images */}
          {/* For AI-discovered: show "click to find actual images"; for private: show verification note */}
          {plant.image_url && !plant.image_verified && plant.image_confidence !== 'verified_botanical' && (isAIDiscoveredOnly || isAIGenerated || !plant.is_public) && (
            <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-yellow-800">
                    {plant.image_confidence === 'ornamental' || plant.image_confidence === 'representative' || plant.not_a_botanical_species 
                      ? 'Representative image - may not be exact species'
                      : 'Image not verified - may not match this exact plant'}
                  </p>
                  <p className="text-xs text-yellow-700 mt-0.5">
                    {isAIDiscoveredOnly || isAIGenerated
                      ? 'Click this card to find verified botanical images from trusted sources'
                      : 'Click to view verified botanical photos in gallery'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Image attribution - TERTIARY (if from AI/verified source) */}
          {plant.photo_attribution && (isAIGenerated || plant.photo_source === 'wikimedia' || plant.photo_source === 'inaturalist') && (
            <div className="mb-2">
              <p className="text-xs text-gray-500">
                {plant.image_verified && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-100 text-green-700 rounded mr-1">
                    ✓ Verified Botanical Image
                  </span>
                )}
                <span className="text-gray-500">{plant.photo_attribution}</span>
              </p>
            </div>
          )}
          <div className="flex gap-2 mb-3 flex-wrap">
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              plant.category === 'Trees' ? 'bg-amber-100 text-amber-800' :
              plant.category === 'Shrubs' ? 'bg-emerald-100 text-emerald-800' :
              plant.category === 'Perennials' ? 'bg-purple-100 text-purple-800' :
              plant.category === 'Annuals' ? 'bg-pink-100 text-pink-800' :
              plant.category === 'Grasses' ? 'bg-teal-100 text-teal-800' :
              plant.category === 'Vines' ? 'bg-indigo-100 text-indigo-800' :
              plant.category === 'Bulbs' ? 'bg-yellow-100 text-yellow-800' :
              plant.category === 'Succulents' ? 'bg-orange-100 text-orange-800' :
              'bg-green-100 text-green-800'
            }`}>
              {plant.category}
            </span>
            {plant.size && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                {plant.size}
              </span>
            )}
            {/* Show Public Library, AI Discovered, or Private Collection label */}
            {plant.is_public ? (
              <>
                {isAIDiscoveredOnly ? (
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs flex items-center gap-1" title="Found by AI search - not yet in our database">
                    ✨ AI Discovered
                  </span>
                ) : (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs flex items-center gap-1">
                    🌍 Public Library
                  </span>
                )}
                {alsoDiscoveredByAI && !isAIDiscoveredOnly && (
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs flex items-center gap-1" title="Also found by AI search">
                    ✨ Also discovered by AI
                  </span>
                )}
              </>
            ) : (
              <>
              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs flex items-center gap-1">
                ✨ Private Collection
                </span>
                {isFromPublicLibrary && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs flex items-center gap-1" title="Added from Public Library">
                    📚 From Public Library
              </span>
                )}
                {isAIGenerated && (
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs flex items-center gap-1" title="AI Generated">
                    🤖 AI Generated
                  </span>
                )}
                {alsoDiscoveredByAI && (
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs flex items-center gap-1" title="Also found by AI search">
                    ✨ Also discovered by AI
                  </span>
                )}
              </>
            )}
          </div>
          <p className="text-sm text-gray-700 line-clamp-2 flex-grow">{plant.description}</p>
        </div>
      </div>
      
      {/* Action Buttons - Bottom Aligned */}
      <div className="px-4 pb-4 mt-auto space-y-2">
        {/* Verify Image Button - For private plants with unverified images (owner confirms photo matches) */}
        {!plant.is_public && plant.image_url && !plant.image_verified && isOwner && typeof onMarkImageVerified === 'function' && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Mark this plant image as verified?\n\nThis removes the "Not Verified" badge for your private plant.')) {
                onMarkImageVerified(plant, true);
              }
            }}
            className="w-full py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors bg-amber-600 hover:bg-amber-700 text-white shadow-md"
            title="Confirm this photo matches your plant"
          >
            <CheckCircle className="w-4 h-4" />
            Verify Image
          </button>
        )}
        {/* Find Photos Button - Show only for non-public plants or admins (not on front of public library cards) */}
        {!plant.is_public && ((!plant.image_url || !plant.image_verified || plant.image_confidence !== 'verified_botanical') || isAdmin) && onAILookup && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAILookup(plant);
            }}
            className="w-full py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors bg-blue-600 hover:bg-blue-700 text-white shadow-md"
            title="Find verified botanical photos for this plant"
          >
            <ImageIcon className="w-4 h-4" />
            {plant.image_url ? 'Find Verified Photos' : 'Find Photos'}
          </button>
        )}
        
        {/* Add to Private Collection Button (for public plants, non-owners) - This includes AI search results */}
        {plant.is_public && !isOwner && onLike && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onLike(plant);
            }}
            className={`w-full py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              isLiked
                ? 'bg-pink-600 text-white hover:bg-pink-700'
                : 'bg-pink-100 text-pink-700 hover:bg-pink-200'
            }`}
          >
            {isLiked ? '❤️ In Collection' : '🤍 Add to Private Collection'}
          </button>
        )}
        
        {/* Edit, Share, and Delete buttons (only for owner's plants) - separate touch zone for mobile */}
        {isOwner && (
          <div
            className="flex flex-col sm:flex-row gap-2 mt-2 relative z-10"
            onClick={(e) => e.stopPropagation()}
            onPointerDownCapture={(e) => e.stopPropagation()}
            onTouchStartCapture={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              role="button"
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (onEdit) onEdit(plant);
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (onEdit) onEdit(plant);
              }}
              className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 text-sm flex items-center justify-center gap-1 min-h-[44px] min-w-[44px] touch-manipulation font-medium"
            >
              ✏️ Edit
            </button>
            {!plant.is_public && (
              <button
                type="button"
                onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); if (onShareToCommunity) onShareToCommunity(plant); }}
                onClick={(e) => { e.stopPropagation(); if (onShareToCommunity) onShareToCommunity(plant); }}
                className="flex-1 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 text-sm flex items-center justify-center gap-1 min-h-[44px] min-w-[44px] touch-manipulation font-medium"
                title="Share this plant with the community"
              >
                🌍 Share
              </button>
            )}
            {plant.is_public && (
              <button
                type="button"
                onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); if (onShareToCommunity) onShareToCommunity(plant, false); }}
                onClick={(e) => { e.stopPropagation(); if (onShareToCommunity) onShareToCommunity(plant, false); }}
                className="flex-1 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 active:bg-gray-800 text-sm flex items-center justify-center gap-1 min-h-[44px] min-w-[44px] touch-manipulation font-medium"
                title="Remove from Public Library (keep in Private Collection only)"
              >
                🔒 Unshare
              </button>
            )}
            <button
              type="button"
              onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); if (onDelete && window.confirm(`Delete ${plant.common_name}? This cannot be undone.`)) onDelete(plant.id); }}
              onClick={(e) => { e.stopPropagation(); if (onDelete && window.confirm(`Delete ${plant.common_name}? This cannot be undone.`)) onDelete(plant.id); }}
              className="flex-1 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 active:bg-red-800 text-sm flex items-center justify-center gap-1 min-h-[44px] min-w-[44px] touch-manipulation font-medium"
              title="Delete this plant"
            >
              🗑️ Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// AI Lookup Modal - Fetches enhanced plant info and allows saving to collection
export const AILookupModal = ({ plant, onClose, onSaveToCollection, onUseForEdit }) => {
  const [loading, setLoading] = useState(false);
  const [aiData, setAiData] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [findingPhoto, setFindingPhoto] = useState(false);
  const [photoOptions, setPhotoOptions] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [addingImage, setAddingImage] = useState(false);
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);
  const [gallerySelectedIndex, setGallerySelectedIndex] = useState(0);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [gallerySelectedImages, setGallerySelectedImages] = useState([]);
  const [askQuestion, setAskQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [aiAnswer, setAiAnswer] = useState(null);
  const [aiAnswerPhotos, setAiAnswerPhotos] = useState([]);
  const [showSelectFieldsModal, setShowSelectFieldsModal] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false); // New: Show edit form before saving
  const [selectedFields, setSelectedFields] = useState({
    care_notes: true,
    growing_notes: true,
    typical_price: false,
    mature_size: false,
    light_requirements: false,
    water_requirements: false,
    hardiness_zones: false
  });
  const [editablePlantData, setEditablePlantData] = useState(null); // Editable plant data
  const [photoSearchColor, setPhotoSearchColor] = useState(''); // Color filter for photo search
  const [saveDestination, setSaveDestination] = useState('private'); // 'private' or 'public'
  const [saveWithoutImage, setSaveWithoutImage] = useState(false); // Allow saving without image
  
  // Use parent's currentUser prop if available, otherwise read from localStorage once
  const [modalCurrentUser] = React.useState(() => {
    try {
      const userData = localStorage.getItem('userData') || localStorage.getItem('user');
      return userData ? JSON.parse(userData) : {};
    } catch (e) {
      return {};
    }
  });
  const isOwner = plant?.id && plant?.created_by_designer_id === modalCurrentUser?.id;
  const isAdmin = modalCurrentUser?.role === 'admin' || modalCurrentUser?.email === 'john@plantaday.com';

  const fetchAIData = React.useCallback(async () => {
    if (!plant) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // If the plant is an AI suggestion from smart-search, use its pre-fetched data
      // This avoids redundant API calls and preserves the image already fetched
      if (plant.isAISuggestion && plant.aiPlantData) {
        const aiPlant = plant.aiPlantData;
        setAiData(aiPlant);
        
        // Use the image from smart-search (already fetched and unique per plant)
        if (plant.image_url) {
          setSelectedPhoto(plant.image_url);
          setSelectedImages([plant.image_url]);
        } else if (aiPlant.image_url) {
          setSelectedPhoto(aiPlant.image_url);
          setSelectedImages([aiPlant.image_url]);
        }
        
        // Initialize editable plant data from AI suggestion
        setEditablePlantData({
          common_name: aiPlant.common_name || plant.common_name || '',
          botanical_name: aiPlant.botanical_name || plant.botanical_name || '',
          category: aiPlant.category || plant.category || 'Perennials',
          size: aiPlant.mature_size || plant.plant_data?.size || '',
          sun_requirements: aiPlant.light_requirements || plant.plant_data?.sun_requirements || '',
          water_requirements: aiPlant.water_requirements || plant.plant_data?.water_requirements || '',
          hardiness_zones: aiPlant.hardiness_zones || plant.plant_data?.hardiness_zones || '',
          description: aiPlant.care_notes || aiPlant.growing_notes || plant.description || '',
          growth_rate: plant.plant_data?.growth_rate || '',
          typical_price: aiPlant.typical_price || '',
          care_notes: aiPlant.why_recommended || aiPlant.care_notes || '',
          growing_notes: aiPlant.key_features || aiPlant.growing_notes || ''
        });
        
        // Don't auto-fetch photos - user must click "Find More Verified Photos" button
        
        setLoading(false);
        return; // Exit early, no need to refetch from /ai-lookup
      }
      
      // Otherwise, fetch AI data from the API (for manual AI lookup)
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/plants/ai-lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ 
          query: plant.botanical_name || plant.common_name 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.message || data.error || 'Failed to fetch AI data';
        
        // Check for authentication errors
        if (response.status === 401 || response.status === 403) {
          if (errorMessage.toLowerCase().includes('token') || 
              errorMessage.toLowerCase().includes('authentication') ||
              errorMessage.toLowerCase().includes('unauthorized')) {
            throw new Error('Please sign in to use AI lookup. You need to be logged in to search for plant information.');
          }
        }
        
        // Check for authentication errors in the error message
        if (errorMessage.toLowerCase().includes('token') || 
            errorMessage.toLowerCase().includes('authentication') ||
            errorMessage.toLowerCase().includes('unauthorized') ||
            errorMessage.toLowerCase().includes('invalid token')) {
          throw new Error('Please sign in to use AI lookup. Your session may have expired.');
        }
        
        throw new Error(errorMessage);
      }

      setAiData(data.plant);
      // Set initial photo if available (only if not null/placeholder)
      if (data.plant?.image_url && data.plant.image_url !== 'null' && !data.plant.image_url.includes('placeholder')) {
        setSelectedPhoto(data.plant.image_url);
        setSelectedImages([data.plant.image_url]); // Initialize with primary image
      } else {
        // No valid photo - clear selection so we show "View Gallery" button
        setSelectedPhoto(null);
        setSelectedImages([]);
      }
      // Initialize editable plant data
      setEditablePlantData({
        common_name: data.plant.common_name || plant.common_name || '',
        botanical_name: data.plant.botanical_name || plant.botanical_name || '',
        category: data.plant.category || plant.category || 'Perennials',
        size: data.plant.mature_size || plant.size || '',
        sun_requirements: data.plant.light_requirements || plant.sun_requirements || '',
        water_requirements: data.plant.water_requirements || plant.water_requirements || '',
        hardiness_zones: data.plant.hardiness_zones || plant.hardiness_zones || '',
        description: data.plant.care_notes || data.plant.growing_notes || plant.description || '',
        growth_rate: plant.growth_rate || '',
        typical_price: data.plant.typical_price || '',
        care_notes: data.plant.care_notes || '',
        growing_notes: data.plant.growing_notes || ''
      });
    } catch (err) {
      console.error('AI Lookup Error:', err);
      const errorMessage = err.message || 'Unknown error';
      // Check for authentication errors in the error message
      if (errorMessage.toLowerCase().includes('token') || 
          errorMessage.toLowerCase().includes('authentication') ||
          errorMessage.toLowerCase().includes('unauthorized')) {
        setError('Please sign in to use AI lookup. Your session may have expired.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [plant]);

  useEffect(() => {
    if (plant) {
      // If plant has aiPlantData (from smart-search), use it directly instead of fetching
      if (plant.aiPlantData || plant.isAISuggestion) {
        const aiDataFromSearch = plant.aiPlantData || plant;
        setLoading(false);
        setError(null);
        
        // Convert smart-search format to ai-lookup format
        const convertedAiData = {
          common_name: aiDataFromSearch.common_name || plant.common_name || '',
          botanical_name: aiDataFromSearch.botanical_name || plant.botanical_name || '',
          category: aiDataFromSearch.category || plant.category || 'Perennials',
          mature_size: aiDataFromSearch.mature_size || plant.size || '',
          light_requirements: aiDataFromSearch.light_requirements || plant.sun_requirements || '',
          water_requirements: aiDataFromSearch.water_requirements || plant.water_requirements || '',
          hardiness_zones: aiDataFromSearch.hardiness_zones || plant.hardiness_zones || '',
          care_notes: aiDataFromSearch.why_recommended || aiDataFromSearch.care_notes || '',
          growing_notes: aiDataFromSearch.key_features || aiDataFromSearch.growing_notes || '',
          typical_price: aiDataFromSearch.typical_price || '',
          image_url: aiDataFromSearch.image_url || plant.image_url || '',
          image_search_term: aiDataFromSearch.image_search_term || `${aiDataFromSearch.botanical_name || aiDataFromSearch.common_name} plant`
        };
        
        setAiData(convertedAiData);
        
        // Set initial photo if available (only if not null/placeholder)
        if (convertedAiData.image_url && convertedAiData.image_url !== 'null' && !convertedAiData.image_url.includes('placeholder')) {
          setSelectedPhoto(convertedAiData.image_url);
          setSelectedImages([convertedAiData.image_url]);
        } else {
          // No valid photo - clear selection so we show "View Gallery" button
          setSelectedPhoto(null);
          setSelectedImages([]);
        }
        
        // Initialize editable plant data
        setEditablePlantData({
          common_name: convertedAiData.common_name,
          botanical_name: convertedAiData.botanical_name,
          category: convertedAiData.category,
          size: convertedAiData.mature_size,
          sun_requirements: convertedAiData.light_requirements,
          water_requirements: convertedAiData.water_requirements,
          hardiness_zones: convertedAiData.hardiness_zones,
          description: convertedAiData.care_notes || convertedAiData.growing_notes || '',
          growth_rate: plant.growth_rate || '',
          typical_price: convertedAiData.typical_price,
          care_notes: convertedAiData.care_notes,
          growing_notes: convertedAiData.growing_notes
        });
        
        // Don't auto-fetch photos - user must click "Find More Verified Photos" button
      } else {
        // Regular plant - fetch AI data
        fetchAIData();
      }
    }
  }, [plant, fetchAIData]);

  // Reset selected fields when AI data loads
  useEffect(() => {
    if (aiData) {
      setSelectedFields({
        care_notes: !!aiData.care_notes,
        growing_notes: !!aiData.growing_notes,
        typical_price: false,
        mature_size: false,
        light_requirements: false,
        water_requirements: false,
        hardiness_zones: false
      });
    }
  }, [aiData]);

  const findBetterPhotos = async () => {
    if (!plant) return;
    
    setFindingPhoto(true);
    setError(null);
    
    try {
      // Use backend endpoint to find photos (which has API keys)
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      const token = localStorage.getItem('authToken');
      
      // Use EXACT botanical name - backend will enhance it with "rose" or "plant" as needed
      // e.g., "Rosa Rhapsody in Blue" -> backend will search "Rosa Rhapsody in Blue rose"
      const botanicalName = plant.botanical_name || plant.common_name || '';
      // Remove quotes and clean up, but keep the exact cultivar name
      let cleanName = botanicalName.replace(/'/g, '').trim();
      
      // If a color is specified, add it to the search query
      if (photoSearchColor && photoSearchColor.trim() !== '') {
        const color = photoSearchColor.trim().toLowerCase();
        // Add color to search term: "Rosa Rhapsody in Blue" + "red" -> "Rosa Rhapsody in Blue red"
        cleanName = `${cleanName} ${color}`;
      }
      
      // Send the search term with optional color
      const searchTerm = cleanName;
      
      // For AI plants, always use verified only
      const isAIPlant = plant.isAISuggestion || !plant.id;
      const isVerifiedRequest = isAIPlant || plant.is_public || plant.image_verified || plant.image_confidence === 'verified_botanical';
      
      // Call backend endpoint with botanical name for accurate image search
      const response = await fetch(`${API_BASE_URL}/plants/find-photos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ 
          query: searchTerm,
          botanicalName: plant.botanical_name || plant.common_name,
          commonName: plant.common_name,
          color: photoSearchColor || null,
          verifiedOnly: isVerifiedRequest // Always verified for AI plants
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.photos && data.photos.length > 0) {
          // Filter to only verified images for AI plants
          let filteredPhotos = data.photos;
          if (isAIPlant) {
            filteredPhotos = data.photos.filter(p => 
              p.verified || p.source === 'wikimedia' || p.source === 'inaturalist' || p.source === 'kew' || p.source === 'gbif' || p.source === 'plantnet'
            );
          }
          
          if (filteredPhotos.length > 0) {
            // Use the new gallery (galleryPhotos) instead of photoOptions
            setGalleryPhotos(filteredPhotos);
            setGallerySelectedImages([]); // Reset selection
            // Also set photoOptions for backward compatibility, but prioritize galleryPhotos
            setPhotoOptions(filteredPhotos);
            setSelectedPhoto(filteredPhotos[0].url);
            setSelectedImages([filteredPhotos[0].url]); // Set first photo as selected
            // Automatically open the gallery modal
            setShowPhotoGallery(true);
            setGallerySelectedIndex(0);
          } else {
            setError('No verified botanical photos found. Try searching with a different term or use Google Images.');
          }
        } else {
          setError('No photos found. Try searching manually.');
        }
      } else {
        setError('Photo search unavailable. You can manually add an image URL.');
      }
    } catch (err) {
      console.error('Photo search error:', err);
      setError('Photo search failed. You can manually add an image URL.');
    } finally {
      setFindingPhoto(false);
    }
  };

  const handleSaveToCollection = async () => {
    if (!aiData || !editablePlantData) return;
    
    // Check if user is authenticated before attempting to save
    const token = localStorage.getItem('authToken');
    const currentUser = modalCurrentUser;
      
    if (!token || !currentUser?.id) {
      alert('Please sign in to save plants to your collection. You need to be logged in to add plants.');
      return;
    }
    
    // If edit form is not shown, show it first
    if (!showEditForm) {
      // Close gallery modal if open (gallery is now integrated into edit form)
      setShowPhotoGallery(false);
      setShowEditForm(true);
      return;
    }
    
    setSaving(true);
    try {
      // Find selected photo attribution from photoOptions or aiAnswerPhotos
      let photoAttribution = '';
      let photoSource = 'designer_contributed';
      
      if (selectedPhoto) {
        // Check galleryPhotos first (from auto-fetched gallery)
        const galleryPhotoData = galleryPhotos.find(p => p.url === selectedPhoto);
        if (galleryPhotoData) {
          if (galleryPhotoData.attribution) {
            photoAttribution = galleryPhotoData.attribution.replace(/<[^>]*>/g, '').trim();
            photoSource = galleryPhotoData.source || 'ai_generated';
          } else if (galleryPhotoData.photographer) {
            photoAttribution = `Photo by ${galleryPhotoData.photographer}`;
            photoSource = galleryPhotoData.source || 'pexels_unsplash';
          }
        }
        
        // If not found in galleryPhotos, check photoOptions (from "Find Better Photo")
        if (!photoAttribution) {
          const selectedPhotoData = photoOptions.find(p => p.url === selectedPhoto);
          if (selectedPhotoData?.photographer) {
            photoAttribution = `Photo by ${selectedPhotoData.photographer}`;
            photoSource = 'pexels_unsplash';
          } else {
            // Check aiAnswerPhotos (from "Ask" feature)
            const answerPhotoData = aiAnswerPhotos.find(p => p.url === selectedPhoto);
            if (answerPhotoData?.photographer) {
              photoAttribution = `Photo by ${answerPhotoData.photographer}`;
              photoSource = 'pexels_unsplash';
            } else if (currentUser?.name) {
              // Fallback to user name if no photographer info
              photoAttribution = `Photo by ${currentUser.name}`;
              photoSource = 'designer_contributed';
            }
          }
        }
      } else if (aiData.image_url && !selectedPhoto) {
        // If using AI-provided image, check if it has attribution
        photoAttribution = aiData.photo_attribution || (currentUser?.name ? `Photo by ${currentUser.name}` : '');
        photoSource = aiData.photo_source || 'ai_generated';
      } else if (currentUser?.name) {
        // Default attribution if no photo selected
        photoAttribution = `Photo by ${currentUser.name}`;
        photoSource = 'designer_contributed';
      }
      
      // Use editable plant data (user can edit before saving)
      const primaryImage = selectedImages.length > 0 ? selectedImages[0] : (selectedPhoto || aiData.image_url || plant.image_url || '');
      const additionalImages = selectedImages.length > 1 ? selectedImages.slice(1) : [];
      
      // Get photo attribution for primary image
      let primaryPhotoAttribution = photoAttribution;
      let primaryPhotoSource = photoSource;
      if (selectedImages.length > 0) {
        const primaryPhotoData = photoOptions.find(p => p.url === selectedImages[0]) || 
                                 aiAnswerPhotos.find(p => p.url === selectedImages[0]);
        if (primaryPhotoData?.photographer) {
          primaryPhotoAttribution = `Photo by ${primaryPhotoData.photographer}`;
          primaryPhotoSource = 'pexels_unsplash';
        }
      }
      
      // Determine if saving to public library (admin only) and if hidden
      // Only allow public library if verified photos are selected (or no image for hidden plants)
      const hasVerifiedPhoto = selectedImages.length > 0 && selectedImages.some(url => {
        const photo = galleryPhotos.find(p => p.url === url) || photoOptions.find(p => p.url === url);
        return photo && (photo.verified || photo.source === 'wikimedia' || photo.source === 'inaturalist' || photo.source === 'kew' || photo.source === 'gbif' || photo.source === 'plantnet');
      });
      
      // If admin tries to save to public library with non-verified photos, save to private instead
      const isPublic = saveDestination === 'public' && isAdmin && (hasVerifiedPhoto || !primaryImage || saveWithoutImage);
      const isHidden = isPublic && (!primaryImage || saveWithoutImage);
      
      const plantPayload = {
        common_name: editablePlantData.common_name,
        botanical_name: editablePlantData.botanical_name,
        category: editablePlantData.category,
        size: editablePlantData.size,
        sun_requirements: editablePlantData.sun_requirements,
        water_requirements: editablePlantData.water_requirements,
        hardiness_zones: editablePlantData.hardiness_zones,
        description: editablePlantData.description,
        growth_rate: editablePlantData.growth_rate || '',
        image_url: saveWithoutImage ? null : primaryImage, // Allow null if saving without image
        is_public: isPublic,
        is_hidden: isHidden, // Hidden if public and no image
        photo_attribution: primaryImage ? primaryPhotoAttribution : null,
        photo_source: primaryImage ? primaryPhotoSource : null,
        created_by_designer_id: currentUser?.id || null,
        plant_data: {
          typical_price: selectedFields.typical_price ? editablePlantData.typical_price : undefined,
          care_notes: selectedFields.care_notes ? editablePlantData.care_notes : undefined,
          growing_notes: selectedFields.growing_notes ? editablePlantData.growing_notes : undefined,
          additional_images: additionalImages
        }
      };

      await plantsAPI.create(plantPayload);
      
      if (isPublic && isHidden) {
        alert('Plant saved to public library (hidden). You can add a verified image later and publish it.');
      } else if (isPublic) {
        alert('Plant saved to public library with verified photo!');
      } else if (saveDestination === 'public' && isAdmin && !hasVerifiedPhoto && primaryImage) {
        alert('Plant saved to your private collection. Only verified photos (green badge) can be shared with the public library.');
      } else {
        alert('Plant saved to your collection!');
      }
      
      if (onSaveToCollection) {
        onSaveToCollection();
      }
      setShowEditForm(false);
      onClose();
    } catch (error) {
      console.error('Error saving plant:', error);
      const errorMessage = error.message || 'Unknown error';
      
      // Check for authentication errors
      if (errorMessage.toLowerCase().includes('token') || 
          errorMessage.toLowerCase().includes('authentication') ||
          errorMessage.toLowerCase().includes('unauthorized') ||
          errorMessage.toLowerCase().includes('invalid token') ||
          errorMessage === 'Authentication required') {
        alert('Please sign in to save plants to your collection. Your session may have expired.');
      } else {
        alert(`Error saving plant: ${errorMessage}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUseForEdit = () => {
    if (aiData && onUseForEdit) {
      // Show selection modal instead of directly replacing
      setShowSelectFieldsModal(true);
    }
  };

  const handleApplySelectedFields = async () => {
    if (!aiData) return;
    
    const currentUser = modalCurrentUser;
    const token = localStorage.getItem('authToken');
    
    // Check if user is authenticated before attempting to save
    if (!token || !currentUser?.id) {
      alert('Please sign in to save plant information. You need to be logged in to save changes to plants.');
      return;
    }
    
    // Find selected photo attribution from photoOptions or aiAnswerPhotos
    let photoAttribution = plant?.photo_attribution || '';
    let photoSource = plant?.photo_source || 'designer_contributed';
    
    if (selectedPhoto) {
      // Check photoOptions first (from "Find Better Photo")
      const selectedPhotoData = photoOptions.find(p => p.url === selectedPhoto);
      if (selectedPhotoData?.photographer) {
        photoAttribution = `Photo by ${selectedPhotoData.photographer}`;
        photoSource = 'pexels_unsplash';
      } else {
        // Check aiAnswerPhotos (from "Ask" feature)
        const answerPhotoData = aiAnswerPhotos.find(p => p.url === selectedPhoto);
        if (answerPhotoData?.photographer) {
          photoAttribution = `Photo by ${answerPhotoData.photographer}`;
          photoSource = 'pexels_unsplash';
        } else if (!photoAttribution && currentUser?.name) {
          // Fallback to user name if no photographer info
          photoAttribution = `Photo by ${currentUser.name}`;
          photoSource = 'designer_contributed';
        }
      }
    }
    
    // Build notes content from selected AI fields
    const existingNotes = plant?.plant_data?.notes || '';
    const aiNotesParts = [];
    
    if (selectedFields.care_notes && aiData.care_notes) {
      aiNotesParts.push(`Care Notes: ${aiData.care_notes}`);
    }
    if (selectedFields.growing_notes && aiData.growing_notes) {
      aiNotesParts.push(`Growing Notes: ${aiData.growing_notes}`);
    }
    if (selectedFields.typical_price && aiData.typical_price) {
      aiNotesParts.push(`Typical Price: ${aiData.typical_price}`);
    }
    if (selectedFields.mature_size && aiData.mature_size) {
      aiNotesParts.push(`Mature Size: ${aiData.mature_size}`);
    }
    if (selectedFields.light_requirements && aiData.light_requirements) {
      aiNotesParts.push(`Light Requirements: ${aiData.light_requirements}`);
    }
    if (selectedFields.water_requirements && aiData.water_requirements) {
      aiNotesParts.push(`Water Requirements: ${aiData.water_requirements}`);
    }
    if (selectedFields.hardiness_zones && aiData.hardiness_zones) {
      aiNotesParts.push(`Hardiness Zones: ${aiData.hardiness_zones}`);
    }
    
    // Append AI notes to existing notes
    const separator = existingNotes ? '\n\n--- AI Enhanced Information ---\n' : '';
    const newNotes = existingNotes + separator + aiNotesParts.join('\n\n');
    
    // Check if user owns this plant
    const isOwner = plant?.created_by_designer_id === currentUser?.id;
    const isPublicPlant = plant?.is_public === true;
    
    // If user doesn't own the plant (especially if it's public), create a new private copy
    if (!isOwner && plant?.id) {
      try {
        setSaving(true);
        const plantPayload = {
          common_name: plant.common_name,
          botanical_name: plant.botanical_name,
          category: plant.category || aiData.category || 'Perennials',
          size: plant.size || aiData.mature_size || '',
          growth_rate: plant.growth_rate || '',
          sun_requirements: plant.sun_requirements || aiData.light_requirements || '',
          water_requirements: plant.water_requirements || aiData.water_requirements || '',
          hardiness_zones: plant.hardiness_zones || aiData.hardiness_zones || '',
          description: plant.description || aiData.care_notes || aiData.growing_notes || '',
          image_url: selectedPhoto || plant.image_url || aiData.image_url || '',
          is_public: false, // Always save as private copy
          photo_attribution: selectedPhoto ? photoAttribution : (plant.photo_attribution || 'From Public Library'),
          photo_source: selectedPhoto ? photoSource : (plant.photo_source || 'public_library'),
          created_by_designer_id: currentUser?.id || null,
          plant_data: {
            ...plant.plant_data,
            notes: newNotes,
            typical_price: aiData.typical_price || plant.plant_data?.typical_price,
            care_notes: aiData.care_notes || plant.plant_data?.care_notes,
            growing_notes: aiData.growing_notes || plant.plant_data?.growing_notes
          }
        };
        
        const API_BASE_URL = process.env.REACT_APP_API_URL || 
          (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
        
        const response = await fetch(`${API_BASE_URL}/plants`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(plantPayload)
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Failed to save' }));
          const errorMessage = errorData.error || errorData.message || 'Failed to save plant';
          
          // Check for authentication errors
          if (response.status === 401 || response.status === 403 || 
              errorMessage.toLowerCase().includes('token') || 
              errorMessage.toLowerCase().includes('authentication') ||
              errorMessage.toLowerCase().includes('unauthorized')) {
            alert('Please sign in to save plant information. Your session may have expired.');
            return;
          }
          
          throw new Error(errorMessage);
        }
        
        alert(`"${plant.common_name}" saved to your Private Collection with AI-enhanced notes!`);
        if (onSaveToCollection) {
          onSaveToCollection();
        }
        setShowSelectFieldsModal(false);
        onClose();
        return;
      } catch (error) {
        console.error('Error saving plant copy:', error);
        const errorMessage = error.message || 'Unknown error';
        
        // Check for authentication errors in the error message
        if (errorMessage.toLowerCase().includes('token') || 
            errorMessage.toLowerCase().includes('authentication') ||
            errorMessage.toLowerCase().includes('unauthorized')) {
          alert('Please sign in to save plant information. Your session may have expired.');
        } else {
          alert(`Error saving plant: ${errorMessage}`);
        }
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }
    
    // If user owns the plant, use the edit flow
    if (onUseForEdit) {
    const enhancedPlant = {
      ...plant,
      // Only update image if user selected a new one
      image_url: selectedPhoto || plant.image_url,
      photo_attribution: selectedPhoto ? photoAttribution : plant?.photo_attribution,
      photo_source: selectedPhoto ? photoSource : plant?.photo_source,
      plant_data: {
        ...plant.plant_data,
        notes: newNotes
      }
    };
    
    onUseForEdit(enhancedPlant);
    }
    
    setShowSelectFieldsModal(false);
    onClose();
  };

  const handleAddImageToPlant = async () => {
    if (!plant?.id || !selectedPhoto || !isOwner) return;
    
    setAddingImage(true);
    try {
      const currentUser = modalCurrentUser;
      
      // Find the selected photo in photoOptions first (from "Find Better Photo")
      let selectedPhotoData = photoOptions.find(p => p.url === selectedPhoto);
      let attribution = '';
      let photoSource = 'designer_contributed';
      
      if (selectedPhotoData?.photographer) {
        attribution = `Photo by ${selectedPhotoData.photographer}`;
        photoSource = 'pexels_unsplash';
      } else {
        // Check aiAnswerPhotos (from "Ask" feature)
        selectedPhotoData = aiAnswerPhotos.find(p => p.url === selectedPhoto);
        if (selectedPhotoData?.photographer) {
          attribution = `Photo by ${selectedPhotoData.photographer}`;
          photoSource = 'pexels_unsplash';
        } else if (currentUser?.name) {
          // Fallback to user name if no photographer info
          attribution = `Photo by ${currentUser.name}`;
          photoSource = 'designer_contributed';
        }
      }
      
      // Get current plant data to preserve existing additional_images
      const currentPlantData = plant.plant_data || {};
      const currentAdditionalImages = Array.isArray(currentPlantData.additional_images) 
        ? currentPlantData.additional_images 
        : [];
      
      // Add the selected photo to additional_images (don't replace primary image)
      // Avoid duplicates
      const newAdditionalImages = currentAdditionalImages.includes(selectedPhoto)
        ? currentAdditionalImages
        : [...currentAdditionalImages, selectedPhoto];
      
      // Update plant: keep original image_url, add to additional_images
      await plantsAPI.update(plant.id, {
        // Don't change image_url - keep the original primary image
        plant_data: {
          ...currentPlantData,
          additional_images: newAdditionalImages
        }
      });
      
      alert('Image added to your plant\'s additional images! Your primary image remains unchanged.');
      if (onSaveToCollection) {
        onSaveToCollection();
      }
      onClose();
    } catch (error) {
      console.error('Error adding image:', error);
      alert(`Error adding image: ${error.message || 'Unknown error'}`);
    } finally {
      setAddingImage(false);
    }
  };

  const handleAskQuestion = async () => {
    if (!askQuestion.trim() || !plant) return;
    
    setAsking(true);
    setAiAnswer(null);
    setAiAnswerPhotos([]);
    setError(null);
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/plants/ai-ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          plantName: plant.botanical_name || plant.common_name,
          question: askQuestion
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to get answer');
      }
      
      setAiAnswer(data.answer);
      if (data.photos && data.photos.length > 0) {
        setAiAnswerPhotos(data.photos);
      }
      setAskQuestion(''); // Clear question after asking
    } catch (err) {
      console.error('AI Ask Error:', err);
      setError(err.message);
    } finally {
      setAsking(false);
    }
  };

  if (!plant) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-emerald-600" />
                <div>
                  <h2 className="text-2xl font-bold">Plant Information</h2>
                  {plant && (
                    <p className="text-sm text-gray-600 mt-1">
                      {plant.botanical_name || plant.common_name || 'Unknown Plant'}
                    </p>
                  )}
                </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Full plant info from AI - shown when we have aiData or editablePlantData */}
          {(aiData || editablePlantData) && (
            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <h4 className="font-semibold text-emerald-900 mb-3">About This Plant</h4>
              <div className="space-y-2 text-sm text-gray-800">
                {(aiData?.care_notes || editablePlantData?.care_notes) && (
                  <p><span className="font-medium text-emerald-800">Why recommended:</span> {aiData?.care_notes || editablePlantData?.care_notes}</p>
                )}
                {(aiData?.growing_notes || editablePlantData?.growing_notes) && (
                  <p><span className="font-medium text-emerald-800">Key features:</span> {aiData?.growing_notes || editablePlantData?.growing_notes}</p>
                )}
                {(aiData?.mature_size || editablePlantData?.size) && (
                  <p><span className="font-medium text-emerald-800">Mature size:</span> {aiData?.mature_size || editablePlantData?.size}</p>
                )}
                {(aiData?.light_requirements || editablePlantData?.sun_requirements) && (
                  <p><span className="font-medium text-emerald-800">Light:</span> {aiData?.light_requirements || editablePlantData?.sun_requirements}</p>
                )}
                {(aiData?.water_requirements || editablePlantData?.water_requirements) && (
                  <p><span className="font-medium text-emerald-800">Water:</span> {aiData?.water_requirements || editablePlantData?.water_requirements}</p>
                )}
                {(aiData?.hardiness_zones || editablePlantData?.hardiness_zones) && (
                  <p><span className="font-medium text-emerald-800">Hardiness zones:</span> {aiData?.hardiness_zones || editablePlantData?.hardiness_zones}</p>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-900">Error</p>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            </div>
          )}

            {/* Ask Question Section */}
              <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                Ask a Question
                </h4>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={askQuestion}
                    onChange={(e) => setAskQuestion(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAskQuestion();
                      }
                    }}
                    placeholder="e.g., How often should I water this? Is it deer resistant?"
                  className="flex-1 px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
                    disabled={asking}
                  />
                  <button
                    onClick={handleAskQuestion}
                    disabled={asking || !askQuestion.trim()}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 flex items-center gap-2"
                  >
                    {asking ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Asking...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Ask
                      </>
                    )}
                  </button>
                </div>
              
                {aiAnswer && (
                <div className="mt-3 p-4 bg-white rounded-lg border border-purple-200">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap mb-3">{aiAnswer}</p>
                    {aiAnswerPhotos.length > 0 && (
                          <button
                            onClick={() => {
                              setGalleryPhotos(aiAnswerPhotos);
                        setGallerySelectedImages([]);
                              setShowPhotoGallery(true);
                            }}
                      className="text-sm text-purple-600 hover:text-purple-800 font-medium flex items-center gap-2"
                    >
                                  <ImageIcon className="w-4 h-4" />
                      View {aiAnswerPhotos.length} Image{aiAnswerPhotos.length !== 1 ? 's' : ''} →
                            </button>
                    )}
                  </div>
                )}
              </div>

            {/* View More Images Button */}
            <div className="mb-6">
                <button
                onClick={async () => {
                  const botanicalName = plant?.botanical_name;
                  const commonName = plant?.common_name;
                  
                  // Validate that we have at least a botanical name or common name
                  const hasBotanicalName = botanicalName && botanicalName.trim() !== '';
                  const hasCommonName = commonName && commonName.trim() !== '';
                  
                  if (!hasBotanicalName && !hasCommonName) {
                    // If no name available, open Google Images directly
                    const searchTerm = botanicalName || commonName || 'plant';
                    const googleUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchTerm)}`;
                    window.open(googleUrl, '_blank', 'noopener,noreferrer');
                    return;
                  }
                  
                                setFindingPhoto(true);
                                try {
                                  const API_BASE_URL = process.env.REACT_APP_API_URL || 
                                    (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
                                  
                    // Build request body - use botanicalName if available, otherwise use commonName as query
                    const requestBody = {
                      commonName: commonName || null,
                      verifiedOnly: true
                    };
                    
                    if (hasBotanicalName) {
                      requestBody.botanicalName = botanicalName.trim();
                      requestBody.query = botanicalName.trim(); // Also send for legacy fallback
                    } else {
                      requestBody.query = (commonName || '').trim();
                    }
                    
                                  const response = await fetch(`${API_BASE_URL}/plants/find-photos`, {
                                    method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(requestBody)
                    });

                    if (!response.ok) {
                      const errorData = await response.json().catch(() => ({}));
                      throw new Error(errorData.error || `Server error: ${response.status}`);
                    }

                                    const data = await response.json();
                                    if (data.photos && data.photos.length > 0) {
                                      const verifiedPhotos = data.photos.filter(p => 
                                        p.url && 
                                        p.url !== 'null' && 
                                        !p.url.includes('placeholder') &&
                                        (p.verified || p.source === 'wikimedia' || p.source === 'inaturalist' || p.source === 'kew' || p.source === 'gbif' || p.source === 'plantnet')
                                      );
                                      if (verifiedPhotos.length > 0) {
                                        setGalleryPhotos(verifiedPhotos);
                                        setGallerySelectedImages([]);
                                        setShowPhotoGallery(true);
                                      } else {
                        // No verified photos found - open Google Images
                        const searchTerm = botanicalName || commonName || '';
                        const googleUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchTerm)}`;
                        window.open(googleUrl, '_blank', 'noopener,noreferrer');
                                      }
                                    } else {
                      // No photos found - open Google Images
                      const searchTerm = botanicalName || commonName || '';
                      const googleUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchTerm)}`;
                      window.open(googleUrl, '_blank', 'noopener,noreferrer');
                                  }
                                } catch (err) {
                                  console.error('Error fetching photos:', err);
                    // On error, open Google Images as fallback
                    const searchTerm = botanicalName || commonName || '';
                    if (searchTerm) {
                      const googleUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchTerm)}`;
                      window.open(googleUrl, '_blank', 'noopener,noreferrer');
                    }
                                } finally {
                                  setFindingPhoto(false);
                                }
                              }}
                              disabled={findingPhoto}
                className="w-full px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 flex items-center justify-center gap-2 font-medium"
                            >
                              {findingPhoto ? (
                          <>
                            <Loader className="w-5 h-5 animate-spin" />
                    Loading Images...
                          </>
                        ) : (
                          <>
                    <Search className="w-5 h-5" />
                    View Images
                          </>
                        )}
                      </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Wikimedia, iNaturalist & verified sources first. If none found, Google Images opens.
              </p>
                      </div>

            {/* Close Button */}
            <div className="flex justify-end">
                  <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Close
                  </button>
                </div>
              </div>
        </div>
      </div>

      {/* Photo Gallery Modal - Keep for viewing images */}
      {showPhotoGallery && galleryPhotos.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setShowPhotoGallery(false)}>
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Plant Photos</h3>
                {plant && (plant.botanical_name || plant.common_name) && (
                  <div className="mt-1 text-sm text-gray-600">
                    {plant.botanical_name || plant.common_name}
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowPhotoGallery(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            {/* Main Gallery */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {galleryPhotos.map((photo, index) => {
                  const isSelected = gallerySelectedImages.includes(photo.url);
                  const isPrimary = gallerySelectedImages[0] === photo.url;
                  
                  return (
                    <div
                      key={index}
                      className={`relative border-2 rounded-lg overflow-hidden cursor-pointer transition-all ${
                        isSelected
                          ? 'border-emerald-500 ring-4 ring-emerald-200'
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                      onClick={() => {
                        if (isSelected) {
                          setGallerySelectedImages(prev => prev.filter(url => url !== photo.url));
                        } else {
                          setGallerySelectedImages(prev => [...prev, photo.url]);
                        }
                      }}
                    >
                      <img
                        src={photo.url}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-64 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.style.display = 'none';
                        }}
                      />
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-emerald-600 text-white rounded-full p-2">
                          <CheckCircle className="w-5 h-5" />
                        </div>
                      )}
                      {isPrimary && (
                        <div className="absolute bottom-0 left-0 right-0 bg-emerald-600 text-white text-xs text-center py-1 font-medium">
                          Primary
                        </div>
                      )}
                      
                      {/* Attribution Overlay */}
                      <div className={`absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white p-2 text-xs ${isPrimary ? 'pb-6' : ''}`}>
                        {plant && (plant.botanical_name || plant.common_name) && (
                          <div className="font-semibold mb-1">
                            Plant: <span className="font-normal italic">{plant.botanical_name || plant.common_name}</span>
                          </div>
                        )}
                        {(photo.attribution || photo.photographer) && (
                          <div className="opacity-90 truncate" title={(photo.attribution || `Photo by ${photo.photographer}`).replace(/<[^>]*>/g, '').trim()}>
                            {photo.attribution ? photo.attribution.replace(/<[^>]*>/g, '').trim() : `Photo by ${photo.photographer}`}
                          </div>
                        )}
                        {/* Source and Verification Badge */}
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {photo.verified || photo.source === 'wikimedia' || photo.source === 'inaturalist' || photo.source === 'kew' || photo.source === 'gbif' || photo.source === 'plantnet' ? (
                            <div className="flex items-center gap-1">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            <span className="text-green-400">Verified</span>
                          </div>
                          ) : photo.source === 'pexels' || photo.source === 'unsplash' ? (
                            <div className="flex items-center gap-1">
                              <AlertCircle className="w-3 h-3 text-orange-400" />
                              <span className="text-orange-400">Stock Photo</span>
                            </div>
                          ) : null}
                          {photo.source && (
                            <span className="text-[10px] opacity-75 bg-white bg-opacity-20 px-1.5 py-0.5 rounded">
                              {photo.source === 'wikimedia' ? 'Wikimedia' :
                               photo.source === 'inaturalist' ? 'iNaturalist' :
                               photo.source === 'kew' ? 'Kew Gardens' :
                               photo.source === 'gbif' ? 'GBIF' :
                               photo.source === 'plantnet' ? 'PlantNet' :
                               photo.source === 'pexels' ? 'Pexels' :
                               photo.source === 'unsplash' ? 'Unsplash' :
                               photo.source}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t bg-gray-50">
              <div className="text-sm text-gray-600">
                {gallerySelectedImages.length > 0 ? (
                  <span>
                      {gallerySelectedImages.length} photo{gallerySelectedImages.length !== 1 ? 's' : ''} selected
                    </span>
                ) : (
                  <span>Click photos to view details</span>
                )}
              </div>
                  <button
                onClick={() => setShowPhotoGallery(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Close
                </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Plant Form Modal Component
const PlantFormModal = ({ plant, onClose, onSave }) => {
  const navigate = useNavigate();
  const [currentUser] = useState(() => {
    try {
      const userData = localStorage.getItem('userData') || localStorage.getItem('user');
      return userData ? JSON.parse(userData) : {};
    } catch (e) {
      return {};
    }
  });
  
  // Check if user is a designer (has role 'designer') or customer
  const isDesigner = currentUser?.role === 'designer';
  
  const [formData, setFormData] = useState({
    common_name: plant?.common_name || '',
    botanical_name: plant?.botanical_name || '',
    category: plant?.category || 'Perennials',
    size: plant?.size || '',
    growth_rate: plant?.growth_rate || 'Medium',
    sun_requirements: plant?.sun_requirements || 'Full Sun',
    water_requirements: plant?.water_requirements || 'Moderate',
    hardiness_zones: plant?.hardiness_zones || '',
    description: plant?.description || '',
    image_url: plant?.image_url || '',
    price: plant?.plant_data?.price || '',
    spacing: plant?.plant_data?.spacing || '',
    notes: plant?.plant_data?.notes || '',
    // For new plants: designers default to public, customers default to private
    // For existing plants: keep current value
    is_public: plant?.is_public !== undefined ? plant.is_public : (isDesigner ? true : false),
    photo_attribution: plant?.photo_attribution || '',
    photo_source: plant?.photo_source || 'original',
    additional_images: plant?.plant_data?.additional_images || []
  });

  // Update form data when plant prop changes (e.g., when editing a different plant)
  useEffect(() => {
    if (plant) {
      setFormData({
        common_name: plant.common_name || '',
        botanical_name: plant.botanical_name || '',
        category: plant.category || 'Perennials',
        size: plant.size || '',
        growth_rate: plant.growth_rate || 'Medium',
        sun_requirements: plant.sun_requirements || 'Full Sun',
        water_requirements: plant.water_requirements || 'Moderate',
        hardiness_zones: plant.hardiness_zones || '',
        description: plant.description || '',
        image_url: plant.image_url || '',
        price: plant.plant_data?.price || '',
        spacing: plant.plant_data?.spacing || '',
        notes: plant.plant_data?.notes || '',
        is_public: plant.is_public !== undefined ? plant.is_public : (isDesigner ? true : false),
        photo_attribution: plant.photo_attribution || '',
        photo_source: plant.photo_source || 'original',
        additional_images: plant.plant_data?.additional_images || []
      });
      
      // Update uploaded images when plant changes
      const images = [];
      if (plant.image_url) {
        images.push({ url: plant.image_url, isPrimary: true });
      }
      if (plant.plant_data?.additional_images) {
        plant.plant_data.additional_images.forEach(img => {
          const url = typeof img === 'string' ? img : (img?.url || null);
          if (url && url !== plant.image_url) {
            images.push({ url, photographer: typeof img === 'object' ? img.photographer : undefined, isPrimary: false });
          }
        });
      }
      setUploadedImages(images);
      setPrimaryImageIndex(0);
    }
  }, [plant, isDesigner]);

  const [saving, setSaving] = useState(false);
  const [uploadingImages, setUploadingImages] = useState([]); // Array of files being uploaded
  const [uploadedImages, setUploadedImages] = useState(() => {
    // Initialize with existing images
    const images = [];
    if (plant?.image_url) {
      images.push({ url: plant.image_url, isPrimary: true });
    }
    if (plant?.plant_data?.additional_images) {
      plant.plant_data.additional_images.forEach(img => {
        const url = typeof img === 'string' ? img : (img?.url || null);
        if (url && url !== plant.image_url) {
          images.push({ url, photographer: typeof img === 'object' ? img.photographer : undefined, isPrimary: false });
        }
      });
    }
    return images;
  });
  const [primaryImageIndex, setPrimaryImageIndex] = useState(0); // Index of primary image
  const [uploading, setUploading] = useState(false);
  const [aiLookupLoading, setAiLookupLoading] = useState(false);
  const [findingPhotos, setFindingPhotos] = useState(false);
  const [foundPhotos, setFoundPhotos] = useState([]);
  const [selectedFoundPhotos, setSelectedFoundPhotos] = useState([]);
  const [showPhotoSelectionModal, setShowPhotoSelectionModal] = useState(false);
  const [addByUrlValue, setAddByUrlValue] = useState('');
  const [addByUrlAttribution, setAddByUrlAttribution] = useState('');
  const [includeAttribution, setIncludeAttribution] = useState(() => {
    // Default to true if plant already has attribution, false otherwise
    return plant?.photo_attribution ? true : false;
  }); // Whether to include attribution when sharing
  const [attributionName, setAttributionName] = useState(() => {
    // Extract name from existing attribution if present
    if (plant?.photo_attribution) {
      const match = plant.photo_attribution.match(/Photo by (.+)/);
      return match ? match[1] : '';
    }
    // Default to user's name if available
    try {
      const userData = localStorage.getItem('userData') || localStorage.getItem('user');
      const user = userData ? JSON.parse(userData) : {};
      return user.name || user.fullName || '';
    } catch {
      return '';
    }
  }); // Name or social handle for attribution

  const handleImageFilesChange = async (e) => {
    let files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      files = await convertHeicFiles(files);
    } catch (err) {
      alert(err.message || 'Could not convert HEIC image. Please try a JPG or PNG.');
      e.target.value = '';
      return;
    }

    const invalidFiles = files.filter(file => file.size > 5 * 1024 * 1024);
    if (invalidFiles.length > 0) {
      alert(`Some images are too large (max 5MB). Please resize and try again.`);
        return;
      }

    setUploadingImages(prev => [...prev, ...files]);

    files.forEach(file => uploadImageFile(file));

    e.target.value = '';
  };

  const uploadImageFile = async (file) => {
    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('image', file);
    
    const token = localStorage.getItem('authToken');
    const API_BASE_URL = process.env.REACT_APP_API_URL || 
      (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
    
    try {
      const uploadResponse = await fetch(`${API_BASE_URL}/upload/plant-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Image upload failed');
      }

      const uploadData = await uploadResponse.json();
      
      // Add to uploaded images (not primary by default if there are existing images)
      const isFirstImage = uploadedImages.length === 0;
      setUploadedImages(prev => [...prev, { 
        url: uploadData.imageUrl, 
        isPrimary: isFirstImage 
      }]);
      
      // If this is the first image, set it as primary
      if (isFirstImage) {
        setPrimaryImageIndex(0);
      }
      
      // Remove from uploading queue
      setUploadingImages(prev => prev.filter(f => f !== file));
    } catch (uploadError) {
      console.error('Upload error:', uploadError);
      alert(`⚠️ Failed to upload ${file.name}: ${uploadError.message}`);
      setUploadingImages(prev => prev.filter(f => f !== file));
    } finally {
      setUploading(false);
    }
  };

  const setImageAsPrimary = (index) => {
    setUploadedImages(prev => {
      const newImages = prev.map((img, i) => ({
        ...img,
        isPrimary: i === index
      }));
      return newImages;
    });
    setPrimaryImageIndex(index);
  };

  const removeImage = (index) => {
    setUploadedImages(prev => {
      const newImages = prev.filter((_, i) => i !== index);
      // If we removed the primary image, set the first remaining as primary
      if (index === primaryImageIndex && newImages.length > 0) {
        newImages[0].isPrimary = true;
        setPrimaryImageIndex(0);
      } else if (index < primaryImageIndex) {
        // Adjust primary index if we removed an image before it
        setPrimaryImageIndex(prev => prev - 1);
      }
      return newImages;
    });
  };

  const handleAILookup = async () => {
    // Use the plant's common name or botanical name for lookup
    const query = plant?.common_name || plant?.botanical_name || formData.common_name || formData.botanical_name;
    
    if (!query) {
      alert('Please enter a plant name first to use AI lookup');
      return;
    }

    setAiLookupLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      const response = await fetch(`${API_BASE_URL}/plants/ai-lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ query })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to lookup plant');
      }

      if (data.plant) {
        const aiPlant = data.plant;
        // Get current additional images
        const currentAdditionalImages = formData.additional_images || [];
        const newAdditionalImages = aiPlant.image_url && !currentAdditionalImages.includes(aiPlant.image_url)
          ? [...currentAdditionalImages, aiPlant.image_url]
          : currentAdditionalImages;
        
        // Update form fields with AI data (only fill in empty fields or allow user to override)
        // IMPORTANT: Don't replace image_url - add AI image to additional_images instead
        setFormData(prev => ({
          ...prev,
          common_name: prev.common_name || aiPlant.common_name || '',
          botanical_name: prev.botanical_name || aiPlant.botanical_name || '',
          category: prev.category || aiPlant.category || prev.category,
          size: prev.size || aiPlant.mature_size || aiPlant.size || '',
          sun_requirements: prev.sun_requirements || aiPlant.light_requirements || aiPlant.sun_requirements || prev.sun_requirements,
          water_requirements: prev.water_requirements || aiPlant.water_requirements || prev.water_requirements,
          hardiness_zones: prev.hardiness_zones || aiPlant.hardiness_zones || '',
          description: prev.description || (aiPlant.care_notes ? 
            [aiPlant.care_notes, aiPlant.growing_notes].filter(n => n).join('\n\n') : 
            aiPlant.description) || '',
          // Don't replace image_url - keep existing main image
          // Instead, add AI image to additional_images if provided
          additional_images: newAdditionalImages
        }));

        // Also add AI image to uploadedImages array (not as primary) so it shows in the image gallery
        if (aiPlant.image_url) {
          const existingImageUrls = uploadedImages.map(img => img.url);
          if (!existingImageUrls.includes(aiPlant.image_url)) {
            setUploadedImages(prev => [...prev, {
              url: aiPlant.image_url,
              isPrimary: false
            }]);
          }
          alert(`✨ AI lookup complete! Form fields have been updated. The AI image has been added to additional images (not replacing your main image). Review and save when ready.`);
        } else {
          alert('✨ AI lookup complete! Form fields have been updated. Review and save when ready.');
        }
      }
    } catch (error) {
      console.error('AI Lookup Error:', error);
      alert(`AI lookup failed: ${error.message}`);
    } finally {
      setAiLookupLoading(false);
    }
  };

  const handleFindMorePhotos = async () => {
    const botanicalName = formData.botanical_name || plant?.botanical_name;
    const commonName = formData.common_name || plant?.common_name;
    
    // Validate that we have at least a botanical name or common name
    const hasBotanicalName = botanicalName && botanicalName.trim() !== '';
    const hasCommonName = commonName && commonName.trim() !== '';
    
    if (!hasBotanicalName && !hasCommonName) {
      // If no name available, open Google Images directly
      const searchTerm = botanicalName || commonName || 'plant';
      const googleUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchTerm)}`;
      window.open(googleUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    setFindingPhotos(true);
    setFoundPhotos([]);
    setSelectedFoundPhotos([]);
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const token = localStorage.getItem('authToken');
      
      // Build request body - use botanicalName if available, otherwise use commonName as query
      const requestBody = {
        commonName: commonName || null,
        verifiedOnly: true // Search for verified photos only, like AI Lookup
      };
      
      if (hasBotanicalName) {
        requestBody.botanicalName = botanicalName.trim();
        requestBody.query = botanicalName.trim(); // Also send for legacy fallback
      } else {
        requestBody.query = (commonName || '').trim();
      }
      
      const response = await fetch(`${API_BASE_URL}/plants/find-photos`, {
            method: 'POST',
            headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.photos && data.photos.length > 0) {
          // Filter to only verified images
          const verifiedPhotos = data.photos.filter(p => 
            p.url && 
            p.url !== 'null' && 
            !p.url.includes('placeholder') &&
            (p.verified || p.source === 'wikimedia' || p.source === 'inaturalist' || p.source === 'kew' || p.source === 'gbif' || p.source === 'plantnet')
          );
          
          if (verifiedPhotos.length > 0) {
            setFoundPhotos(verifiedPhotos);
          setShowPhotoSelectionModal(true);
        } else {
            // No verified photos found - open Google Images
            const searchTerm = botanicalName || commonName || '';
            const googleUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchTerm)}`;
            window.open(googleUrl, '_blank', 'noopener,noreferrer');
          }
        } else {
          // No photos found - open Google Images
          const searchTerm = botanicalName || commonName || '';
          const googleUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchTerm)}`;
          window.open(googleUrl, '_blank', 'noopener,noreferrer');
        }
      } else {
        // On error, open Google Images as fallback
        const searchTerm = botanicalName || commonName || '';
        if (searchTerm) {
          const googleUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchTerm)}`;
          window.open(googleUrl, '_blank', 'noopener,noreferrer');
          } else {
        alert('Photo search unavailable. You can manually upload images.');
        }
      }
    } catch (err) {
      console.error('Photo search error:', err);
      // On error, open Google Images as fallback
      const searchTerm = botanicalName || commonName || '';
      if (searchTerm) {
        const googleUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchTerm)}`;
        window.open(googleUrl, '_blank', 'noopener,noreferrer');
      } else {
      alert('Photo search failed. You can manually upload images.');
      }
    } finally {
      setFindingPhotos(false);
    }
  };

  const handleAddSelectedPhotos = () => {
    if (selectedFoundPhotos.length === 0) {
      alert('Please select at least one photo to add');
      return;
    }

    // Add selected photos to uploadedImages with attribution when available
    const newImages = selectedFoundPhotos.map(photoUrl => {
      const fullPhoto = foundPhotos.find(p => p.url === photoUrl);
      return {
        url: photoUrl,
        photographer: fullPhoto?.attribution || fullPhoto?.photographer || undefined,
        isPrimary: false
      };
    });

    setUploadedImages(prev => [...prev, ...newImages]);
    setShowPhotoSelectionModal(false);
    setFoundPhotos([]);
    setSelectedFoundPhotos([]);
    alert(`${selectedFoundPhotos.length} photo(s) added! Select which one should be primary before saving.`);
  };

  const handleAddByUrl = () => {
    const url = addByUrlValue.trim();
    if (!url) {
      alert('Please enter an image URL.');
      return;
    }
    try {
      new URL(url);
    } catch {
      alert('Please enter a valid URL.');
      return;
    }
    const isFirst = uploadedImages.length === 0;
    const newImg = { url, isPrimary: isFirst };
    if (addByUrlAttribution.trim()) newImg.photographer = addByUrlAttribution.trim();
    setUploadedImages(prev => [...prev, newImg]);
    if (isFirst) setPrimaryImageIndex(0);
    setAddByUrlValue('');
    setAddByUrlAttribution('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Wait for any pending uploads to complete
      if (uploadingImages.length > 0) {
        alert('Please wait for all images to finish uploading before saving.');
          setSaving(false);
          return;
        }

      // Get primary image and additional images from uploadedImages
      const primaryImage = uploadedImages.find(img => img.isPrimary);
      const finalImageUrl = primaryImage?.url || '';
      const additionalImagesForPayload = uploadedImages
        .filter(img => !img.isPrimary)
        .map(img => img.photographer ? { url: img.url, photographer: img.photographer } : img.url);

      // Set attribution if sharing to community and user wants it
      let photoAttribution;
      let photoSource;
      if (formData.is_public && includeAttribution && attributionName) {
        photoAttribution = `Photo by ${attributionName}`;
        photoSource = 'designer_contributed';
      }

      const plantPayload = {
        common_name: formData.common_name,
        botanical_name: formData.botanical_name,
        category: formData.category,
        size: formData.size,
        growth_rate: formData.growth_rate,
        sun_requirements: formData.sun_requirements,
        water_requirements: formData.water_requirements,
        hardiness_zones: formData.hardiness_zones,
        description: formData.description,
        image_url: finalImageUrl,
        // Do not change public/private visibility in edit mode
        is_public: plant ? undefined : formData.is_public,
        photo_attribution: photoAttribution,
        photo_source: photoSource,
        created_by_designer_id: currentUser?.id || null,
        plant_data: {
          // Never show/edit price for Public Library plants
          ...(formData.is_public ? {} : { price: formData.price && formData.price.trim() ? parseFloat(formData.price) : null }),
          spacing: formData.spacing && formData.spacing.trim() ? formData.spacing : null,
          notes: formData.notes && formData.notes.trim() ? formData.notes : null
        },
        additional_images: additionalImagesForPayload,
        // Needed so deletions in the edit UI persist (backend otherwise merges arrays)
        replace_additional_images: !!plant
      };

      console.log('Saving plant:', plantPayload);

      if (plant) {
        await plantsAPI.update(plant.id, plantPayload);
        alert('Plant updated successfully!');
      } else {
        const result = await plantsAPI.create(plantPayload);
        console.log('Plant created:', result);
        alert('Plant added successfully!');
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving plant:', error);
      const errorMessage = error.message || 'Unknown error';
      
      // Check for authentication errors
      if (errorMessage.toLowerCase().includes('token') || 
          errorMessage.toLowerCase().includes('authentication') ||
          errorMessage.toLowerCase().includes('unauthorized') ||
          errorMessage.toLowerCase().includes('invalid token')) {
        alert('Please sign in to save plant information. Your session may have expired.');
      } else {
        alert(`Error saving plant: ${errorMessage}`);
      }
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{plant ? 'Edit Plant' : 'Add New Plant'}</h2>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
              ×
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Common Name *</label>
              <input
                type="text"
                required
                value={formData.common_name}
                onChange={(e) => setFormData({ ...formData, common_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Botanical Name *</label>
              <input
                type="text"
                required
                value={formData.botanical_name}
                onChange={(e) => setFormData({ ...formData, botanical_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
                <span className="text-xs text-gray-500 ml-2">(used for database filtering, not AI search)</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option>Annuals</option>
                <option>Bulbs</option>
                <option>Ferns</option>
                <option>Grasses</option>
                <option>Ground Cover</option>
                <option>Perennials</option>
                <option>Shrubs</option>
                <option>Trees</option>
                <option>Vines</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
              <input
                type="text"
                value={formData.size}
                onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                placeholder="e.g. 3-5 ft"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Growth Rate</label>
              <select
                value={formData.growth_rate}
                onChange={(e) => setFormData({ ...formData, growth_rate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option>Slow</option>
                <option>Medium</option>
                <option>Fast</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sun Requirements</label>
              <select
                value={formData.sun_requirements}
                onChange={(e) => setFormData({ ...formData, sun_requirements: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option>Full Sun</option>
                <option>Partial Shade</option>
                <option>Full Shade</option>
                <option>Full Sun to Partial Shade</option>
                <option>Shade to Partial Shade</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Water Needs</label>
              <select
                value={formData.water_requirements}
                onChange={(e) => setFormData({ ...formData, water_requirements: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option>Low</option>
                <option>Moderate</option>
                <option>High</option>
                <option>Low to Moderate</option>
                <option>Moderate to High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hardiness Zones</label>
              <input
                type="text"
                value={formData.hardiness_zones}
                onChange={(e) => setFormData({ ...formData, hardiness_zones: e.target.value })}
                placeholder="e.g. 5-9"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          {/* Simple Image Upload Section */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Plant Images
                <span className="text-xs text-gray-500 ml-2">(upload one or more images, select which is primary)</span>
              </label>
              {/* Lookup More Images Button */}
                <button
                  type="button"
                onClick={handleFindMorePhotos}
                disabled={findingPhotos || !formData.botanical_name && !formData.common_name && !plant?.botanical_name && !plant?.common_name}
                className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm flex items-center gap-2"
                title="View images from trusted sources (Wikimedia, iNaturalist, etc.)"
              >
                {findingPhotos ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Searching...
                  </>
                ) : (
                    <>
                    <Search className="w-4 h-4" />
                    View Images
                  </>
                )}
                </button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Searches Wikimedia, iNaturalist & verified botanical sources first. If none found, Google Images opens automatically.
            </p>

            {/* Add by URL */}
            <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">Add by URL (optional attribution)</p>
              <div className="flex flex-wrap gap-2">
                <input
                  type="url"
                  value={addByUrlValue}
                  onChange={(e) => setAddByUrlValue(e.target.value)}
                  placeholder="https://.../image.jpg"
                  className="flex-1 min-w-[180px] px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <input
                  type="text"
                  value={addByUrlAttribution}
                  onChange={(e) => setAddByUrlAttribution(e.target.value)}
                  placeholder="Photographer (optional)"
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <button
                  type="button"
                  onClick={handleAddByUrl}
                  className="px-4 py-2 bg-white border-2 border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50 text-sm font-medium"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Upload input */}
            <div className="mb-3">
                <input
                type="file"
                accept="image/*,image/heic,image/heif,.heic,.heif"
                multiple
                onChange={handleImageFilesChange}
                disabled={uploading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Upload one or more photos (JPG, PNG, GIF, WebP - max 5MB each)
              </p>
              {uploading && (
                <p className="text-xs text-blue-600 mt-1">📤 Uploading images...</p>
              )}
          </div>

            {/* Display uploaded images */}
            {uploadedImages.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {uploadedImages.map((img, index) => (
                  <div key={index} className="relative group border-2 rounded-lg overflow-hidden" style={{
                    borderColor: img.isPrimary ? '#10b981' : '#e5e7eb'
                  }}>
                    <img
                      src={img.url}
                      alt={`Plant image ${index + 1}`}
                      className="w-full h-32 object-cover"
                    onError={(e) => {
                        e.target.src = 'https://placehold.co/200x150/e5e7eb/6b7280?text=Image+Error';
                      }}
                    />
                    {img.isPrimary && (
                      <div className="absolute top-2 left-2 bg-green-600 text-white px-2 py-1 rounded text-xs font-medium">
                    Primary
                </div>
                    )}
                    {img.photographer && (
                      <div className="absolute bottom-1 left-1 right-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded truncate">
                        {img.photographer}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center gap-2">
                      {!img.isPrimary && (
                      <button
                        type="button"
                          onClick={() => setImageAsPrimary(index)}
                          className="opacity-0 group-hover:opacity-100 px-3 py-1 bg-green-600 text-white rounded text-xs font-medium transition-opacity"
                        title="Set as primary image"
                      >
                          Set Primary
                      </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="opacity-0 group-hover:opacity-100 bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm transition-opacity"
                        title="Remove image"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Creator Attribution - Only show if sharing to community */}
          {formData.is_public && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-3">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
                <span className="text-sm font-medium text-blue-900">Plant Attribution</span>
          </div>

              <div className="mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeAttribution}
                    onChange={(e) => setIncludeAttribution(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Include my name/handle as attribution</span>
                </label>
                <p className="text-xs text-gray-500 ml-6 mt-1">
                  If checked, your name or handle will be shown as the plant creator in the Public Library
                </p>
              </div>

              {includeAttribution && (
              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Attribution Name or Handle
                    <span className="text-xs text-gray-500 ml-2">(e.g. "Jane Designer" or "@jane_designer")</span>
                  </label>
                  <input
                    type="text"
                    value={attributionName}
                    onChange={(e) => setAttributionName(e.target.value)}
                    placeholder="Your name or social media handle"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This will be displayed as: "Photo by {attributionName || 'you'}"
                  </p>
                </div>
              )}
              
              {!includeAttribution && (
                <p className="text-xs text-blue-600">
                  This plant will be shared anonymously in the Public Library
                </p>
                )}
              </div>
            )}


          {!formData.is_public ? (
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Spacing</label>
                <input
                  type="text"
                  value={formData.spacing}
                  onChange={(e) => setFormData({ ...formData, spacing: e.target.value })}
                  placeholder="e.g. 12-18 in"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          ) : (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Spacing</label>
              <input
                type="text"
                value={formData.spacing}
                onChange={(e) => setFormData({ ...formData, spacing: e.target.value })}
                placeholder="e.g. 12-18 in"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          {/* Share with Community Section - Only for creating new plants (not edit mode) */}
          {!plant && (
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
              <div className="mb-3">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <span>Share with Community</span>
                  <span className="text-xs text-gray-500">Choose whether to share this plant with the Public Library or keep it in your Private Collection only</span>
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, is_public: true })}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      formData.is_public
                        ? 'bg-green-600 text-white shadow-md'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                    title="Share with Public Library - visible to everyone"
                  >
                    🌍 Share to Public Library
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, is_public: false })}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      !formData.is_public
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                    title="Keep in Private Collection only - visible only to you"
                  >
                    ✨ Private Collection Only
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleAILookup}
              disabled={aiLookupLoading}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-2 px-4 rounded-lg hover:from-emerald-600 hover:to-teal-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {aiLookupLoading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Looking up...
                </>
              ) : (
                <>
                  ✨ AI Lookup {plant ? 'More Info' : ''}
                </>
              )}
            </button>
            <button
              type="submit"
              disabled={saving || uploading}
              className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {uploading ? '📤 Uploading...' : saving ? 'Saving...' : plant ? 'Update Plant' : 'Add Plant'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Photo Selection Modal */}
        {showPhotoSelectionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="mb-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">Select Photos to Add</h3>
                      {(formData.botanical_name || formData.common_name || plant?.botanical_name || plant?.common_name) && (
                        <div className="space-y-1">
                          <div className="text-sm font-medium text-gray-800">
                            <span className="text-gray-600">Plant:</span> {formData.botanical_name || formData.common_name || plant?.botanical_name || plant?.common_name || 'Unknown'}
                          </div>
                          {formData.common_name && formData.common_name !== formData.botanical_name && (
                            <div className="text-xs text-gray-600 italic">
                              {formData.common_name}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setShowPhotoSelectionModal(false);
                        setFoundPhotos([]);
                        setSelectedFoundPhotos([]);
                      }}
                      className="text-gray-400 hover:text-gray-600 text-2xl flex-shrink-0"
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Select one or more photos to add to your plant. You can choose which one is primary after adding them.
                  </p>
                </div>

                {foundPhotos.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    {foundPhotos.map((photo, index) => (
                      <div
                        key={index}
                        className={`relative border-2 rounded-lg overflow-hidden cursor-pointer transition-all ${
                          selectedFoundPhotos.includes(photo.url)
                            ? 'border-purple-600 ring-2 ring-purple-300'
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                        onClick={() => {
                          if (selectedFoundPhotos.includes(photo.url)) {
                            setSelectedFoundPhotos(prev => prev.filter(url => url !== photo.url));
                          } else {
                            setSelectedFoundPhotos(prev => [...prev, photo.url]);
                          }
                        }}
                      >
                        <img
                          src={photo.url}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-40 object-cover"
                          onError={(e) => {
                            e.target.src = 'https://placehold.co/200x150/e5e7eb/6b7280?text=Image+Error';
                          }}
                        />
                        {selectedFoundPhotos.includes(photo.url) && (
                          <div className="absolute top-2 right-2 bg-purple-600 text-white rounded-full p-1">
                            <CheckCircle className="w-5 h-5" />
                          </div>
                        )}
                        {/* Attribution Overlay - Show plant name prominently, especially on mobile */}
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white p-2 text-xs">
                          {/* Show actual plant name from form/plant if available */}
                          {(formData.botanical_name || formData.common_name || plant?.botanical_name || plant?.common_name) ? (
                            <div className="font-semibold mb-1">
                              Plant: <span className="font-normal italic">{formData.botanical_name || formData.common_name || plant?.botanical_name || plant?.common_name}</span>
                              {formData.common_name && formData.common_name !== formData.botanical_name && (
                                <span className="font-normal text-gray-300 ml-1">({formData.common_name})</span>
                              )}
                            </div>
                          ) : null}
                          {photo.attribution && (
                            <div className="opacity-90 truncate" title={photo.attribution.replace(/<[^>]*>/g, '').trim()}>
                              {photo.attribution.replace(/<[^>]*>/g, '').trim()}
                            </div>
                          )}
                          {!photo.attribution && photo.photographer && (
                            <div className="opacity-90">
                              Photo by {photo.photographer.replace(/<[^>]*>/g, '').trim()}
                            </div>
                          )}
                          {photo.verified && (
                            <div className="flex items-center gap-1 mt-1">
                              <CheckCircle className="w-3 h-3 text-green-400" />
                              <span className="text-green-400">Verified</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No photos found</p>
                )}

                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={handleAddSelectedPhotos}
                    disabled={selectedFoundPhotos.length === 0}
                    className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Add {selectedFoundPhotos.length > 0 ? `${selectedFoundPhotos.length} ` : ''}Selected Photo{selectedFoundPhotos.length !== 1 ? 's' : ''}
                  </button>
                  <button
                    onClick={() => {
                      setShowPhotoSelectionModal(false);
                      setFoundPhotos([]);
                      setSelectedFoundPhotos([]);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Safety: Track render count to detect infinite loops
let renderCount = 0;
const MAX_RENDERS = 100; // Increased threshold
let lastResetTime = Date.now();

export default function Plants() {
  // ALL HOOKS MUST BE CALLED FIRST - before any early returns
  const navigate = useNavigate();
  const location = useLocation();
  const [plants, setPlants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false); // Start false, will trigger after mount
  
  // Initialize searchTerm from URL parameter if present
  const searchParams = new URLSearchParams(location.search);
  const initialSearchTerm = searchParams.get('search') || '';
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);

  // Handle OAuth callback success/error messages
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    
    if (success === 'facebook_connected') {
      alert('Facebook account connected successfully! You can now post to your business page.');
      // Clean URL
      navigate(location.pathname, { replace: true });
    }
    
    if (error) {
      const errorMessages = {
        'facebook_auth_failed': 'Facebook authentication failed. Please try again.',
        'facebook_token_error': 'Failed to get Facebook access token.',
        'no_pages_found': 'No Facebook Pages found. Please make sure you have a Facebook Business Page.',
        'facebook_callback_error': 'An error occurred during Facebook connection.'
      };
      alert(errorMessages[error] || 'An error occurred connecting your account.');
      // Clean URL
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, navigate, location.pathname]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [ownershipFilter, setOwnershipFilter] = useState('all'); // 'all', 'public', or 'mine'
  
  // Structured field filters
  const [heightMin, setHeightMin] = useState('');
  const [heightMax, setHeightMax] = useState('');
  const [widthMin, setWidthMin] = useState('');
  const [widthMax, setWidthMax] = useState('');
  const [zoneMin, setZoneMin] = useState('');
  const [zoneMax, setZoneMax] = useState('');
  const [lightStructured, setLightStructured] = useState('');
  const [waterStructured, setWaterStructured] = useState('');
  
  const [showFilters, setShowFilters] = useState(false); // Collapsible filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false); // Advanced structured filters
  
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingPlant, setEditingPlant] = useState(null);
  
  // AI Search state (inline results; modal removed)
  const [aiSearchQuery, setAiSearchQuery] = useState('');
  const [aiSearchResults, setAiSearchResults] = useState([]);
  const [aiSearchLoading, setAiSearchLoading] = useState(false);
  const [aiSearchError, setAiSearchError] = useState(null);
  // Retained no-op setter: the AI search modal was removed but two call sites
  // below still reference setShowAISearchModal. Keep the identifier defined to
  // avoid a reference error without resurrecting the modal.
  // eslint-disable-next-line no-unused-vars
  const [, setShowAISearchModal] = useState(false);
  
  // Photo gallery state for AI Search
  const [showAISearchPhotoGallery, setShowAISearchPhotoGallery] = useState(false);
  const [aiSearchGalleryPhotos, setAiSearchGalleryPhotos] = useState([]);
  // Plant identification (PlantNet) modal
  const [showIdentifyPlantModal, setShowIdentifyPlantModal] = useState(false);
  const [identifyFile, setIdentifyFile] = useState(null);
  const [identifyOrgan, setIdentifyOrgan] = useState('leaf');
  const [identifyUploading, setIdentifyUploading] = useState(false);
  const [identifyLoading, setIdentifyLoading] = useState(false);
  const [identifyError, setIdentifyError] = useState(null);
  const [identifyResult, setIdentifyResult] = useState(null);
  const [isMobileIdentifyUI, setIsMobileIdentifyUI] = useState(false);

  // Open Identify Plant modal when linked from homepage or nav (?identify=1), then strip query
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('identify') !== '1') return;
    setShowIdentifyPlantModal(true);
    setIdentifyError(null);
    setIdentifyResult(null);
    setIdentifyFile(null);
    params.delete('identify');
    const qs = params.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  // After sign-in from Identify flow, open modal on plant library
  useEffect(() => {
    if (!localStorage.getItem('authToken')) return;
    if (sessionStorage.getItem('plantaday_openIdentify') !== '1') return;
    sessionStorage.removeItem('plantaday_openIdentify');
    setShowIdentifyPlantModal(true);
    setIdentifyError(null);
    setIdentifyResult(null);
    setIdentifyFile(null);
  }, []);

  // Save success state
  const [savingPlantId, setSavingPlantId] = useState(null);
  const [savedPlantId, setSavedPlantId] = useState(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showSearchTooltip, setShowSearchTooltip] = useState(false);
  const searchTooltipRef = useRef(null);
  // Preserve list scroll position when editing so user returns to same spot after save
  const listScrollYRef = useRef(null);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchTooltipRef.current && !searchTooltipRef.current.contains(event.target)) {
        setShowSearchTooltip(false);
      }
    };

    if (showSearchTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSearchTooltip]);

  // Basic mobile detection (used only for messaging/copy)
  useEffect(() => {
    const compute = () => {
      try {
        setIsMobileIdentifyUI(window.matchMedia('(max-width: 640px)').matches);
      } catch {
        setIsMobileIdentifyUI(false);
      }
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  // Get current user from localStorage - check on every render to catch login changes
  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem('userData') || localStorage.getItem('user');
      return userData ? JSON.parse(userData) : {};
    } catch (e) {
      return {};
    }
  };
  
  // Get current user ID - check localStorage directly to ensure it's up to date
  const currentUserId = getCurrentUser()?.id;
  const currentUser = getCurrentUser();
  
  // Reset ownership filter to 'all' if user is not logged in and filter is set to 'mine'
  useEffect(() => {
    if (!currentUserId && ownershipFilter === 'mine') {
      setOwnershipFilter('all');
    }
  }, [currentUserId, ownershipFilter]);
  
  const isDesigner = currentUser?.role === 'designer' || currentUser?.verificationStatus === 'verified' || currentUser?.verificationStatus === 'approved';
  
  // Check if user is admin (johnconnors1690) - case insensitive
  const isAdminUser = currentUser?.email?.toLowerCase() === 'johnconnors1690@gmail.com';
  
  // View mode switcher for admin: 'admin' (owner view) or 'customer' (regular user view)
  const [viewMode, setViewMode] = useState(() => {
    // Only load view mode if user is actually logged in as admin
    if (!currentUser?.id) return 'customer';
    const saved = localStorage.getItem('plantaday_viewMode');
    // For admin users, default to 'admin' unless explicitly saved as 'customer'
    // This ensures admin mode is the default while keeping the toggle functional
    if (isAdminUser) {
      return saved === 'customer' ? 'customer' : 'admin';
    }
    return saved || 'customer';
  });
  
  // Update viewMode in localStorage when it changes (only for logged-in admin users)
  // TEMPORARILY DISABLED to debug infinite loop - will save on explicit viewMode change instead
  // React.useEffect(() => {
  //   if (isAdminUser && currentUserId) {
  //     localStorage.setItem('plantaday_viewMode', viewMode);
  //   }
  // }, [viewMode, isAdminUser, currentUserId]);
  
  // View mode logic:
  // - Admin View: Show ALL designer features (as if they're a designer)
  // - Customer View: Show NO designer features (as if they're a regular customer browsing)
  const effectiveIsDesigner = viewMode === 'admin' && isAdminUser ? true : (viewMode === 'customer' ? false : isDesigner);
  const showAdminFeatures = isAdminUser && viewMode === 'admin';

  const [showAILookupModal, setShowAILookupModal] = useState(false);
  const [aiLookupPlant, setAiLookupPlant] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [plantToShare, setPlantToShare] = useState(null);
  const [showEditRequestModal, setShowEditRequestModal] = useState(false);
  const [existingPublicPlant, setExistingPublicPlant] = useState(null);
  const [shareAttribution, setShareAttribution] = useState('');
  const [sharePhotoSource, setSharePhotoSource] = useState('designer_contributed');
  const [likedPlants, setLikedPlants] = useState(new Set()); // Track liked plants
  const [userPrivatePlants, setUserPrivatePlants] = useState(new Map()); // Track user's private plants with metadata

  // Helper to check if a plant is from public library (was added to collection from public library)
  const isFromPublicLibrary = (plant) => {
    if (!plant) return false;
    // Check if photo_source indicates it's from public library
    if (plant.photo_source === 'public_library') return true;
    // Check if it's a private copy but has public library attribution
    if (!plant.is_public && plant.photo_attribution && plant.photo_attribution.includes('Public Library')) return true;
    return false;
  };

  // Helper to check if plant is AI generated
  const isAIGenerated = (plant) => {
    if (!plant) return false;
    return plant.photo_source === 'ai_generated' || plant.plant_data?.ai_generated === true;
  };

  const handleAILookup = (plant) => {
    setAiLookupPlant(plant);
    setShowAILookupModal(true);
  };

  // Remove plant from private collection
  const handleRemoveFromCollection = async (plant) => {
    if (!currentUserId) return;
    
    if (!window.confirm(`Remove "${plant.common_name}" from your Private Collection?`)) {
      return;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/plants/${plant.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setLikedPlants(prev => {
          const newSet = new Set(prev);
          newSet.delete(plant.id);
          return newSet;
        });
        setUserPrivatePlants(prev => {
          const newMap = new Map(prev);
          newMap.delete(plant.id);
          return newMap;
        });
        alert(`"${plant.common_name}" removed from your Private Collection!`);
        loadPlants(); // Refresh to show updated counts
      } else {
        const errorData = await response.json().catch(() => ({ error: `Server error: ${response.status}` }));
        throw new Error(errorData.error || errorData.message || 'Failed to remove plant');
      }
    } catch (error) {
      console.error('Error removing plant from collection:', error);
      alert(`Error removing plant: ${error.message || 'Unknown error'}`);
    }
  };

  const handleLikePlant = async (plant) => {
    // Check if this is a remove action (plant is already in collection and from public library)
    const plantInfo = userPrivatePlants.get(plant.id);
    if (plantInfo?.fromPublicLibrary && likedPlants.has(plant.id)) {
      // Find the private copy to remove
      const privateCopy = plants.find(p => !p.is_public && p.created_by_designer_id === currentUserId && 
        (p.botanical_name === plant.botanical_name || p.id === plant.id));
      if (privateCopy) {
        await handleRemoveFromCollection(privateCopy);
        return;
      }
    }
    
    // For AI search results, save directly to collection (same as public library plants)
    // Don't open AILookupModal - user can use AI Lookup button separately if needed
    
    // In customer view mode, anyone can save to collection (they just need to be logged in)
    // In designer/admin view, need to be authenticated
    if (!currentUserId) {
      alert('Please sign in to save plants to your collection');
      navigate('/login');
      return;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const token = localStorage.getItem('authToken');
      if (!token || !token.trim()) {
        alert('Please sign in to save plants to your collection.');
        navigate('/login');
        return;
      }

      // For AI search results, use the AI plant data directly
      const aiPlantData = plant.aiPlantData || (plant.isAISuggestion ? plant : null);
      
      // Ensure we have at least a common_name before proceeding
      const finalCommonName = aiPlantData?.common_name || plant?.common_name || '';
      if (!finalCommonName || finalCommonName.trim() === '') {
        alert('Error: Plant name is missing. Cannot save to collection.');
        return;
      }
      
      // Create a copy of the plant in user's private collection
      // Note: Server POST endpoint expects plant_data as separate fields (price, spacing, notes)
      const plantCopy = aiPlantData ? {
        common_name: finalCommonName.trim(),
        botanical_name: (aiPlantData.botanical_name || plant.botanical_name || '').trim() || null,
        category: aiPlantData.category || plant.category || 'Perennial',
        size: aiPlantData.mature_size || plant.size || '',
        growth_rate: plant.growth_rate || 'Medium',
        sun_requirements: aiPlantData.light_requirements || plant.sun_requirements || 'Full Sun',
        water_requirements: aiPlantData.water_requirements || plant.water_requirements || 'Moderate',
        hardiness_zones: aiPlantData.hardiness_zones || plant.hardiness_zones || '',
        description: aiPlantData.why_recommended || aiPlantData.care_notes || aiPlantData.growing_notes || plant.description || '',
        image_url: aiPlantData.image_url || plant.image_url || null,
        is_public: false,
        photo_attribution: aiPlantData.photo_attribution || plant.photo_attribution || null,
        photo_source: aiPlantData.photo_source || plant.photo_source || 'ai_generated',
        image_verified: aiPlantData.image_verified || plant.image_verified || false,
        created_by_designer_id: currentUserId,
        // Server builds plant_data from these fields
        price: null,
        spacing: null,
        notes: (aiPlantData.care_notes || aiPlantData.growing_notes || '').trim() || null
      } : {
        common_name: (plant.common_name || '').trim(),
        botanical_name: (plant.botanical_name || '').trim() || null,
        category: plant.category || 'Perennial',
        size: plant.size || '',
        growth_rate: plant.growth_rate || 'Medium',
        sun_requirements: plant.sun_requirements || 'Full Sun',
        water_requirements: plant.water_requirements || 'Moderate',
        hardiness_zones: plant.hardiness_zones || '',
        description: plant.description || '',
        image_url: plant.image_url || null,
        photo_attribution: plant.photo_attribution || 'From Public Library',
        photo_source: plant.photo_source || 'public_library',
        is_public: false, // Private collection
        created_by_designer_id: currentUserId,
        // Server builds plant_data from these fields
        price: (plant.plant_data && plant.plant_data.typical_price) || null,
        spacing: null,
        notes: ((plant.plant_data && plant.plant_data.care_notes) || (plant.plant_data && plant.plant_data.growing_notes) || '').trim() || null
      };

      const response = await fetch(`${API_BASE_URL}/plants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(plantCopy)
      });

      if (response.ok) {
        const data = await response.json();
        const savedPlant = data.plant || data; // Handle both response formats
        
        // Ensure saved plant has correct ownership info
        if (savedPlant && !savedPlant.created_by_designer_id) {
          savedPlant.created_by_designer_id = currentUserId;
        }
        savedPlant.is_public = false; // Ensure it's marked as private
        
        setLikedPlants(prev => new Set([...prev, plant.id]));
        
        // Show success message with option to view
        const shouldView = window.confirm(`"${plant.common_name}" saved to your Private Collection!\n\nWould you like to view it now?`);
        
        if (shouldView) {
          // Close any open modals first
          setShowAISearchModal(false);
          setShowDetailModal(false);
          
          // Switch to private collection view
          setOwnershipFilter('mine');
          ownershipFilterRef.current = 'mine';
          
          // Wait a bit for filter to update, then load plants and open the saved plant
          setTimeout(() => {
            loadPlants().then(() => {
              // Set the saved plant with correct ownership info and open modal
              if (savedPlant && savedPlant.id) {
                setSelectedPlant({
                  ...savedPlant,
                  created_by_designer_id: currentUserId,
                  is_public: false
                });
                setShowDetailModal(true);
              }
            });
          }, 100);
        } else {
          loadPlants(); // Just refresh the list
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: `Server error: ${response.status}` }));
        console.error('Save plant error response:', errorData);
        
        if (errorData.error && (errorData.error.includes('already exists') || errorData.error.includes('already in'))) {
          // Plant already in collection
          setLikedPlants(prev => new Set([...prev, plant.id]));
          alert(`"${plant.common_name}" is already in your Private Collection!`);
        } else {
          if (response.status === 403 && (errorData.error || '').toLowerCase().includes('invalid token')) {
            alert('Your session may have expired. Please sign in again to add plants to your collection.');
            navigate('/login');
            return;
          }
          throw new Error(errorData.error || errorData.message || `Failed to save plant (${response.status})`);
        }
      }
    } catch (error) {
      console.error('Error saving plant to collection:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
      if (error.message && error.message.toLowerCase().includes('invalid token')) {
        alert('Your session may have expired. Please sign in again to add plants to your collection.');
        navigate('/login');
        return;
      }
      alert(`Error saving plant: ${error.message || 'Unknown error'}. Please try again or contact support.`);
    }
  };

  // DISABLED: URL parameter sync - was causing re-render loops
  // If URL search param is present, initialize searchTerm once on mount only
  // useEffect(() => {
  //   const searchParam = searchParams.get('search');
  //   if (searchParam && searchParam !== searchTerm) {
  //     setSearchTerm(searchParam);
  //   }
  // }, [location.search]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(20);
  const [totalPlants, setTotalPlants] = useState(0);
  
  // Auto AI Search state - for automatic smart search in loadPlants (different from modal AI search)
  const [autoAiSearchResults, setAutoAiSearchResults] = useState(null);
  const [autoAiSearchLoading, setAutoAiSearchLoading] = useState(false);
  const [autoAiSearchError, setAutoAiSearchError] = useState(null);
  
  // Refs to prevent infinite loops
  const isLoadingRef = React.useRef(false);
  const lastLoadKeyRef = React.useRef('');
  
  // Use refs to capture current values without causing useCallback to recreate the function
  const searchTermRef = React.useRef(searchTerm);
  const selectedCategoryRef = React.useRef(selectedCategory);
  const selectedColorRef = React.useRef(selectedColor);
  const ownershipFilterRef = React.useRef(ownershipFilter);
  const currentPageRef = React.useRef(currentPage);
  const resultsPerPageRef = React.useRef(resultsPerPage);
  
  // Structured field filter refs
  const heightMinRef = React.useRef(heightMin);
  const heightMaxRef = React.useRef(heightMax);
  const widthMinRef = React.useRef(widthMin);
  const widthMaxRef = React.useRef(widthMax);
  const zoneMinRef = React.useRef(zoneMin);
  const zoneMaxRef = React.useRef(zoneMax);
  const lightStructuredRef = React.useRef(lightStructured);
  const waterStructuredRef = React.useRef(waterStructured);
  
  // Keep refs in sync
  React.useEffect(() => {
    searchTermRef.current = searchTerm;
    selectedCategoryRef.current = selectedCategory;
    selectedColorRef.current = selectedColor;
    ownershipFilterRef.current = ownershipFilter;
    currentPageRef.current = currentPage;
    resultsPerPageRef.current = resultsPerPage;
    heightMinRef.current = heightMin;
    heightMaxRef.current = heightMax;
    widthMinRef.current = widthMin;
    widthMaxRef.current = widthMax;
    zoneMinRef.current = zoneMin;
    zoneMaxRef.current = zoneMax;
    lightStructuredRef.current = lightStructured;
    waterStructuredRef.current = waterStructured;
  }, [searchTerm, selectedCategory, selectedColor, ownershipFilter, currentPage, resultsPerPage, heightMin, heightMax, widthMin, widthMax, zoneMin, zoneMax, lightStructured, waterStructured]);
  
  const loadPlants = React.useCallback(async () => {
    // Prevent multiple simultaneous loads
    if (isLoadingRef.current) {
      // Already loading, skip
      return;
    }
    
    
    try {
      isLoadingRef.current = true;
      setLoading(true);
      
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      const token = localStorage.getItem('authToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Public Library: keyword search only (no auto natural-language interpretation)
      // Other filters: may use natural-language interpretation to map descriptive queries to structured filters
      // AI Search button: explicit AI search with external discovery/verified photo lookups
      const searchTerm = searchTermRef.current || '';
      const currentFilter = ownershipFilterRef.current;
      
      // Debug log
      if (searchTerm) {
        console.log(`🔍 Search filter check: currentFilter="${currentFilter}", searchTerm="${searchTerm}"`);
      }
      
      // Use natural-language (AI) search for ALL text queries - ensures semantic understanding.
      // "rose" = true roses (Rosa), not rosemary; "yellow roses" = yellow AND roses; no 17-of-4 count bugs.
      const useNaturalLanguage = !!(searchTerm && searchTerm.trim());
      
      let plantsArray = [];
      let totalCount = 0;
      
      // Use natural language search if query looks like natural language
      console.log(`🔍 Natural language check: useNaturalLanguage=${useNaturalLanguage}, currentFilter="${currentFilter}", searchTerm="${searchTerm}"`);
      if (useNaturalLanguage && searchTerm.trim()) {
        try {
          console.log('🔍 Using natural language search for:', searchTerm);
          // Set ownership filter based on current filter
          const requestBody = { query: searchTerm };
          if (currentFilter === 'public') {
            requestBody.is_public = true;
          } else if (currentFilter === 'mine') {
            // For private collection, search only user's plants
            requestBody.created_by_designer_id = currentUser?.id;
            requestBody.is_public = false;
          } else if (currentFilter === 'all') {
            // For "All" filter, search both public and user's private plants
            // Don't set is_public filter - let backend search all accessible plants
            if (currentUser?.id) {
              requestBody.created_by_designer_id = currentUser.id;
            }
          }
          const response = await fetch(`${API_BASE_URL}/plants/natural-language-search`, {
            method: 'POST',
            headers,
            body: JSON.stringify(requestBody)
          });
          
          if (response.ok) {
            const data = await response.json();
            plantsArray = Array.isArray(data.plants) ? data.plants : [];
            totalCount = Math.max(plantsArray.length, data.count || 0);
            console.log(`✅ Natural language search found ${plantsArray.length} plants`);
            
            // Set plants and total, then return early
            setPlants(plantsArray);
            if (totalCount > 0) {
              setTotalPlants(totalCount);
            } else {
              setTotalPlants(plantsArray.length);
            }
            
            // Load categories if needed
            if (categories.length === 0) {
              loadCategories().catch(err => console.error('Failed to load categories:', err));
            }
            
            // DO NOT automatically trigger AI smart search - only when user clicks "AI Search Plants" button
            // Natural language search is fine (it only searches database), but smart-search does external image lookups
            // Clear any auto AI search results
            setAutoAiSearchResults(null);
            setAutoAiSearchError(null);
            setAutoAiSearchLoading(false);
            
            setLoading(false);
            isLoadingRef.current = false;
            return; // Exit early - natural language search succeeded
          } else {
            console.warn('Natural language search failed, falling back to regular search');
            useNaturalLanguage = false;
          }
        } catch (nlError) {
          console.warn('Natural language search error, using regular search:', nlError);
          useNaturalLanguage = false;
        }
      }
      
      // Use regular search if not using natural language or if natural language search failed
      if (!useNaturalLanguage) {
        const params = {};
        // Use refs to get current values without triggering useCallback recreation
        
        // For "red flowers" type queries, keep the full search term
        // The backend will search in names, and we'll also apply color filter if needed
        let cleanedSearchTerm = searchTermRef.current || '';
        
        // Send the full search term to backend (it will search in plant names)
        if (cleanedSearchTerm) params.search = cleanedSearchTerm;
        if (selectedCategoryRef.current) params.category = selectedCategoryRef.current;
        
        // Only add color filter if color was manually selected from dropdown
        // If color was auto-detected from search term, don't add it as separate filter
        // (the search term already contains the color)
        // This prevents double-filtering
        if (selectedColorRef.current) {
          const searchLower = cleanedSearchTerm.toLowerCase();
          const colorKeywords = ['red', 'pink', 'purple', 'blue', 'yellow', 'orange', 'white', 'green', 'multicolor', 'multi-color', 'multi color', 'rainbow'];
          const colorInSearch = colorKeywords.some(keyword => {
            const regex = new RegExp(`\\b${keyword}\\b`, 'i');
            return regex.test(searchLower);
          });
          
          // Only add color filter if color is NOT already in the search term
          // This way "red flowers" searches for "red flowers" in names, not "flowers" + color filter
          if (!colorInSearch) {
            params.color = selectedColorRef.current;
          }
        }
        
        // Add structured field filters
        if (heightMinRef.current) params.height_min = heightMinRef.current;
        if (heightMaxRef.current) params.height_max = heightMaxRef.current;
        if (widthMinRef.current) params.width_min = widthMinRef.current;
        if (widthMaxRef.current) params.width_max = widthMaxRef.current;
        if (zoneMinRef.current) params.zone_min = zoneMinRef.current;
        if (zoneMaxRef.current) params.zone_max = zoneMaxRef.current;
        if (lightStructuredRef.current) params.light_structured = lightStructuredRef.current;
        if (waterStructuredRef.current) params.water_structured = waterStructuredRef.current;
      // Get currentUserId - check localStorage directly to ensure it's current
      const userId = getCurrentUser()?.id;
      const currentFilter = ownershipFilterRef.current;
      
      console.log('🔍 loadPlants - Filter check:', {
        currentFilter,
        userId,
        ownershipFilterState: ownershipFilter
      });
      
      if (currentFilter === 'mine' && userId) {
        // Private Collection: Only show user's private plants
        params.created_by_designer_id = userId;
        params.is_public = false;
        console.log('✅ Setting Private Collection filter:', { 
          userId, 
          is_public: false, 
          color: params.color,
          category: params.category,
          search: params.search
        });
      } else if (currentFilter === 'public') {
        // Public Library: Only show public plants (no user filter)
        params.is_public = true;
        console.log('✅ Setting Public Library filter (public only)');
      } else if (currentFilter === 'all') {
        // All: Show both public and user's private plants (if logged in)
        // Don't set is_public filter - let backend handle showing both
        if (userId) {
          // Backend will show public + user's private plants
          console.log('✅ Setting All filter (public + user private if logged in)');
        } else {
          // Not logged in - only show public
          params.is_public = true;
          console.log('✅ Setting All filter (public only - not logged in)');
        }
      }
      
      // Calculate offset for pagination
      const offset = (currentPageRef.current - 1) * resultsPerPageRef.current;
      params.limit = resultsPerPageRef.current;
      params.offset = offset;
      
      // Load plants - works with or without authentication
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      const token = localStorage.getItem('authToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.category) queryParams.append('category', params.category);
      if (params.color) queryParams.append('color', params.color);
      if (params.created_by_designer_id) queryParams.append('created_by_designer_id', params.created_by_designer_id);
      if (params.is_public !== undefined) queryParams.append('is_public', String(params.is_public));
      // Structured field filters (height, width, zone, light, water)
      if (params.height_min) queryParams.append('height_min', params.height_min);
      if (params.height_max) queryParams.append('height_max', params.height_max);
      if (params.width_min) queryParams.append('width_min', params.width_min);
      if (params.width_max) queryParams.append('width_max', params.width_max);
      if (params.zone_min) queryParams.append('zone_min', params.zone_min);
      if (params.zone_max) queryParams.append('zone_max', params.zone_max);
      if (params.light_structured) queryParams.append('light_structured', params.light_structured);
      if (params.water_structured) queryParams.append('water_structured', params.water_structured);
      queryParams.append('limit', params.limit);
      queryParams.append('offset', params.offset);
      
      // Debug logging
      if (ownershipFilterRef.current === 'mine') {
        console.log('Private Collection Filter - Params:', {
          userId,
          created_by_designer_id: params.created_by_designer_id,
          is_public: params.is_public,
          queryString: queryParams.toString()
        });
      }
      
      const response = await fetch(`${API_BASE_URL}/plants?${queryParams.toString()}`, { headers });
      
      // Handle unauthorized gracefully - plants endpoint should work without auth
      // CRITICAL: Don't throw errors or redirect - just retry without auth silently
      if (response.status === 401 || response.status === 403) {
        console.warn('Unauthorized - retrying without auth token');
        // Retry without auth token - don't redirect, don't throw
        const publicHeaders = { 'Content-Type': 'application/json' };
        const publicResponse = await fetch(`${API_BASE_URL}/plants?${queryParams.toString()}`, { headers: publicHeaders });
        if (!publicResponse.ok) {
          // If public request also fails, just return empty array - don't throw
          console.error('Failed to load plants (public):', publicResponse.status);
          setPlants([]);
          setTotalPlants(0);
          return; // Exit early - don't throw
        }
        const publicData = await publicResponse.json();
        setPlants(Array.isArray(publicData.plants) ? publicData.plants : []);
        if (publicData.pagination && publicData.pagination.total) {
          setTotalPlants(publicData.pagination.total);
        }
        return; // Exit early - we've handled it
      }
      
      if (!response.ok) {
        // Don't throw - just set empty data and log
        console.error('Failed to load plants:', response.status, response.statusText);
        setPlants([]);
        setTotalPlants(0);
        return; // Don't throw to prevent re-render loops
      }
      const data = await response.json();
      
      const plantsArray = Array.isArray(data.plants) ? data.plants : [];
      setPlants(plantsArray);
      
      // Track which private plants are from public library
      if (ownershipFilterRef.current === 'mine' && userId) {
        // Check each private plant to see if it's from public library
        const privatePlantsMap = new Map();
        plantsArray.forEach(p => {
          if (!p.is_public && p.created_by_designer_id === userId) {
            const isFromPublic = isFromPublicLibrary(p);
            const isAI = isAIGenerated(p);
            privatePlantsMap.set(p.id, { fromPublicLibrary: isFromPublic, isAIGenerated: isAI, originalPlantId: p.id });
          }
        });
        setUserPrivatePlants(privatePlantsMap);
      }
      
      if (data.pagination && data.pagination.total) {
        setTotalPlants(data.pagination.total);
      }
      
      // Inline AI search runs in parallel when user clicks Search (unified search) - do not clear here
      } // Close if (!useNaturalLanguage) block
    } catch (error) {
      console.error('❌ Error loading plants:', error);
      setPlants([]);
      setTotalPlants(0);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, []); // NO dependencies - use refs and currentUser directly to avoid recreations
  
  // DISABLED: Filter/page change handlers - these were causing infinite loops
  // Plants will only load once on mount. User can manually refresh if needed.
  // TODO: Re-enable with proper debouncing if needed

  // Simple ref to prevent multiple loads (NO state - state changes cause re-runs!)
  const hasTriedLoadRef = React.useRef(false);
  const loadTimeoutRef = React.useRef(null);
  
  // Auto-load on mount - SIMPLE, no dependencies that change
  useEffect(() => {
    // If already loaded or loading, don't do anything
    if (hasTriedLoadRef.current || isLoadingRef.current) {
      return;
    }
    
    hasTriedLoadRef.current = true;
    
    // Clear any existing timeout
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
    }
    
    // Small delay then load
    loadTimeoutRef.current = setTimeout(() => {
      // Double-check we're not already loading
      if (!isLoadingRef.current && hasTriedLoadRef.current) {
        setLoading(true);
        loadPlants().catch(err => {
          console.error('Failed to load plants:', err);
          // Don't retry - just set loading to false
          setLoading(false);
          isLoadingRef.current = false;
        });
        loadCategories().catch(err => {
          console.error('Failed to load categories:', err);
          // Silent fail - categories aren't critical
        });
      }
    }, 150);
    
    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // EMPTY deps - only run once, never re-run
  
  // Clear AI results when search term is cleared
  useEffect(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      setAiSearchResults(null);
      setAiSearchError(null);
      setAiSearchLoading(false);
    }
  }, [searchTerm]);

  const loadCategories = React.useCallback(async () => {
    try {
      // Use direct fetch to avoid authenticatedFetch redirect loop on unauthorized
      // authenticatedFetch redirects to '/' on 401/403, causing infinite reload loop!
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      const response = await fetch(`${API_BASE_URL}/plants/meta/categories`, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      // Handle unauthorized gracefully - categories should be public
      if (response.status === 401 || response.status === 403) {
        console.warn('Unauthorized loading categories - continuing without categories');
        setCategories([]);
        return;
      }
      
      if (!response.ok) {
        console.error('Failed to load categories:', response.status);
        setCategories([]);
        return;
      }
      
      const data = await response.json();
      setCategories(Array.isArray(data.categories) ? data.categories : []);
    } catch (error) {
      console.error('Error loading categories:', error);
      // Categories aren't critical - continue without them
      setCategories([]);
    }
  }, []); // Empty deps - this function doesn't depend on any props/state

  // Check for plantId in URL and open plant detail modal
  useEffect(() => {
    const plantId = searchParams.get('plantId');
    if (plantId && !showDetailModal) {
      // First, try to find the plant in the loaded plants
      let plant = plants.find(p => p.id === plantId);
      
      if (plant) {
        // Plant found in loaded list - open modal
        setSelectedPlant(plant);
        setShowDetailModal(true);
        // Clean up URL - remove plantId from query string
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.delete('plantId');
        const newSearch = newSearchParams.toString();
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
      } else if (plants.length > 0) {
        // Plant not in loaded list - fetch it directly (especially for public plants)
        // This ensures shared links work even if the plant isn't on the current page
        const fetchPlantById = async () => {
          try {
            const API_BASE_URL = process.env.REACT_APP_API_URL || 
              (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
            const token = localStorage.getItem('authToken');
            
            const headers = { 'Content-Type': 'application/json' };
            if (token) {
              headers['Authorization'] = `Bearer ${token}`;
            }
            
            const response = await fetch(`${API_BASE_URL}/plants/${plantId}`, { headers });
            
            if (response.ok) {
              const data = await response.json();
              // Response format: { success: true, plant: {...} }
              const plantData = data.plant || data;
              setSelectedPlant(plantData);
              setShowDetailModal(true);
              // Clean up URL - remove plantId from query string
              const newSearchParams = new URLSearchParams(location.search);
              newSearchParams.delete('plantId');
              const newSearch = newSearchParams.toString();
              navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
            } else {
              const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
              console.warn(`Plant ${plantId} not found or not accessible:`, errorData.error || errorData.message);
            }
          } catch (error) {
            console.error('Error fetching plant by ID:', error);
          }
        };
        
        fetchPlantById();
      }
    }
  }, [plants, searchParams, location.search, location.pathname, navigate, showDetailModal]);

  const handlePlantClick = (plant) => {
    // For AI suggestions, preserve the image and AI data
    if (plant.isAISuggestion) {
      // Ensure the plant object has the image preserved
      const plantWithImage = {
        ...plant,
        image_url: plant.image_url || plant.aiPlantData?.image_url
      };
      setSelectedPlant(plantWithImage);
    } else {
      setSelectedPlant(plant);
    }
    setShowDetailModal(true);
  };

  const handleAddPlant = () => {
    // New plant creation should not try to restore list scroll
    listScrollYRef.current = null;
    setEditingPlant(null);
    setShowFormModal(true);
  };

  const handleEditPlant = (plant) => {
    // Capture current list scroll position before opening the edit form
    try {
      listScrollYRef.current = window.scrollY || 0;
    } catch {
      listScrollYRef.current = null;
    }
    setEditingPlant(plant);
    setShowDetailModal(false);
    setShowFormModal(true);
  };

  const handleDeletePlant = async (plantId) => {
    try {
      await plantsAPI.delete(plantId);
      setShowDetailModal(false);
      loadPlants();
    } catch (error) {
      alert(`Error deleting plant: ${error.message}`);
    }
  };

  const handleSavePlant = async () => {
    console.log('HandleSavePlant called - reloading plants...');
    setShowFormModal(false);
    setEditingPlant(null);
    
    // Add a small delay to ensure the plant is saved before reloading
    setTimeout(async () => {
      await loadPlants();
      console.log('Plants reloaded after save');

      // Restore scroll position back to where the user was in results
      const y = listScrollYRef.current;
      if (typeof y === 'number' && !Number.isNaN(y)) {
        // Wait for the DOM to paint the updated list before scrolling
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo({ top: y, behavior: 'auto' });
          });
        });
      }
      listScrollYRef.current = null;
    }, 500);
  };

  const handleMarkImageVerified = async (plant, verified = true) => {
    try {
      if (!plant?.id) return;
      const resp = await plantsAPI.update(plant.id, { image_verified: !!verified });
      const updated = resp?.plant || resp;
      if (!updated) return;

      setPlants(prev => prev.map(p => (p.id === plant.id ? { ...p, ...updated } : p)));
      setSelectedPlant(prev => (prev && prev.id === plant.id ? { ...prev, ...updated } : prev));
    } catch (error) {
      console.error('Error marking plant image verified:', error);
      const msg = (error.message || '').toLowerCase();
      if (msg.includes('authentication') || msg.includes('invalid token') || msg.includes('unauthorized')) {
        alert('Your session may have expired. Please sign in again to verify images.');
        navigate('/login');
      } else {
        alert(`Failed to update verification: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const handlePlantUpdated = (updatedPlant) => {
    if (!updatedPlant?.id) return;
    setPlants(prev => prev.map(p => (p.id === updatedPlant.id ? { ...p, ...updatedPlant } : p)));
    setSelectedPlant(prev => (prev && prev.id === updatedPlant.id ? { ...prev, ...updatedPlant } : prev));
  };

  // AI Search handler
  const handleAISearch = async (overrideQuery) => {
    const queryToUse = (overrideQuery !== undefined ? overrideQuery : aiSearchQuery).trim();
    if (!queryToUse) {
      setAiSearchError('Please enter a search query');
      return;
    }

    setAiSearchLoading(true);
    setAiSearchError(null);
    setAiSearchResults([]);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/plants/smart-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ query: queryToUse })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'AI search failed');
      }

      const data = await response.json();
      
      // The smart-search endpoint returns { results: { plants: [...] } }
      let plants = [];
      if (data.results && data.results.plants && Array.isArray(data.results.plants)) {
        plants = data.results.plants;
      } else if (data.plants && Array.isArray(data.plants)) {
        plants = data.plants;
      }
      
      // Mark each plant as AI suggestion for saving
      const plantsWithFlags = plants.map(plant => ({
        ...plant,
        isAISuggestion: true,
        aiPlantData: plant
      }));
      setAiSearchResults(plantsWithFlags);
    } catch (error) {
      console.error('Error in AI search:', error);
      setAiSearchError(error.message || 'Failed to search for plants');
      setAiSearchResults([]);
    } finally {
      setAiSearchLoading(false);
    }
  };

  const startAISearchFromIdentify = (query) => {
    const q = (query || '').toString().trim();
    if (!q) return;
    setShowIdentifyPlantModal(false);
    setShowAISearchModal(true);
    setAiSearchQuery(q);
    setAiSearchResults([]);
    setAiSearchError(null);
    handleAISearch(q);
  };

  const handleIdentifyPlant = async () => {
    if (!identifyFile) {
      setIdentifyError('Please choose a photo.');
      return;
    }

    setIdentifyError(null);
    setIdentifyResult(null);
    setIdentifyUploading(true);
    setIdentifyLoading(false);

    try {
      const { blobUrl } = await uploadPlantIdentificationImage(identifyFile);
      setIdentifyUploading(false);
      setIdentifyLoading(true);
      const idData = await identifyPlantWithBlobUrl(blobUrl, identifyOrgan);
      setIdentifyResult(idData);
    } catch (e) {
      console.error('Identify plant error:', e);
      setIdentifyError(e.message || 'Failed to identify plant.');
    } finally {
      setIdentifyUploading(false);
      setIdentifyLoading(false);
    }
  };

  // Save AI search plant directly to private collection
  const handleSaveAISearchPlant = async (plant) => {
    const token = localStorage.getItem('authToken');
    const currentUser = getCurrentUser();
    
    if (!token || !currentUser?.id) {
      alert('Please sign in to save plants to your collection.');
      return;
    }

    setSavingPlantId(plant.botanical_name || plant.common_name);
    
    try {
      const plantPayload = {
        common_name: plant.common_name || '',
        botanical_name: plant.botanical_name || '',
        category: plant.category || 'Perennials',
        size: plant.mature_size || '',
        growth_rate: 'Medium',
        sun_requirements: plant.light_requirements || 'Full Sun',
        water_requirements: plant.water_requirements || 'Moderate',
        hardiness_zones: plant.hardiness_zones || '',
        description: plant.why_recommended || plant.care_notes || plant.growing_notes || '',
        image_url: plant.image_url || null,
        is_public: false, // Always save to private collection
        photo_attribution: plant.photo_attribution || null,
        photo_source: plant.photo_source || 'ai_generated',
        image_verified: plant.image_verified || false,
        created_by_designer_id: currentUser.id,
        plant_data: {
          care_notes: plant.care_notes || null,
          growing_notes: plant.growing_notes || null,
          typical_price: plant.typical_price || null
        }
      };

      const savedPlant = await plantsAPI.create(plantPayload);
      setSavedPlantId(savedPlant.id);
      setShowSaveSuccess(true);
      
      // Remove the saved plant from results
      setAiSearchResults(prev => prev.filter(p => 
        (p.botanical_name || p.common_name) !== (plant.botanical_name || plant.common_name)
      ));
    } catch (error) {
      console.error('Error saving plant:', error);
      alert(`Error saving plant: ${error.message || 'Please try again.'}`);
    } finally {
      setSavingPlantId(null);
    }
  };

  // Find more images for a plant in AI search results
  const handleFindMoreImagesForPlant = async (plant) => {
    const botanicalName = plant.botanical_name;
    const commonName = plant.common_name;
    
    const hasBotanicalName = botanicalName && botanicalName.trim() !== '';
    const hasCommonName = commonName && commonName.trim() !== '';
    
    if (!hasBotanicalName && !hasCommonName) {
      const searchTerm = botanicalName || commonName || 'plant';
      const googleUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchTerm)}`;
      window.open(googleUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      
      const requestBody = {
        commonName: commonName || null,
        verifiedOnly: true
      };
      
      if (hasBotanicalName) {
        requestBody.botanicalName = botanicalName.trim();
        requestBody.query = botanicalName.trim(); // Also send for legacy fallback
      } else {
        requestBody.query = (commonName || '').trim();
      }
      
      const response = await fetch(`${API_BASE_URL}/plants/find-photos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to fetch photos');
      }

      const data = await response.json();
      if (data.photos && data.photos.length > 0) {
        const verifiedPhotos = data.photos.filter(p => 
          p.url && 
          p.url !== 'null' && 
          !p.url.includes('placeholder') &&
          (p.verified || p.source === 'wikimedia' || p.source === 'inaturalist' || p.source === 'kew' || p.source === 'gbif' || p.source === 'plantnet')
        );
        if (verifiedPhotos.length > 0) {
          // Open photo gallery modal for AI Search
          setAiSearchGalleryPhotos(verifiedPhotos);
          setShowAISearchPhotoGallery(true);
        } else {
          // No verified photos - open Google Images
          const searchTerm = botanicalName || commonName || '';
          const googleUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchTerm)}`;
          window.open(googleUrl, '_blank', 'noopener,noreferrer');
        }
      } else {
        // No photos found - open Google Images
        const searchTerm = botanicalName || commonName || '';
        const googleUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchTerm)}`;
        window.open(googleUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Error fetching photos:', err);
      // On error, open Google Images as fallback
      const searchTerm = botanicalName || commonName || '';
      if (searchTerm) {
        const googleUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchTerm)}`;
        window.open(googleUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const handleShareToCommunity = async (plant, makePublic = true) => {
    // If unsharing, just update directly
    if (!makePublic) {
      try {
        const updatedPlant = {
          ...plant,
          is_public: false
        };
        
        await plantsAPI.update(plant.id, updatedPlant);
        alert(`"${plant.common_name}" has been removed from the community and is now in "My Plants" only.`);
        loadPlants();
      } catch (error) {
        console.error('Error updating plant visibility:', error);
        alert(`Error unsharing plant: ${error.message}`);
      }
      return;
    }

    // If sharing, show modal to add attribution
    const currentUser = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('user') || '{}');
    setShareAttribution(plant.photo_attribution || (currentUser?.name ? `Photo by ${currentUser.name}` : ''));
    setSharePhotoSource(plant.photo_source || 'designer_contributed');
    setPlantToShare(plant);
    setShowShareModal(true);
  };

  // Check if plant already exists in public database
  const checkExistingPublicPlant = async (plant) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const token = localStorage.getItem('authToken');
      
      // Search for existing public plant by botanical name
      const response = await fetch(
        `${API_BASE_URL}/plants?search=${encodeURIComponent(plant.botanical_name)}&is_public=true&limit=10`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Check if we found a match
        const match = data.plants?.find(p => 
          p.is_public === true && 
          (p.botanical_name?.toLowerCase() === plant.botanical_name?.toLowerCase() ||
           p.common_name?.toLowerCase() === plant.common_name?.toLowerCase())
        );
        return match || null;
      }
      return null;
    } catch (error) {
      console.error('Error checking for existing plant:', error);
      return null;
    }
  };

  // Send edit request to admin (john)
  const sendEditRequest = async () => {
    if (!plantToShare || !existingPublicPlant) return;

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
      const token = localStorage.getItem('authToken');
      const currentUser = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('user') || '{}');
      
      // Create a message/request to admin
      const requestData = {
        type: 'plant_edit_request',
        subject: `Edit Request for ${existingPublicPlant.common_name}`,
        message: `User ${currentUser.name || currentUser.email || 'A user'} is requesting permission to edit the public plant "${existingPublicPlant.common_name}" (${existingPublicPlant.botanical_name}).

Their private version can be viewed at: /plants?plantId=${plantToShare.id}
Public plant ID: ${existingPublicPlant.id}

Please review and grant edit permissions if appropriate.`,
        recipient_email: 'johnconnors1690@gmail.com',
        plant_id: existingPublicPlant.id,
        private_plant_id: plantToShare.id
      };

      // Try to use messages API if available, otherwise use a simple notification
      try {
        const response = await fetch(`${API_BASE_URL}/messages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestData)
        });

        if (response.ok) {
          alert('Edit request sent to admin! You will be notified when permission is granted.');
        } else {
          // Fallback: just show a message
          alert(`Edit request prepared. Please contact admin at johnconnors1690@gmail.com with:\n\nPlant: ${existingPublicPlant.common_name}\nYour private version: /plants?plantId=${plantToShare.id}`);
        }
      } catch (msgError) {
        // Fallback notification
        alert(`Edit request prepared. Please contact admin at johnconnors1690@gmail.com with:\n\nPlant: ${existingPublicPlant.common_name}\nYour private version: /plants?plantId=${plantToShare.id}`);
      }

      setShowEditRequestModal(false);
      setShowShareModal(false);
      setPlantToShare(null);
      setExistingPublicPlant(null);
    } catch (error) {
      console.error('Error sending edit request:', error);
      alert('Error sending edit request. Please try again or contact admin directly.');
    }
  };

  const confirmShareToCommunity = async () => {
    if (!plantToShare) return;

    try {
      // First check if plant already exists in public database
      const existing = await checkExistingPublicPlant(plantToShare);
      
      if (existing) {
        // Plant already exists publicly - show edit request modal
        setExistingPublicPlant(existing);
        setShowShareModal(false);
        setShowEditRequestModal(true);
        return;
      }

      // Use main component's currentUser (already defined with useState)
      const updatedPlant = {
        ...plantToShare,
        is_public: true,
        status: 'pending', // Require admin approval for community sharing
        photo_attribution: shareAttribution || (currentUser?.name ? `Photo by ${currentUser.name}` : ''),
        photo_source: sharePhotoSource,
        created_by_designer_id: plantToShare.created_by_designer_id || currentUser?.id || null
      };
      
      await plantsAPI.update(plantToShare.id, updatedPlant);
      alert(`"${plantToShare.common_name}" has been submitted for community sharing! It will be reviewed by an admin before being made public.`);
      setShowShareModal(false);
      setPlantToShare(null);
      loadPlants();
    } catch (error) {
      console.error('Error sharing plant:', error);
      alert(`Error sharing plant: ${error.message}`);
    }
  };

  // ALL HOOKS MUST BE BEFORE ANY CONDITIONAL RETURNS
  // Show loading screen, but add a timeout fallback
  const [showManualButton, setShowManualButton] = useState(false);
  
  useEffect(() => {
    if (loading) {
      // If loading takes more than 30 seconds, show manual button as fallback
      // Increased from 5s to 30s to allow time for AI attribute filtering
      const timeout = setTimeout(() => {
        setShowManualButton(true);
      }, 30000);
      return () => clearTimeout(timeout);
    } else {
      setShowManualButton(false);
    }
  }, [loading]);
  
  // Safety check: If too many renders, show error message (AFTER all hooks)
  renderCount++;
  const now = Date.now();
  let showLoopError = false;
  if (now - lastResetTime < 1000) {
    // If we've rendered many times in under a second, we're in a loop
    if (renderCount > MAX_RENDERS) {
      showLoopError = true;
      // Reset counter after a delay
      setTimeout(() => { renderCount = 0; lastResetTime = Date.now(); }, 5000);
    }
  } else {
    // Reset counter if more than 1 second has passed
    renderCount = 1;
    lastResetTime = now;
  }
  
  if (showLoopError) {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-red-800 mb-4">Infinite Loop Detected</h2>
          <p className="text-red-700 mb-4">The component is re-rendering too quickly. Please:</p>
          <ol className="text-left text-red-700 list-decimal list-inside mb-4 space-y-2">
            <li>Clear your browser cache (Ctrl+Shift+Delete)</li>
            <li>Or close and reopen the browser</li>
            <li>Or navigate to a different page and come back</li>
          </ol>
          <button 
            onClick={() => window.location.href = '/'}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Try Reloading
          </button>
        </div>
      </div>
    );
  }
  
  if (loading && !showManualButton) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading plants...</div>
      </div>
    );
  }
  
  if (loading && showManualButton) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Loading Taking Too Long</h2>
          <p className="text-gray-600 mb-6">
            Automatic loading seems stuck. Click the button below to load manually.
          </p>
          <button
            onClick={() => {
              window.__plantaday_autoLoaded = false;
              setShowManualButton(false);
              setLoading(true);
              loadPlants();
              loadCategories();
            }}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 text-lg font-semibold"
          >
            Load Plants Manually
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicNav
        currentUserId={currentUserId}
        beforeNav={
          isAdminUser && currentUserId ? (
            <div className="md:hidden border-b border-gray-200 bg-white">
              <div className="max-w-7xl mx-auto px-3 py-2">
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-purple-700">View:</span>
                  <button
                    type="button"
                    onClick={() => {
                      setViewMode('admin');
                      localStorage.setItem('plantaday_viewMode', 'admin');
                    }}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      viewMode === 'admin'
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-300'
                    }`}
                    title="Admin View - See all designer features"
                  >
                    👑 Admin
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setViewMode('customer');
                      localStorage.setItem('plantaday_viewMode', 'customer');
                    }}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      viewMode === 'customer'
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-300'
                    }`}
                    title="User View - See as regular customer"
                  >
                    👤 User
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/admin')}
                    className="px-3 py-1.5 rounded text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 border border-purple-700 transition-colors"
                    title="Open Admin Panel"
                  >
                    Admin Panel
                  </button>
                </div>
              </div>
            </div>
          ) : null
        }
      />

      <div className="py-3 sm:py-6 px-3 sm:px-4">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section / Preamble - Compact */}
        <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-lg shadow-md p-3 sm:p-4 mb-4 sm:mb-6 border border-green-200">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
            {/* Logo - Smaller */}
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-white rounded-lg shadow-sm flex items-center justify-center border border-green-200 overflow-hidden">
              <img src="/plantadaylogo.png" alt="PlantaDay Logo" className="w-full h-full object-contain p-1 sm:p-1.5" />
                </div>
            <div className="flex-grow min-w-0">
              <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-0.5 sm:mb-1">Plant library</h1>
              <p className="text-xs sm:text-sm text-gray-700 leading-snug line-clamp-2 sm:line-clamp-none">
                Search and explore plants for your yard. Prefer a gentler path? Use <button type="button" onClick={() => navigate({ pathname: '/ideas', hash: '#guided-lookup' })} className="text-green-700 font-medium underline hover:text-green-900">Yard Ideas &amp; guided lookup</button> or start from the <button type="button" onClick={() => navigate('/')} className="text-green-700 font-medium underline hover:text-green-900">home page</button> for ideas and designers.
              </p>
              </div>
          </div>
              </div>
            
        {/* Search and Filters - PRIMARY CTA - Most Prominent */}
        <div className="bg-white rounded-lg shadow-lg sm:shadow-xl p-4 sm:p-5 mb-4 sm:mb-6 border-2 border-green-400 sm:border-green-300">
          {/* Search Row - More Prominent on Mobile */}
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <div className="w-full sm:flex-1 relative" ref={searchTooltipRef}>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                    const newSearchTerm = e.target.value;
                    
                    // Keep the full search term visible - don't remove anything
                    setSearchTerm(newSearchTerm);
                    
                    // Auto-detect color words in search term and set color filter
                    const colorMap = {
                      'red': 'red',
                      'pink': 'pink',
                      'purple': 'purple',
                      'blue': 'blue',
                      'yellow': 'yellow',
                      'orange': 'orange',
                      'white': 'white',
                      'green': 'green',
                      'multicolor': 'multi',
                      'multi-color': 'multi',
                      'multi color': 'multi',
                      'rainbow': 'multi'
                    };
                    
                    const searchLower = newSearchTerm.toLowerCase();
                    let detectedColor = '';
                    
                    // Check for color words (as whole words, not substrings)
                    for (const [keyword, colorValue] of Object.entries(colorMap)) {
                      // Use word boundary regex to match whole words only
                      const regex = new RegExp(`\\b${keyword}\\b`, 'i');
                      if (regex.test(searchLower)) {
                        detectedColor = colorValue;
                        break; // Use first match
                      }
                    }
                    
                    // If color detected and different from current, update it
                    if (detectedColor && detectedColor !== selectedColor) {
                      setSelectedColor(detectedColor);
                      selectedColorRef.current = detectedColor;
                    } else if (!detectedColor && selectedColor) {
                      // If no color detected but one is set, check if it's still in the search term
                      const currentColorKeyword = Object.entries(colorMap).find(([_, val]) => val === selectedColor)?.[0];
                      if (currentColorKeyword) {
                        const regex = new RegExp(`\\b${currentColorKeyword}\\b`, 'i');
                        if (!regex.test(searchLower)) {
                          // Color word removed from search - clear color filter
                          setSelectedColor('');
                          selectedColorRef.current = '';
                        }
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                  searchTermRef.current = searchTerm;
                  selectedCategoryRef.current = selectedCategory;
                  selectedColorRef.current = selectedColor;
                  ownershipFilterRef.current = ownershipFilter;
                      setCurrentPage(1);
                  setTimeout(() => loadPlants(), 0);
                  // Public Library = database only (no AI). All/Private = database + AI.
                  if (searchTerm.trim() && ownershipFilter !== 'public') {
                    setAiSearchQuery(searchTerm);
                    setAiSearchError(null);
                    handleAISearch(searchTerm.trim());
                  } else {
                    setAiSearchResults([]);
                    setAiSearchError(null);
                    setAiSearchLoading(false);
                  }
                    }
                  }}
                placeholder="Search for anything... plant names, conditions, or descriptions (e.g., 'roses,' 'shade perennials,' 'drought tolerant zone 5')"
                className="w-full px-4 py-3.5 sm:px-4 sm:py-3 pr-10 border-2 border-green-500 sm:border-green-400 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base sm:text-base min-h-[56px] sm:min-h-[52px] touch-manipulation shadow-md hover:shadow-lg transition-shadow"
              />
              {/* Search Tip Tooltip Icon */}
              <button
                type="button"
                onClick={() => setShowSearchTooltip(!showSearchTooltip)}
                onMouseEnter={() => setShowSearchTooltip(true)}
                onMouseLeave={() => setShowSearchTooltip(false)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 p-1"
                aria-label="Search tips"
              >
                <Info className="w-4 h-4" />
              </button>
              {/* Tooltip Content */}
              {showSearchTooltip && (
                <div 
                  className="absolute z-50 right-0 top-full mt-1 w-72 sm:w-80 bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3"
                  onMouseEnter={() => setShowSearchTooltip(true)}
                  onMouseLeave={() => setShowSearchTooltip(false)}
                >
                  <div className="font-semibold mb-1">💡 Search Tips:</div>
                  <div className="space-y-1 text-gray-200">
                    <div>• Plant names: "Hydrangea", "Acer palmatum"</div>
                    <div>• Conditions: "shade perennials", "drought tolerant zone 5"</div>
                    <div>• Descriptions: "red flowers", "low maintenance shrubs"</div>
                    <div className="text-gray-300 mt-2 text-[10px]">Search anything—results include our database plus AI suggestions.</div>
                  </div>
                  <div className="absolute -top-1 right-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                </div>
              )}
            </div>
            <button
                  onClick={() => {
                searchTermRef.current = searchTerm;
                selectedCategoryRef.current = selectedCategory;
                selectedColorRef.current = selectedColor;
                ownershipFilterRef.current = ownershipFilter;
                    setCurrentPage(1);
                setTimeout(() => loadPlants(), 0);
                // Public Library = database only (no AI). All/Private = database + AI.
                if (searchTerm.trim() && ownershipFilter !== 'public') {
                  setAiSearchQuery(searchTerm);
                  setAiSearchError(null);
                  handleAISearch(searchTerm.trim());
                } else {
                  setAiSearchResults([]);
                  setAiSearchError(null);
                  setAiSearchLoading(false);
                }
                  }}
              className="w-full sm:w-auto px-6 sm:px-4 py-3 sm:py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 min-h-[52px] sm:min-h-[44px] text-base sm:text-sm font-semibold sm:font-medium flex items-center justify-center gap-2 sm:gap-1.5 touch-manipulation shadow-lg sm:shadow-sm"
              aria-label="Search"
                >
              <Search className="w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0" />
              <span>Search</span>
            </button>
          </div>

          {/* Filters Toggle Button */}
          <div className="mt-3 flex items-center justify-between">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
            >
              {showFilters ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Hide Filters
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Show Filters
                </>
              )}
            </button>
            {(searchTerm || selectedCategory || selectedColor || ownershipFilter !== 'all' || heightMin || heightMax || widthMin || widthMax || zoneMin || zoneMax || lightStructured || waterStructured) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                  setSelectedColor('');
                  setOwnershipFilter('all');
                  setHeightMin('');
                  setHeightMax('');
                  setWidthMin('');
                  setWidthMax('');
                  setZoneMin('');
                  setZoneMax('');
                  setLightStructured('');
                  setWaterStructured('');
                  searchTermRef.current = '';
                  selectedCategoryRef.current = '';
                  selectedColorRef.current = '';
                  ownershipFilterRef.current = 'all';
                  heightMinRef.current = '';
                  heightMaxRef.current = '';
                  widthMinRef.current = '';
                  widthMaxRef.current = '';
                  zoneMinRef.current = '';
                  zoneMaxRef.current = '';
                  lightStructuredRef.current = '';
                  waterStructuredRef.current = '';
                  setCurrentPage(1);
                  setTimeout(() => loadPlants(), 0);
                }}
                className="px-3 py-2 text-xs sm:text-sm text-blue-600 hover:text-blue-800 active:text-blue-900 font-medium whitespace-nowrap"
              >
                Clear all
              </button>
            )}
          </div>

          {/* Filters Row - Collapsible */}
          {showFilters && (
            <>
              <div className="mt-3 space-y-2 sm:space-y-0 sm:flex sm:flex-wrap sm:items-center sm:gap-2">
                <select
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    selectedCategoryRef.current = e.target.value;
                    setCurrentPage(1);
                    setTimeout(() => loadPlants(), 0);
                  }}
                  className="w-full sm:w-auto px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm min-h-[44px] bg-white touch-manipulation sm:min-w-[140px]"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <select
                  value={selectedColor}
                  onChange={(e) => {
                    setSelectedColor(e.target.value);
                    selectedColorRef.current = e.target.value;
                    setCurrentPage(1);
                    setTimeout(() => loadPlants(), 0);
                  }}
                  className="w-full sm:w-auto px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm min-h-[44px] bg-white touch-manipulation sm:min-w-[140px]"
                  title="If a plant comes in this colour, it will appear in the list. Use AI Lookup to find more pictures of plants in specific colours."
                >
                  <option value="">All Colours</option>
                  <option value="red">🔴 Red</option>
                  <option value="pink">🌸 Pink</option>
                  <option value="purple">💜 Purple</option>
                  <option value="blue">🔵 Blue</option>
                  <option value="yellow">🟡 Yellow</option>
                  <option value="orange">🟠 Orange</option>
                  <option value="white">⚪ White</option>
                  <option value="green">🟢 Green</option>
                  <option value="multi">🌈 Multi-Colour</option>
                </select>
                
                {/* Advanced Filters Toggle */}
                <button
                  type="button"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className="w-full sm:w-auto px-3 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm min-h-[44px] bg-white touch-manipulation text-gray-700 font-medium"
                >
                  {showAdvancedFilters ? 'Hide Advanced' : 'Advanced Filters'}
                </button>
              </div>
              
              {/* Advanced Structured Filters */}
              {showAdvancedFilters && (
              <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
                <div className="text-xs font-semibold text-gray-600 mb-2">Size & Growing Conditions</div>
                
                {/* Height Range */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Min Height (ft)</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={heightMin}
                      onChange={(e) => {
                        setHeightMin(e.target.value);
                        heightMinRef.current = e.target.value;
                        setCurrentPage(1);
                        setTimeout(() => loadPlants(), 0);
                      }}
                      placeholder="Min"
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm min-h-[44px]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Max Height (ft)</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={heightMax}
                      onChange={(e) => {
                        setHeightMax(e.target.value);
                        heightMaxRef.current = e.target.value;
                        setCurrentPage(1);
                        setTimeout(() => loadPlants(), 0);
                      }}
                      placeholder="Max"
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm min-h-[44px]"
                    />
                  </div>
                </div>
                
                {/* Width Range */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Min Width (ft)</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={widthMin}
                      onChange={(e) => {
                        setWidthMin(e.target.value);
                        widthMinRef.current = e.target.value;
                        setCurrentPage(1);
                        setTimeout(() => loadPlants(), 0);
                      }}
                      placeholder="Min"
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm min-h-[44px]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Max Width (ft)</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={widthMax}
                      onChange={(e) => {
                        setWidthMax(e.target.value);
                        widthMaxRef.current = e.target.value;
                        setCurrentPage(1);
                        setTimeout(() => loadPlants(), 0);
                      }}
                      placeholder="Max"
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm min-h-[44px]"
                    />
                  </div>
                </div>
                
                {/* Zone Range */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Min Zone</label>
                    <input
                      type="number"
                      min="1"
                      max="13"
                      value={zoneMin}
                      onChange={(e) => {
                        setZoneMin(e.target.value);
                        zoneMinRef.current = e.target.value;
                        setCurrentPage(1);
                        setTimeout(() => loadPlants(), 0);
                      }}
                      placeholder="Min"
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm min-h-[44px]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Max Zone</label>
                    <input
                      type="number"
                      min="1"
                      max="13"
                      value={zoneMax}
                      onChange={(e) => {
                        setZoneMax(e.target.value);
                        zoneMaxRef.current = e.target.value;
                        setCurrentPage(1);
                        setTimeout(() => loadPlants(), 0);
                      }}
                      placeholder="Max"
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm min-h-[44px]"
                    />
                  </div>
                </div>
                
                {/* Light & Water */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Light</label>
                    <select
                      value={lightStructured}
                      onChange={(e) => {
                        setLightStructured(e.target.value);
                        lightStructuredRef.current = e.target.value;
                        setCurrentPage(1);
                        setTimeout(() => loadPlants(), 0);
                      }}
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm min-h-[44px] bg-white"
                    >
                      <option value="">Any Light</option>
                      <option value="full_sun">Full Sun</option>
                      <option value="partial_sun">Partial Sun</option>
                      <option value="partial_shade">Partial Shade</option>
                      <option value="full_shade">Full Shade</option>
                      <option value="dappled_shade">Dappled Shade</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Water</label>
                    <select
                      value={waterStructured}
                      onChange={(e) => {
                        setWaterStructured(e.target.value);
                        waterStructuredRef.current = e.target.value;
                        setCurrentPage(1);
                        setTimeout(() => loadPlants(), 0);
                      }}
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm min-h-[44px] bg-white"
                    >
                      <option value="">Any Water</option>
                      <option value="drought_tolerant">Drought Tolerant</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="moist">Moist</option>
                    </select>
                  </div>
                </div>
              </div>
              )}
            </>
          )}
            </div>

        {/* Secondary Action Buttons - Below Search */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {/* Identify Plant button - vibrant emerald/teal */}
            <button
              onClick={() => {
                setShowIdentifyPlantModal(true);
                setIdentifyError(null);
                setIdentifyResult(null);
                setIdentifyFile(null);
              }}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg hover:from-emerald-600 hover:to-teal-600 active:from-emerald-700 active:to-teal-700 flex items-center gap-2 shadow-md hover:shadow-lg transition-all min-h-[44px] text-sm font-medium touch-manipulation flex-1 sm:flex-none"
              title="Identify a plant from a photo"
            >
              <span className="text-base">📷</span>
              <span className="truncate">Identify Plant</span>
            </button>

            {/* Get Yard Ideas button - vibrant green/blue gradient */}
            <button
              onClick={() => navigate('/yard-analysis')}
              className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg hover:from-green-600 hover:to-blue-600 active:from-green-700 active:to-blue-700 flex items-center gap-2 shadow-md hover:shadow-lg transition-all min-h-[44px] text-sm font-medium touch-manipulation flex-1 sm:flex-none"
            >
              <span className="text-base">🌿</span>
              <span className="truncate">Get Yard Ideas</span>
            </button>
            
            {/* Find a Designer button - vibrant violet/indigo */}
            <button
              onClick={() => navigate('/find-designer')}
              className="bg-gradient-to-r from-violet-500 to-indigo-500 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg hover:from-violet-600 hover:to-indigo-600 active:from-violet-700 active:to-indigo-700 flex items-center gap-2 shadow-md hover:shadow-lg transition-all min-h-[44px] text-sm font-medium touch-manipulation flex-1 sm:flex-none"
            >
              <span className="text-base">👤</span>
              <span className="truncate">Find a Designer</span>
            </button>
            
          {effectiveIsDesigner && (
            <>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-blue-600 text-white px-3 sm:px-4 py-2.5 rounded-lg hover:bg-blue-700 active:bg-blue-800 flex items-center gap-1.5 shadow-sm min-h-[44px] text-sm font-medium touch-manipulation flex-1 sm:flex-none"
              >
                <span className="text-base">📊</span>
                <span className="truncate">Dashboard</span>
              </button>
              {isAdminUser && (
                <button
                  onClick={() => {
                    // Open add plant modal for admin
                    setEditingPlant(null);
                    setShowFormModal(true);
                  }}
                  className="bg-purple-600 text-white px-3 sm:px-4 py-2.5 rounded-lg hover:bg-purple-700 active:bg-purple-800 flex items-center gap-1.5 shadow-sm min-h-[44px] text-sm font-medium touch-manipulation flex-1 sm:flex-none"
                >
                  <span className="text-base">🌿</span>
                  <span className="truncate">Add Plant</span>
                </button>
              )}
            </>
          )}
          </div>
        </div>

        {/* Identify Plant Modal */}
        {showIdentifyPlantModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-3 sm:p-4" onClick={(e) => {
            if (e.target === e.currentTarget) setShowIdentifyPlantModal(false);
          }}>
            <div className="bg-white rounded-xl w-full max-w-xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 sm:p-6 border-b flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">Identify Plant</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    {isMobileIdentifyUI
                      ? 'Take a photo (or choose one) and we’ll suggest matches (PlantNet).'
                      : 'Upload a photo and we’ll suggest matches (PlantNet).'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowIdentifyPlantModal(false)}
                  className="text-gray-400 hover:text-gray-700 text-2xl leading-none min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="p-4 sm:p-6 space-y-4">
                {!localStorage.getItem('authToken') && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-950">
                    <p className="font-medium">Sign in to identify a plant</p>
                    <p className="mt-1 text-amber-900/90">
                      Photo upload and PlantNet matching use your account. It is free to create one.
                    </p>
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                      <button
                        type="button"
                        onClick={() => {
                          sessionStorage.setItem('plantaday_openIdentify', '1');
                          setShowIdentifyPlantModal(false);
                          navigate('/login');
                        }}
                        className="font-semibold text-amber-900 underline hover:no-underline"
                      >
                        Sign in
                      </button>
                      <Link
                        to="/create-account"
                        className="font-semibold text-amber-900 underline hover:no-underline"
                        onClick={() => setShowIdentifyPlantModal(false)}
                      >
                        Create account
                      </Link>
                    </div>
                  </div>
                )}
                {identifyError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">{identifyError}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Photo</label>
                  <input
                    type="file"
                    accept="image/*,image/heic,image/heif,.heic,.heif"
                    capture="environment"
                    onChange={async (e) => {
                      let file = e.target.files?.[0] || null;
                      if (file && isHeicFile(file)) {
                        try {
                          file = await convertHeicToJpeg(file);
                        } catch (err) {
                          setIdentifyError(err.message || 'Could not convert HEIC image.');
                          return;
                        }
                      }
                      setIdentifyFile(file);
                      setIdentifyError(null);
                      setIdentifyResult(null);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {isMobileIdentifyUI
                      ? 'Tap to take a photo (or choose one). Max 5MB. Works best with a clear leaf/flower photo.'
                      : 'Choose an image file. Max 5MB. Works best with a clear leaf/flower photo.'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organ</label>
                  <select
                    value={identifyOrgan}
                    onChange={(e) => setIdentifyOrgan(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm min-h-[44px] bg-white"
                  >
                    <option value="leaf">Leaf</option>
                    <option value="flower">Flower</option>
                    <option value="fruit">Fruit</option>
                    <option value="bark">Bark</option>
                    <option value="habit">Habit</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <button
                  type="button"
                  onClick={handleIdentifyPlant}
                  disabled={identifyUploading || identifyLoading}
                  className="w-full bg-emerald-600 text-white py-3 px-4 rounded-lg hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed font-semibold flex items-center justify-center gap-2 min-h-[44px] touch-manipulation"
                >
                  {(identifyUploading || identifyLoading) ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      {identifyUploading ? 'Uploading…' : 'Identifying…'}
                    </>
                  ) : (
                    <>
                      <span className="text-base">🔎</span>
                      Identify
                    </>
                  )}
                </button>

                {identifyResult?.result?.results?.length > 0 && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-900">Top Matches</h4>
                      <span className="text-xs text-gray-500">Source: {identifyResult.source}</span>
                    </div>
                    <div className="space-y-2">
                      {identifyResult.result.results.slice(0, 3).map((r, idx) => (
                        <div key={idx} className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {r.species?.scientificNameWithoutAuthor || r.species?.scientificName || 'Unknown'}
                            </div>
                            {r.species?.commonNames?.[0] && (
                              <div className="text-xs text-gray-600 truncate">{r.species.commonNames[0]}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-semibold text-emerald-700">
                              {typeof r.score === 'number' ? `${Math.round(r.score * 100)}%` : '--'}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const q =
                                  r.species?.scientificNameWithoutAuthor ||
                                  r.species?.scientificName ||
                                  r.species?.commonNames?.[0] ||
                                  '';
                                startAISearchFromIdentify(q);
                              }}
                              className="px-3 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-semibold hover:from-purple-700 hover:to-pink-700 min-h-[36px] touch-manipulation"
                              title="Run AI lookup based on this match"
                            >
                              AI Lookup
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      Pick a match to run an AI lookup, then save it from the AI results.
                    </p>
                  </div>
                )}

                {identifyResult && Array.isArray(identifyResult?.result?.results) && identifyResult.result.results.length === 0 && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="text-sm font-semibold text-gray-900">No matches found</h4>
                    <p className="text-xs text-gray-600 mt-1">
                      Try a clearer photo (good lighting, focused leaf/flower, minimal background) or switch the organ.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Collection Filter - Below Search, Above Plants */}
        <div className="mb-4 sm:mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2 sm:mb-3">View Collection</label>
            <div className="flex flex-wrap gap-2 sm:gap-3">
            <button
              onClick={() => {
                setOwnershipFilter('all');
                  ownershipFilterRef.current = 'all';
                  setCurrentPage(1);
                  setTimeout(() => loadPlants(), 0);
                }}
                className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all min-h-[36px] sm:min-h-[44px] touch-manipulation ${
                  ownershipFilter === 'all'
                    ? 'bg-gray-900 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
                }`}
                title="View all plants (public library and your private collection)"
              >
                All
              </button>
              <button
                onClick={() => {
                  setOwnershipFilter('public');
                  ownershipFilterRef.current = 'public';
                  setCurrentPage(1);
                  setAiSearchResults([]);
                  setAiSearchError(null);
                  setAiSearchLoading(false);
                  // Ensure the filter is set before loading
                  setTimeout(() => {
                    console.log('✅ Public Library filter set:', ownershipFilterRef.current);
                    loadPlants();
                  }, 0);
                }}
                className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1 sm:gap-2 min-h-[36px] sm:min-h-[44px] touch-manipulation ${
                  ownershipFilter === 'public'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
                }`}
                title="Database only—no AI suggestions"
              >
                <span className="text-sm sm:text-base">🌍</span>
                <span className="hidden sm:inline">Public Library</span>
                <span className="sm:hidden">Public</span>
              </button>
              <button
                onClick={() => {
                  if (!currentUserId) {
                    alert('Please sign in to view your Private Collection');
                    navigate('/login');
                    return;
                  }
                  setOwnershipFilter('mine');
                  setCurrentPage(1);
                  // Update ref immediately before calling loadPlants
                  ownershipFilterRef.current = 'mine';
                  setTimeout(() => loadPlants(), 0);
                }}
                className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1 sm:gap-2 min-h-[36px] sm:min-h-[44px] touch-manipulation ${
                  ownershipFilter === 'mine'
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
                }`}
                title={effectiveIsDesigner ? "View only plants in your Private Collection" : "Sign in to view your Private Collection"}
              >
                <span className="text-sm sm:text-base">✨</span>
                <span className="hidden sm:inline">Private Collection</span>
                <span className="sm:hidden">Private</span>
              </button>
              {/* Add Manually button - only show when Private Collection filter is active */}
              {ownershipFilter === 'mine' && currentUserId && (
                <button
                  onClick={handleAddPlant}
                  className="bg-green-600 text-white px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg hover:bg-green-700 active:bg-green-800 flex items-center justify-center gap-2 text-xs sm:text-sm font-medium transition-all shadow-md min-h-[36px] sm:min-h-[44px] touch-manipulation"
                  title="Add a new plant to your Private Collection"
                >
                  <span className="text-base sm:text-lg">+</span>
                  <span className="hidden sm:inline">Add Manually</span>
                  <span className="sm:hidden">Add</span>
            </button>
          )}
            </div>
          </div>
        </div>

        {/* Plants Grid */}
        {/* Database Results Section */}
        {plants.length > 0 && (
          <>
            <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-semibold text-gray-900">
                  {searchTerm && searchTerm.trim() ? `In our database (${totalPlants})` : (ownershipFilter === 'mine' ? 'Private Collection' : ownershipFilter === 'public' ? 'Public Library' : 'All Plants')}
                </h3>
                <span className="text-sm sm:text-base text-gray-600">
                  Showing {plants.length} of {totalPlants} plant{totalPlants !== 1 ? 's' : ''}
                </span>
              {ownershipFilter === 'mine' && (
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs sm:text-sm">
                  ✨ Private Collection
                </span>
              )}
              {ownershipFilter === 'public' && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs sm:text-sm" title="Database only—no AI suggestions">
                  🌍 Public Library
                </span>
              )}
              {ownershipFilter === 'all' && (
                <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs sm:text-sm">
                  All Plants
                </span>
              )}
              </div>
              
              {/* Results per page selector */}
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <label className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">Results per page:</label>
                <select
                  value={resultsPerPage}
                  onChange={(e) => {
                    setResultsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                    setTimeout(() => {
                      if (!isLoadingRef.current) {
                        loadPlants();
                      }
                    }, 100);
                  }}
                  className="flex-1 sm:flex-none px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 min-h-[44px] touch-manipulation bg-white"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
              {plants.map(plant => {
                // Check if user owns the plant (in their private collection)
                const isOwner = plant.created_by_designer_id === currentUserId;
                // Check if this plant is from public library (for private collection plants)
                const plantIsFromPublic = !plant.is_public && isFromPublicLibrary(plant);
                const plantIsAIGenerated = !plant.is_public && isAIGenerated(plant);
                // Check if this database plant was also discovered by AI (for "Also discovered by AI" badge)
                const aiPlants = Array.isArray(aiSearchResults) ? aiSearchResults : (aiSearchResults?.plants || []);
                const plantKey = (n) => (n || '').toString().trim().toLowerCase();
                const alsoDiscoveredByAI = aiPlants.some(ai => 
                  plantKey(plant.common_name) === plantKey(ai.common_name) && 
                  plantKey(plant.botanical_name) === plantKey(ai.botanical_name)
                );
                
                return (
                  <PlantCard 
                    key={plant.id} 
                    plant={plant} 
                    onClick={handlePlantClick}
                    alsoDiscoveredByAI={alsoDiscoveredByAI}
                    onAILookup={handleAILookup}
                    onEdit={(() => {
                      // Only allow edit for public plants if in admin mode
                      if (plant.is_public && isAdminUser && viewMode !== 'admin') {
                        return null;
                      }
                      // Allow edit for private plants if owner, or for public plants if admin in admin mode
                      if (plant.is_public) {
                        return (isAdminUser && viewMode === 'admin') ? handleEditPlant : null;
                      }
                      return isOwner ? handleEditPlant : null;
                    })()}
                    onDelete={(() => {
                      // Only allow delete for public plants if in admin mode
                      if (plant.is_public && isAdminUser && viewMode !== 'admin') {
                        return null;
                      }
                      // Allow delete for private plants if owner, or for public plants if admin in admin mode
                      if (plant.is_public) {
                        return (isAdminUser && viewMode === 'admin') ? handleDeletePlant : null;
                      }
                      return isOwner ? handleDeletePlant : null;
                    })()}
                    onShareToCommunity={(() => {
                      // Only allow share/unshare for public plants if in admin mode
                      if (plant.is_public && isAdminUser && viewMode !== 'admin') {
                        return null;
                      }
                      // Allow share for private plants if owner, or for public plants if admin in admin mode
                      if (plant.is_public) {
                        return (isAdminUser && viewMode === 'admin') ? handleShareToCommunity : null;
                      }
                      return isOwner ? handleShareToCommunity : null;
                    })()}
                    onLike={handleLikePlant}
                    onMarkImageVerified={handleMarkImageVerified}
                    isOwner={isOwner}
                    isLiked={likedPlants.has(plant.id) || (!plant.is_public && plant.created_by_designer_id === currentUserId)}
                    isFromPublicLibrary={plantIsFromPublic}
                    isAIGenerated={plantIsAIGenerated}
                    isAdmin={isAdminUser && viewMode === 'admin'}
                  />
                );
              })}
            </div>
            
            {/* Pagination Controls */}
            {totalPlants > resultsPerPage && (
              <div className="mt-6 sm:mt-8 flex items-center justify-center gap-2 flex-wrap">
                <button
                  onClick={() => {
                    const newPage = Math.max(1, currentPage - 1);
                    setCurrentPage(newPage);
                    setTimeout(() => {
                      if (!isLoadingRef.current) {
                        loadPlants();
                      }
                    }, 100);
                  }}
                  disabled={currentPage === 1}
                  className="px-4 sm:px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium min-h-[44px] touch-manipulation"
                >
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </button>
                
                <div className="flex gap-1 overflow-x-auto max-w-full px-2 sm:px-0 scrollbar-hide">
                  {Array.from({ length: Math.ceil(totalPlants / resultsPerPage) }, (_, i) => i + 1)
                    .filter(page => {
                      // Show first page, last page, current page, and pages around current
                      const totalPages = Math.ceil(totalPlants / resultsPerPage);
                      // Show fewer pages on mobile (handled via CSS classes)
                      if (totalPages <= 5) return true;
                      if (page === 1 || page === totalPages) return true;
                      if (Math.abs(page - currentPage) <= 1) return true;
                      return false;
                    })
                    .map((page, index, array) => {
                      // Add ellipsis between gaps
                      const showEllipsisBefore = index > 0 && array[index] - array[index - 1] > 1;
                      return (
                        <React.Fragment key={page}>
                          {showEllipsisBefore && <span className="px-1 sm:px-2 text-gray-400 text-sm">...</span>}
                          <button
                            onClick={() => {
                              setCurrentPage(page);
                              setTimeout(() => {
                                if (!isLoadingRef.current) {
                                  loadPlants();
                                }
                              }, 100);
                            }}
                            className={`px-3 sm:px-4 py-2.5 rounded-lg text-sm font-medium min-h-[44px] min-w-[44px] touch-manipulation ${
                              currentPage === page
                                ? 'bg-green-600 text-white shadow-md'
                                : 'border border-gray-300 hover:bg-gray-50 active:bg-gray-100'
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}
                </div>
                
                <button
                  onClick={() => {
                    const maxPage = Math.ceil(totalPlants / resultsPerPage);
                    const newPage = Math.min(maxPage, currentPage + 1);
                    setCurrentPage(newPage);
                    setTimeout(() => {
                      if (!isLoadingRef.current) {
                        loadPlants();
                      }
                    }, 100);
                  }}
                  disabled={currentPage >= Math.ceil(totalPlants / resultsPerPage)}
                  className="px-4 sm:px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium min-h-[44px] touch-manipulation"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
        
        {/* Promo CTA for non-logged-in users - save to collection */}
        {plants.length > 0 && searchTerm && ownershipFilter !== 'mine' && !loading && !currentUserId && (
          <div className="mt-8 mb-6 p-6 sm:p-8 bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl">
            <div className="max-w-2xl mx-auto text-center">
              <div className="mb-4">
                <Sparkles className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                  Save Plants to Your Collection
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4">
                  Sign in to save plants from search results to your private collection.
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-4 border border-emerald-200 mb-4">
                <p className="text-xs sm:text-sm text-gray-700 mb-3">
                  <span className="font-semibold text-emerald-700">✨ Sign up for free</span> to get:
                </p>
                <ul className="text-left text-xs sm:text-sm text-gray-600 space-y-1 mb-2">
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-600">✓</span>
                    Save plants to your private collection
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-600">✓</span>
                    Filter by Public Library and your collection
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-600">✓</span>
                    Access to premium features
                  </li>
                </ul>
              </div>
              <button
                onClick={() => {
                  navigate('/create-account', { 
                    state: { returnTo: window.location.pathname, query: searchTerm } 
                  });
                }}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium shadow-md transition-all text-sm sm:text-base"
              >
                <Sparkles className="w-4 h-4" />
                Sign Up Free
              </button>
            </div>
          </div>
        )}

        {/* No Results - Show sign-in CTA (when no database or AI results) */}
        {plants.length === 0 && searchTerm && ownershipFilter !== 'mine' && !loading && !aiSearchLoading && ((Array.isArray(aiSearchResults) ? aiSearchResults : (aiSearchResults?.plants || [])).length === 0) && !currentUserId && (
          <div className="mt-8 mb-6 p-8 bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl text-center">
            <div className="max-w-2xl mx-auto">
              <div className="mb-4">
                <Sparkles className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">No plants found for "{searchTerm}"</h3>
                <p className="text-gray-600 mb-6">
                  Try a different search, or sign in to save plants to your private collection.
                </p>
              </div>
              <button
                onClick={() => {
                  navigate('/create-account', { 
                    state: { returnTo: window.location.pathname, query: searchTerm } 
                  });
                }}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium shadow-md transition-all"
              >
                <Sparkles className="w-4 h-4" />
                Sign Up Free
              </button>
            </div>
          </div>
        )}
        
        {aiSearchError && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
            <p className="text-sm text-yellow-700">
              <AlertCircle className="w-5 h-5 inline mr-2" />
              AI search unavailable: {aiSearchError}
            </p>
          </div>
        )}
        
        {/* AI Discovered Plants Section - only show when there's a search term and NOT in Public Library (database-only) */}
        {(ownershipFilter !== 'public' && searchTerm && searchTerm.trim() && (aiSearchLoading || (Array.isArray(aiSearchResults) ? aiSearchResults : (aiSearchResults?.plants || [])).length > 0)) && (
          <div data-ai-results-section>
            <div className="mb-4 mt-8 pt-6 border-t border-emerald-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-lg font-semibold text-emerald-700">AI Discovered Plants</h3>
                </div>
                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    placeholder="Search for more with AI..."
                    defaultValue={searchTerm}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const q = e.target.value?.trim();
                        if (q) {
                          handleAISearch(q);
                          searchTermRef.current = q;
                          setSearchTerm(q);
                        }
                      }
                    }}
                    className="flex-1 min-w-[160px] px-3 py-2 border border-emerald-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.querySelector('[data-ai-results-section] input[placeholder="Search for more with AI..."]');
                      const q = input?.value?.trim();
                      if (q) {
                        handleAISearch(q);
                        searchTermRef.current = q;
                        setSearchTerm(q);
                      }
                    }}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium flex items-center gap-2 whitespace-nowrap"
                  >
                    <Sparkles className="w-4 h-4" />
                    Search
                  </button>
                </div>
              </div>
              {(() => {
                const aiPlantsRaw = Array.isArray(aiSearchResults) ? aiSearchResults : (aiSearchResults?.plants || []);
                const plantKey = (n) => (n || '').toString().trim().toLowerCase();
                const aiPlantsFiltered = aiPlantsRaw.filter(ai =>
                  !plants.some(db =>
                    plantKey(db.common_name) === plantKey(ai.common_name) &&
                    plantKey(db.botanical_name) === plantKey(ai.botanical_name)
                  )
                );
                return aiPlantsFiltered.length > 0 && (
                  <>
                    <p className="text-sm text-gray-500 mb-2">
                      {aiPlantsFiltered.length} {aiPlantsFiltered.length === 1 ? 'plant discovered' : 'plants discovered'} (not in our database)
                    </p>
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-blue-800">
                          <p className="font-medium mb-1">Image Verification Status:</p>
                          <ul className="list-disc list-inside space-y-0.5 text-blue-700">
                            <li><span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-600 text-white rounded text-xs">Verified Botanical</span> = Exact species match confirmed</li>
                            <li><span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-yellow-500 text-white rounded text-xs">Representative</span> = Visual example, may not be exact species</li>
                            <li><span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-500 text-white rounded text-xs">Not Verified</span> = Image may not match this plant</li>
                          </ul>
                          <p className="mt-2 font-medium">Click any plant card to view verified botanical photos in the gallery.</p>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
            
            {aiSearchLoading && (
              <div className="py-12 text-center">
                <Loader className="w-8 h-8 animate-spin mx-auto text-emerald-600 mb-3" />
                <p className="text-sm text-gray-500">Discovering plants with AI...</p>
              </div>
            )}
            
            {!aiSearchLoading && (() => {
              const aiPlantsRaw = Array.isArray(aiSearchResults) ? aiSearchResults : (aiSearchResults?.plants || []);
              const plantKey = (n) => (n || '').toString().trim().toLowerCase();
              const aiPlantsFiltered = aiPlantsRaw.filter(ai =>
                !plants.some(db =>
                  plantKey(db.common_name) === plantKey(ai.common_name) &&
                  plantKey(db.botanical_name) === plantKey(ai.botanical_name)
                )
              );
              return aiPlantsFiltered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
              {aiPlantsFiltered.map((plant, index) => {
                // Convert AI result to plant-like object for PlantCard
                // Mark as public so it shows "Add to Private Collection" button
                // Build "about" description from AI data for card preview
                const aboutParts = [plant.why_recommended, plant.key_features].filter(Boolean);
                const aboutDescription = aboutParts.length > 0 ? aboutParts.join(' ') : null;
                const aiPlant = {
                  id: `ai-${index}`,
                  common_name: plant.common_name,
                  botanical_name: plant.botanical_name,
                  category: plant.category,
                  description: aboutDescription,
                  image_url: (plant.image_url && plant.image_url !== 'null' && !plant.image_url.includes('placeholder'))
                    ? plant.image_url
                    : null,
                  photo_source: plant.photo_source,
                  photo_attribution: plant.photo_attribution,
                  image_verified: plant.image_verified,
                  image_confidence: plant.image_confidence,
                  image_status: plant.image_status,
                  not_a_botanical_species: plant.not_a_botanical_species,
                  is_public: true,
                  plant_data: {
                    size: plant.mature_size,
                    sun_requirements: plant.light_requirements,
                    water_requirements: plant.water_requirements,
                    hardiness_zones: plant.hardiness_zones,
                    care_notes: plant.why_recommended,
                    growing_notes: plant.key_features,
                    price: plant.typical_price
                  },
                  isAISuggestion: true,
                  aiPlantData: plant,
                  image_search_term: plant.image_search_term || `${plant.common_name} ${searchTerm || ''}`
                };
                
                // Check if already in collection
                const isInCollection = likedPlants.has(aiPlant.id) || 
                  plants.some(p => p.common_name === plant.common_name && p.botanical_name === plant.botanical_name);
                
                return (
                  <div key={`ai-${index}`} className="relative">
                    <div className="absolute top-2 right-2 z-10 bg-emerald-600 text-white rounded-full px-2 py-1 flex items-center gap-1 text-xs font-medium">
                      <Sparkles className="w-3 h-3" />
                      AI
                    </div>
                    <PlantCard 
                      plant={aiPlant} 
                      onClick={() => {
                        handleAILookup(aiPlant);
                      }}
                      onAILookup={handleAILookup}
                      onEdit={null}
                      onDelete={null}
                      onShareToCommunity={null}
                      onLike={handleLikePlant}
                      isOwner={false}
                      isLiked={isInCollection}
                      isFromPublicLibrary={false}
                      isAIGenerated={true}
                      isAIDiscoveredOnly={true}
                      isAdmin={isAdminUser && viewMode === 'admin'}
                    />
                  </div>
                );
              })}
            </div>
              );
            })()}
          </div>
        )}
        
        {/* No results message */}
        {plants.length === 0 && !aiSearchLoading && ((Array.isArray(aiSearchResults) ? aiSearchResults : (aiSearchResults?.plants || [])).length === 0) && (
          <div className="text-center py-12">
            <p className="text-gray-600">No plants found. Try adjusting your filters.</p>
          </div>
        )}

        {/* Modals */}
        {showDetailModal && (
          <PlantDetailModal
            plant={selectedPlant}
            onClose={() => setShowDetailModal(false)}
            onEdit={(() => {
              // Only allow edit for public plants if in admin mode
              if (selectedPlant?.is_public && isAdminUser && viewMode !== 'admin') {
                return null;
              }
              // Allow edit for private plants if owner, or for public plants if admin in admin mode
              const isOwner = selectedPlant?.created_by_designer_id === currentUserId;
              if (selectedPlant?.is_public) {
                return (isAdminUser && viewMode === 'admin') ? handleEditPlant : null;
              }
              return isOwner ? handleEditPlant : null;
            })()}
            onDelete={(() => {
              // Only allow delete for public plants if in admin mode
              if (selectedPlant?.is_public && isAdminUser && viewMode !== 'admin') {
                return null;
              }
              // Allow delete for private plants if owner, or for public plants if admin in admin mode
              const isOwner = selectedPlant?.created_by_designer_id === currentUserId;
              if (selectedPlant?.is_public) {
                return (isAdminUser && viewMode === 'admin') ? handleDeletePlant : null;
              }
              return isOwner ? handleDeletePlant : null;
            })()}
            isOwner={selectedPlant?.created_by_designer_id === currentUserId}
            isDesigner={isDesigner}
            onAILookup={handleAILookup}
            onShareToCommunity={(() => {
              // Only allow share/unshare for public plants if in admin mode
              if (selectedPlant?.is_public && isAdminUser && viewMode !== 'admin') {
                return null;
              }
              // Allow share for private plants if owner, or for public plants if admin in admin mode
              const isOwner = selectedPlant?.created_by_designer_id === currentUserId;
              if (selectedPlant?.is_public) {
                return (isAdminUser && viewMode === 'admin') ? handleShareToCommunity : null;
              }
              return isOwner ? handleShareToCommunity : null;
            })()}
            onLike={handleLikePlant}
            isLiked={selectedPlant && (likedPlants.has(selectedPlant.id) || (!selectedPlant.is_public && selectedPlant.created_by_designer_id === currentUserId))}
            isFromPublicLibrary={selectedPlant && !selectedPlant.is_public && isFromPublicLibrary(selectedPlant)}
            onRemoveFromCollection={(() => {
              // Only allow remove from collection for public plants if in admin mode
              if (selectedPlant?.is_public && isAdminUser && viewMode !== 'admin') {
                return null;
              }
              return handleRemoveFromCollection;
            })()}
            onMarkImageVerified={handleMarkImageVerified}
            onPlantUpdated={handlePlantUpdated}
          />
        )}

        {showFormModal && (
          <PlantFormModal
            plant={editingPlant}
            onClose={() => {
              setShowFormModal(false);
              setEditingPlant(null);
            }}
            onSave={handleSavePlant}
          />
        )}

        {showAILookupModal && (
          <AILookupModal
            plant={aiLookupPlant}
            onClose={() => {
              setShowAILookupModal(false);
              setAiLookupPlant(null);
            }}
            onSaveToCollection={() => {
              loadPlants();
            }}
            onUseForEdit={(enhancedPlant) => {
              setEditingPlant(enhancedPlant);
              setShowFormModal(true);
            }}
          />
        )}

        {/* Save Success Modal */}
        {showSaveSuccess && savedPlantId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Plant Saved!</h2>
                <p className="text-gray-600 mb-6">The plant has been saved to your private collection.</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowSaveSuccess(false);
                      setSavedPlantId(null);
                      // Switch to "My Plants" filter and reload
                      setOwnershipFilter('mine');
                      loadPlants();
                      // Try to find and open the saved plant
                      setTimeout(() => {
                        const savedPlant = plants.find(p => p.id === savedPlantId);
                        if (savedPlant) {
                          setSelectedPlant(savedPlant);
                          setShowDetailModal(true);
                        }
                      }, 500);
                    }}
                    className="flex-1 py-3 px-4 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
                  >
                    View in My Plants
                  </button>
                  <button
                    onClick={() => {
                      setShowSaveSuccess(false);
                      setSavedPlantId(null);
                    }}
                    className="flex-1 py-3 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Continue Searching
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Photo Gallery Modal for AI Search */}
        {showAISearchPhotoGallery && aiSearchGalleryPhotos.length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setShowAISearchPhotoGallery(false)}>
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Plant Photos</h3>
                <button
                  onClick={() => setShowAISearchPhotoGallery(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Gallery Grid */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {aiSearchGalleryPhotos.map((photo, index) => (
                    <div
                      key={index}
                      className="relative group border-2 rounded-lg overflow-hidden cursor-pointer"
                      onClick={() => window.open(photo.url, '_blank', 'noopener,noreferrer')}
                    >
                      <img
                        src={photo.thumbnail || photo.url}
                        alt={`Plant photo ${index + 1}`}
                        className="w-full h-48 object-cover"
                        onError={(e) => {
                          e.target.src = 'https://placehold.co/300x200/e5e7eb/6b7280?text=Image+Error';
                        }}
                      />
                      {(photo.verified || photo.source === 'wikimedia' || photo.source === 'inaturalist' || photo.source === 'kew' || photo.source === 'gbif' || photo.source === 'plantnet') && (
                        <div className="absolute top-2 left-2 bg-green-600 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Verified
                        </div>
                      )}
                      {photo.source && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-2">
                          {photo.source === 'wikimedia' ? 'Wikimedia' :
                           photo.source === 'inaturalist' ? 'iNaturalist' :
                           photo.source === 'kew' ? 'Kew Gardens' :
                           photo.source === 'gbif' ? 'GBIF' :
                           photo.source === 'plantnet' ? 'PlantNet' :
                           photo.source}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Share to Community Modal */}
        {showShareModal && plantToShare && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Share with Community</h2>
                <button
                  onClick={() => {
                    setShowShareModal(false);
                    setPlantToShare(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="mb-4">
                <p className="text-gray-700 mb-4">
                  You're about to share <strong>"{plantToShare.common_name}"</strong> with the community. 
                  Please add attribution information:
                </p>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Photo Attribution
                    <span className="text-xs text-gray-500 ml-2">(optional - credit the photographer)</span>
                  </label>
                  <input
                    type="text"
                    value={shareAttribution}
                    onChange={(e) => setShareAttribution(e.target.value)}
                    placeholder="e.g. Photo by John Connors"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Photo Source
                  </label>
                  <select
                    value={sharePhotoSource}
                    onChange={(e) => setSharePhotoSource(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="designer_contributed">💎 Designer Contributed</option>
                    <option value="original">📸 Original Photo</option>
                    <option value="ai_generated">🤖 AI Generated</option>
                    <option value="community">👥 Community</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={confirmShareToCommunity}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700"
                >
                  Share with Community
                </button>
                <button
                  onClick={() => {
                    setShowShareModal(false);
                    setPlantToShare(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Request Modal - Plant Already Exists Publicly */}
        {showEditRequestModal && plantToShare && existingPublicPlant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Plant Already in Public Library</h2>
                <button
                  onClick={() => {
                    setShowEditRequestModal(false);
                    setPlantToShare(null);
                    setExistingPublicPlant(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
      </div>

              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  <strong>"{existingPublicPlant.common_name}"</strong> ({existingPublicPlant.botanical_name}) already exists in the PlantADay public library.
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  To edit the public version, you need permission from the admin. You can view the existing plant or request edit permission with a link to your private version.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    navigate(`/plants?search=${encodeURIComponent(existingPublicPlant.common_name)}`);
                    setShowEditRequestModal(false);
                    setPlantToShare(null);
                    setExistingPublicPlant(null);
                  }}
                  className="flex-1 py-3 px-4 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <span>View Public Plant</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
                <button
                  onClick={sendEditRequest}
                  className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <span>Request Edit Permission</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-3 text-center">
                Your private version: <span className="font-mono">{plantToShare.common_name}</span> (ID: {plantToShare.id})
              </p>
            </div>
          </div>
        )}
        </div>
      </div>
      
      {/* Admin View Mode Switcher - Fixed position, bottom-right on desktop, only for admin */}
      {isAdminUser && currentUserId && (
        <div className="hidden md:block fixed bottom-4 right-4 z-[100] bg-white rounded-lg shadow-lg border-2 border-purple-400 p-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-purple-700 mr-1">View:</span>
            <button
              onClick={() => {
                setViewMode('admin');
                localStorage.setItem('plantaday_viewMode', 'admin');
              }}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                viewMode === 'admin'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-300'
              }`}
              title="Admin View - See all designer features"
            >
              👑 Admin
            </button>
            <button
              onClick={() => {
                setViewMode('customer');
                localStorage.setItem('plantaday_viewMode', 'customer');
              }}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                viewMode === 'customer'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-300'
              }`}
              title="User View - See as regular customer"
            >
              👤 User
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="px-3 py-1.5 rounded text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 border border-purple-700 transition-colors"
              title="Open Admin Panel"
            >
              Admin Panel
            </button>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <Footer />
    </div>
  );
}

